import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import XYZ from "ol/source/XYZ";
import Feature, { FeatureLike } from "ol/Feature";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import MultiLineString from "ol/geom/MultiLineString";
import Polygon from "ol/geom/Polygon";
import { getOverpassWorkerService } from "./overpassWorkerService";
import { getSearchService } from "./searchService";
import { getCameraService } from "./cameraService";
import {
  Circle as CircleStyle,
  Fill,
  Stroke,
  Style,
  Text,
  Icon,
} from "ol/style";
import { StyleFunction } from "ol/style/Style";
import { fromLonLat, toLonLat } from "ol/proj";
import { defaults as defaultControls } from "ol/control";
import {
  defaults as defaultInteractions,
  MouseWheelZoom,
} from "ol/interaction";
import {
  Position,
  Place,
  Route,
  Friend,
  MapStyleId,
  TrackDisplaySettings,
  Coordinate,
  MapMode,
} from "../types";
import { calculateDistance } from "../utils/geoUtils";
import debugx from "debugx";

const debug = debugx("bikedash:map");
const debugRotation = debugx("bikedash:map:rotation", { throttle: "auto" });
const debugFeature = debugx("bikedash:feature");

export interface MapServiceConfig {
  target: HTMLElement;
  center?: Coordinate;
  zoom?: number;
  mode?: MapMode;
  styleId?: string;
  apiKeys?: Record<string, string>;
}

export class MapService {
  private map: Map;
  private isLongPressClick: boolean = false;
  private view: View;

  // Layers
  private baseLayer: TileLayer<XYZ>;
  private overlayLayer?: TileLayer<XYZ>;
  private realTrackLayer: VectorLayer<VectorSource>;
  private smoothTrackLayer: VectorLayer<VectorSource>;
  private routeLayer: VectorLayer<VectorSource>;
  private placesLayer: VectorLayer<VectorSource>;
  private friendsLayer: VectorLayer<VectorSource>;
  private positionLayer: VectorLayer<VectorSource>;
  private selectedFeatureLayer: VectorLayer<VectorSource>; // For displaying selected trails/parks (routes)
  private temporaryPlaceLayer: VectorLayer<VectorSource>; // For temporary place overlays (cleared when search closes)
  private cacheGridLayer: VectorLayer<VectorSource>; // For showing cached grid squares
  private searchResultsLayer: VectorLayer<VectorSource>; // For displaying search result points
  private savedRoutesLayer: VectorLayer<VectorSource>; // For displaying saved route markers
  private currentHeading: number = 0;
  private lastHeadingUpdateTime: number = 0;
  private positionMarkerIcon: Icon | null = null;
  private isDebugOverlayShowing: boolean = false;

  // Loading state callbacks
  private loadingCallbacks: Set<(loading: boolean) => void> = new Set();

  // Mode change callbacks
  private modeCallbacks: Set<(mode: MapMode) => void> = new Set();

  // Sources
  private realTrackSource: VectorSource;
  private smoothTrackSource: VectorSource;
  private routeSource: VectorSource;
  private placesSource: VectorSource;
  private friendsSource: VectorSource;
  private positionSource: VectorSource;
  private selectedFeatureSource: VectorSource;
  private temporaryPlaceSource: VectorSource;
  private searchResultsSource: VectorSource;
  private savedRoutesSource: VectorSource;

  // Features
  private positionFeature: Feature;
  private realTrackFeature: Feature;
  private smoothTrackFeature: Feature;

  // State
  private mode: MapMode =
    (localStorage.getItem("mapMode") as MapMode) || "free"; // Load saved mode or default to free
  private lastMapRotation = 0; // Track last map rotation to avoid constant updates
  private isZooming = false; // Track if currently zooming
  private zoomEndTimeout: number | undefined; // Timeout for zoom end detection
  private pendingCenter?: number[]; // Center to apply after zoom
  private followEnabled = true;
  private lastKnownPosition: { lat: number; lng: number } | null = null;
  private isAnimating = false; // Track if view is animating (prevents position updates from interfering)
  private animationTimeout: number | undefined; // Track animation timeout to cancel on new animations
  private isRotating = false; // Track if map is currently rotating (prevents recentering during rotation)
  private rotationStartAngle = 0; // Track rotation gesture start angle
  private isUserRotating = false; // Track if user is actively rotating
  private multiTouchActive = false; // Track if multiple fingers are on screen

  // Manual transition state for smooth animations
  private transitionState: {
    active: boolean;
    startTime: number;
    duration: number;
    // Offset transition
    fromOffset: number;
    toOffset: number;
    currentOffset: number;
    // Rotation transition
    fromRotation: number;
    toRotation: number;
    currentRotation: number;
  } | null = null;

  // Rotation smoothing for follow-heading mode
  private targetMapRotation: number = 0;
  private currentMapRotation: number = 0;
  private rotationAnimationFrame: number | null = null;
  private trackSettings: TrackDisplaySettings = {
    showLine: true,
    lineWidth: 4,
    lineColor: "#3e6ae1",
    lineOpacity: 0.8,
  };

  private routeSettings: TrackDisplaySettings = {
    showLine: true,
    lineWidth: 3,
    lineColor: "#9333ea",
    lineOpacity: 0.7,
  };

  private getMapStyleUrl(
    styleId: string,
    apiKeys?: Record<string, string>,
  ): string {
    const [provider, style] = styleId.split(":");

    // Default URLs for various providers
    const urls: Record<string, string> = {
      "osm:standard": "https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      "osm:humanitarian":
        "https://{a-c}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png",
      "osm:topographic": "https://{a-c}.tile.opentopomap.org/{z}/{x}/{y}.png",
      "cartodb:light":
        "https://{a-d}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
      "cartodb:dark":
        "https://{a-d}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
      "cartodb:voyager":
        "https://{a-d}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png",
      "esri:gray":
        "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
      "esri:satellite":
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    };

    // Thunderforest styles need API key
    if (provider === "thunderforest") {
      // Try to get API key from multiple sources
      let apiKey = apiKeys?.thunderforest;

      // If not in apiKeys, try cached auth
      if (!apiKey) {
        const cachedAuth = localStorage.getItem("bikedash_cached_auth");
        if (cachedAuth) {
          try {
            const cached = JSON.parse(cachedAuth);
            apiKey = cached.thunderforestApiKey;
          } catch (e) {
            debug.warn("Failed to parse cached auth for Thunderforest API key");
          }
        }
      }

      if (apiKey) {
        const tfUrls: Record<string, string> = {
          "thunderforest:cycle": `https://{a-c}.tile.thunderforest.com/cycle/{z}/{x}/{y}.png?apikey=${apiKey}`,
          "thunderforest:landscape": `https://{a-c}.tile.thunderforest.com/landscape/{z}/{x}/{y}.png?apikey=${apiKey}`,
          "thunderforest:outdoors": `https://{a-c}.tile.thunderforest.com/outdoors/{z}/{x}/{y}.png?apikey=${apiKey}`,
          "thunderforest:transport": `https://{a-c}.tile.thunderforest.com/transport/{z}/{x}/{y}.png?apikey=${apiKey}`,
          "thunderforest:transport-dark": `https://{a-c}.tile.thunderforest.com/transport-dark/{z}/{x}/{y}.png?apikey=${apiKey}`,
          "thunderforest:spinal-map": `https://{a-c}.tile.thunderforest.com/spinal-map/{z}/{x}/{y}.png?apikey=${apiKey}`,
          "thunderforest:pioneer": `https://{a-c}.tile.thunderforest.com/pioneer/{z}/{x}/{y}.png?apikey=${apiKey}`,
          "thunderforest:mobile-atlas": `https://{a-c}.tile.thunderforest.com/mobile-atlas/{z}/{x}/{y}.png?apikey=${apiKey}`,
          "thunderforest:neighbourhood": `https://{a-c}.tile.thunderforest.com/neighbourhood/{z}/{x}/{y}.png?apikey=${apiKey}`,
        };
        return tfUrls[styleId] || urls["osm:standard"];
      } else {
        debug.warn("No Thunderforest API key available, falling back to OSM");
      }
    }

    return urls[styleId] || urls["osm:standard"];
  }

  constructor(config: MapServiceConfig) {
    debug("🚀 MAP SERVICE CONSTRUCTOR STARTING");

    // Initialize the position marker icon early
    this.positionMarkerIcon = new Icon({
      src:
        "data:image/svg+xml;utf8," +
        encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
            <path d="M16 4 L24 20 L16 16 L8 20 Z" fill="#4285f4" stroke="#ffffff" stroke-width="1.5"/>
          </svg>`,
        ),
      scale: 1,
      rotation: 0,
      rotateWithView: false,
    });

    // Initialize sources
    this.positionFeature = new Feature();
    this.realTrackFeature = new Feature();
    this.smoothTrackFeature = new Feature();

    this.positionSource = new VectorSource({
      features: [this.positionFeature],
    });

    this.realTrackSource = new VectorSource({
      features: [this.realTrackFeature],
    });

    this.smoothTrackSource = new VectorSource({
      features: [this.smoothTrackFeature],
    });

    this.routeSource = new VectorSource();
    this.placesSource = new VectorSource();
    this.friendsSource = new VectorSource();

    // Initialize layers with saved style or default
    const initialStyleId = config.styleId || "osm:standard";
    const tileUrl = this.getMapStyleUrl(initialStyleId, config.apiKeys);

    this.baseLayer = new TileLayer({
      source: new XYZ({
        url: tileUrl,
        attributions: "© OpenStreetMap contributors",
      }),
      zIndex: 0,
      preload: 2, // Preload 2 tiles outside the viewport
    });

    this.realTrackLayer = new VectorLayer({
      source: this.realTrackSource,
      style: this.getTrackStyle(this.trackSettings),
      zIndex: 19, // Above all routes and smooth track, below markers
    });

    this.smoothTrackLayer = new VectorLayer({
      source: this.smoothTrackSource,
      style: this.getTrackStyle({ ...this.trackSettings, lineOpacity: 0.5 }),
      zIndex: 18, // Above routes, below real track
    });

    this.routeLayer = new VectorLayer({
      source: this.routeSource,
      style: this.getRouteStyle(),
      zIndex: 5,
    });

    this.placesLayer = new VectorLayer({
      source: this.placesSource,
      style: (feature) => this.getPlaceStyle(feature as Feature),
      zIndex: 20,
    });

    this.friendsLayer = new VectorLayer({
      source: this.friendsSource,
      style: this.getFriendStyle(),
      zIndex: 25,
    });

    this.positionLayer = new VectorLayer({
      source: this.positionSource,
      style: () => this.getPositionStyle(),
      zIndex: 30,
    });

    // Initialize temporary place layer (cleared when search closes)
    this.temporaryPlaceSource = new VectorSource();
    this.temporaryPlaceLayer = new VectorLayer({
      source: this.temporaryPlaceSource,
      style: (feature) => this.getSelectedFeatureStyle(feature as Feature),
      zIndex: 6, // Above route tracks but below everything else
    });

    // Initialize search results layer for showing points
    this.searchResultsSource = new VectorSource();
    this.searchResultsLayer = new VectorLayer({
      source: this.searchResultsSource,
      style: (feature) => this.getSearchResultStyle(feature as Feature),
      zIndex: 20, // Above routes/places
    });

    // Initialize selected feature layer for trails/parks (routes)
    this.selectedFeatureSource = new VectorSource();
    this.selectedFeatureLayer = new VectorLayer({
      source: this.selectedFeatureSource,
      style: (feature) => this.getSelectedFeatureStyle(feature as Feature),
      zIndex: 15, // Below markers but above tracks
      // Use 'lighter' composite to prevent opacity buildup where segments overlap
      className: "route-segments-layer",
    });

    // Initialize saved routes layer for showing saved route markers
    this.savedRoutesSource = new VectorSource();
    this.savedRoutesLayer = new VectorLayer({
      source: this.savedRoutesSource,
      style: (feature) => this.getSavedRouteStyle(feature as Feature),
      zIndex: 21, // Above everything else (markers on top)
    });

    // Initialize cache grid layer for debug display
    this.cacheGridLayer = new VectorLayer({
      source: new VectorSource(),
      style: new Style({
        fill: new Fill({
          color: "rgba(100, 150, 255, 0.25)", // Light blue with more visibility
        }),
        stroke: new Stroke({
          color: "rgba(100, 150, 255, 0.6)",
          width: 2,
          lineDash: [4, 4],
        }),
      }),
      zIndex: 100, // Higher z-index to ensure it's on top
      visible: localStorage.getItem("showCacheGrid") === "true",
    });

    // Initialize view
    this.view = new View({
      center: fromLonLat(config.center || [-97.7431, 30.2672]),
      zoom: config.zoom || 13,
      minZoom: 3,
      maxZoom: 20,
    });

    // Create custom mouse wheel zoom for DESKTOP (focuses on GPS position in follow modes)
    const customMouseWheelZoom = new MouseWheelZoom();
    customMouseWheelZoom.setMouseAnchor(true); // Enable anchor point for zooming

    // Store reference to this for closure (moved up for use in rotation tracking)
    const serviceInstance = this;

    // Track rotation changes from ANY source (pinch rotate, combined gestures, etc)
    let rotationAtGestureStart = 0;
    let lastRotation = 0;
    let accumulatedRotation = 0;
    let isTrackingRotation = false;
    let hasRotatedSignificantly = false;
    let pointerCount = 0;

    // Monitor ALL pointer events to detect multi-touch gestures
    // Update track/route styles when zoom changes to adjust line width
    this.view.on("change:resolution", () => {
      // Update track layer styles to reflect new zoom-based width
      this.realTrackLayer.changed();
      this.smoothTrackLayer.changed();
      // Update route/feature layer styles
      this.selectedFeatureLayer.changed();
      this.temporaryPlaceLayer.changed();
    });

    this.view.on("change:rotation", () => {
      if (isTrackingRotation && !serviceInstance.isUserRotating) {
        // Accumulate rotation changes during gesture
        const currentRotation = serviceInstance.view.getRotation();

        // Calculate the delta from last rotation check
        let deltaFromLast = currentRotation - lastRotation;
        // Normalize to [-PI, PI]
        while (deltaFromLast > Math.PI) deltaFromLast -= 2 * Math.PI;
        while (deltaFromLast < -Math.PI) deltaFromLast += 2 * Math.PI;

        // Accumulate the absolute rotation change
        accumulatedRotation += Math.abs(deltaFromLast);
        lastRotation = currentRotation;

        const accumulatedDegrees = (accumulatedRotation * 180) / Math.PI;

        if (!hasRotatedSignificantly && accumulatedDegrees > 20) {
          hasRotatedSignificantly = true;
          // If in follow-heading or follow-north mode, switch to follow mode
          if (
            serviceInstance.mode === "follow-heading" ||
            serviceInstance.mode === "follow-north"
          ) {
            debug(
              "User rotated map %s° total during gesture - switching from %s to follow mode",
              accumulatedDegrees.toFixed(1),
              serviceInstance.mode,
            );
            serviceInstance.setMode("follow");
            // Fire event so CompassRose can update
            if (serviceInstance.map) {
              serviceInstance.map.dispatchEvent({
                type: "mapinteraction",
                interaction: "rotation",
              } as any);
            }
          }
        }
      }
    });

    serviceInstance.isZooming = false; // Initialize explicitly

    debug("About to install zoom detection hook on MouseWheelZoom");

    // Override handleEvent to detect zoom and set anchor point
    const originalHandleEvent =
      customMouseWheelZoom.handleEvent.bind(customMouseWheelZoom);
    customMouseWheelZoom.handleEvent = function (mapBrowserEvent: any) {
      // Before handling, set the anchor point for zoom
      if (mapBrowserEvent.type === "wheel") {
        debug("🎯 WHEEL EVENT DETECTED - mode=%s", serviceInstance.mode);
        serviceInstance.isZooming = true;

        // In follow modes, set the zoom anchor to GPS position
        if (
          serviceInstance.mode === "follow" ||
          serviceInstance.mode === "follow-north" ||
          serviceInstance.mode === "follow-heading"
        ) {
          const gpsPos = serviceInstance.lastKnownPosition;
          if (gpsPos && serviceInstance.map) {
            const coords = fromLonLat([gpsPos.lng, gpsPos.lat]);

            // Get pixel position of GPS for proper anchor
            const pixel = serviceInstance.map.getPixelFromCoordinate(coords);
            if (pixel) {
              // Override both coordinate and pixel to zoom at GPS position
              mapBrowserEvent.coordinate = coords;
              mapBrowserEvent.pixel = pixel;

              debug(
                "🎯 Set zoom anchor to GPS position at pixel %s,%s",
                pixel[0],
                pixel[1],
              );
            }

            // After zoom, we need to recenter with offset
            const centerCoords = serviceInstance.getOffsetCenter(coords);
            // We'll apply this after the zoom completes
            serviceInstance.pendingCenter = centerCoords;
          }
        } else {
          debug("🎯 Free mode - normal zoom behavior");
        }
      }

      const result = originalHandleEvent(mapBrowserEvent);

      // After handling wheel events, apply offset center and set up zoom end detection
      if (mapBrowserEvent.type === "wheel") {
        // Immediately apply the offset center during zoom
        if (serviceInstance.pendingCenter) {
          serviceInstance.view.setCenter(serviceInstance.pendingCenter);
        }

        // Clear any existing timeout
        if (serviceInstance.zoomEndTimeout) {
          clearTimeout(serviceInstance.zoomEndTimeout);
        }

        // Set timeout to detect when zoom stops
        serviceInstance.zoomEndTimeout = window.setTimeout(() => {
          debug("🔍 ZOOM COMPLETE - resuming updates");
          serviceInstance.isZooming = false;

          // Clear the pending center
          serviceInstance.pendingCenter = undefined;

          // Resume rotation animation if in follow-heading mode
          if (
            serviceInstance.mode === "follow-heading" &&
            serviceInstance.targetMapRotation !== undefined
          ) {
            serviceInstance.startRotationAnimation();
          }
        }, 250); // Reduced from 500ms for more responsive zoom end detection
      }

      return result;
    };

    debug("✅ Zoom detection hook installed on MouseWheelZoom");

    // Initialize map with custom interactions
    this.map = new Map({
      target: config.target,
      layers: [
        this.baseLayer,
        this.routeLayer,
        this.realTrackLayer,
        this.smoothTrackLayer,
        this.selectedFeatureLayer, // Selected trails/parks (routes)
        this.temporaryPlaceLayer, // Temporary place overlays
        this.savedRoutesLayer, // Saved route markers
        this.searchResultsLayer, // Search result points
        this.placesLayer,
        this.friendsLayer,
        this.positionLayer,
        this.cacheGridLayer, // Cache grid overlay (on top for visibility)
      ],
      view: this.view,
      controls: defaultControls({
        zoom: false,
        rotate: false,
        attribution: false,
      }),
      interactions: defaultInteractions({
        mouseWheelZoom: false, // Disable default to use our custom one for desktop
        // Keep default pinchZoom and pinchRotate - they work together automatically on mobile
      }).extend([customMouseWheelZoom]), // Add custom mouse wheel for desktop
    });

    // Load saved mode or use config or default to free
    this.mode =
      (localStorage.getItem("mapMode") as MapMode) || config.mode || "free";

    // Add long-press handler to search for nearest cached way
    let startTime = 0;
    let longPressTimer: number | null = null;
    let longPressShown = false;
    let longPressCoordinate: [number, number] | null = null;
    let longPressPixel: [number, number] | null = null;
    let startPixel: [number, number] | null = null;

    // Movement threshold in pixels - allow small movements on mobile
    const MOVEMENT_THRESHOLD = 10; // pixels

    this.map.on("pointerdown" as any, (evt: any) => {
      startTime = new Date().getTime();
      longPressShown = false;
      longPressCoordinate = evt.coordinate;
      longPressPixel = evt.pixel as [number, number];
      startPixel = evt.pixel as [number, number];

      // Prevent default to avoid issues on mobile
      if (evt.originalEvent) {
        evt.originalEvent.preventDefault();
      }

      // Start timer to show popup while still holding
      longPressTimer = window.setTimeout(async () => {
        if (longPressCoordinate && longPressPixel) {
          debug("Long press timer fired - showing popup while holding");

          // Convert to lon/lat
          const lonLat = toLonLat(longPressCoordinate);
          const lon = lonLat[0];
          const lat = lonLat[1];

          // Search for nearest cached way and show popup
          await this.searchNearestCachedWay(lat, lon, longPressPixel);

          // Mark that we showed the popup
          longPressShown = true;
        }
      }, 500);
    });

    this.map.on("pointerup" as any, () => {
      // Clear timer if still running
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }

      const endTime = new Date().getTime();
      const pressDuration = endTime - startTime;

      if (
        pressDuration >= 500 &&
        !longPressShown &&
        longPressCoordinate &&
        longPressPixel
      ) {
        // They released after 500ms but before the timer fired (edge case)
        debug("Long press released after %dms - showing popup", pressDuration);

        const lonLat = toLonLat(longPressCoordinate);
        const lon = lonLat[0];
        const lat = lonLat[1];

        this.searchNearestCachedWay(lat, lon, longPressPixel);
        longPressShown = true;
      }
    });

    this.map.on("pointermove" as any, (evt: any) => {
      // Only cancel if movement exceeds threshold
      if (startPixel && longPressTimer) {
        const currentPixel = evt.pixel as [number, number];
        const dx = Math.abs(currentPixel[0] - startPixel[0]);
        const dy = Math.abs(currentPixel[1] - startPixel[1]);
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance > MOVEMENT_THRESHOLD) {
          // Cancel long press only if moved significantly
          debug("Cancelling long press due to movement: %d pixels", distance);
          clearTimeout(longPressTimer);
          longPressTimer = null;
          startTime = 0;
          longPressCoordinate = null;
          longPressPixel = null;
          startPixel = null;
          longPressShown = false;
        }
      }
    });

    // Add touch-specific handling for better mobile support
    const mapElement = this.map.getTargetElement();
    if (mapElement) {
      let touchTimer: number | null = null;
      let touchStartCoord: [number, number] | null = null;

      mapElement.addEventListener(
        "touchstart",
        (e: TouchEvent) => {
          if (e.touches.length === 1) {
            const touch = e.touches[0];

            // Get map coordinates from pixel
            const rect = mapElement.getBoundingClientRect();
            const mapPixel: [number, number] = [
              touch.clientX - rect.left,
              touch.clientY - rect.top,
            ];

            const coord = this.map.getCoordinateFromPixel(mapPixel);
            if (coord) {
              touchStartCoord = coord as [number, number];
            }

            // Set up long press timer for touch
            touchTimer = window.setTimeout(async () => {
              if (touchStartCoord) {
                debug("Touch long press detected on mobile");
                const lonLat = toLonLat(touchStartCoord);
                await this.searchNearestCachedWay(
                  lonLat[1],
                  lonLat[0],
                  mapPixel,
                );
                longPressShown = true;
              }
            }, 500);
          }
        },
        { passive: false },
      );

      mapElement.addEventListener("touchend", () => {
        if (touchTimer) {
          clearTimeout(touchTimer);
          touchTimer = null;
        }
      });

      mapElement.addEventListener("touchmove", () => {
        if (touchTimer) {
          clearTimeout(touchTimer);
          touchTimer = null;
        }
      });
    }

    // Add click handler for debugging selected features and search results
    this.map.on("click", (evt) => {
      // Ignore click if we showed a long press popup
      if (longPressShown) {
        this.isLongPressClick = true;
        debug(
          "Ignoring click because long press popup was shown - marking as long press release",
        );
        longPressShown = false;

        // Reset the flag after a short delay to ensure the popup handler sees it
        setTimeout(() => {
          this.isLongPressClick = false;
          debug("Resetting long press click flag");
        }, 100);
        return;
      }

      debugFeature("Map clicked at pixel:", evt.pixel);

      // First check if we clicked on a saved route marker
      const savedRouteFeatures = this.map.getFeaturesAtPixel(evt.pixel, {
        layerFilter: (layer) => layer === this.savedRoutesLayer,
      });

      if (savedRouteFeatures && savedRouteFeatures.length > 0) {
        const feature = savedRouteFeatures[0];
        const routeId = feature.get("routeId");
        const name = feature.get("name");

        debug("Clicked on saved route: %s (routeId: %s)", name, routeId);

        // Show the popup for saved route
        this.showSavedRouteContextMenu(
          evt.pixel as [number, number],
          feature as Feature,
        );
        return;
      }

      // Check if we clicked on a search result point
      const searchFeatures = this.map.getFeaturesAtPixel(evt.pixel, {
        layerFilter: (layer) => layer === this.searchResultsLayer,
      });

      if (searchFeatures && searchFeatures.length > 0) {
        const feature = searchFeatures[0];
        const placeId = feature.get("place_id");
        const name = feature.get("name");

        debug("Clicked on search result: %s (place_id: %s)", name, placeId);

        // Show the popup instead of selecting
        debug("Showing popup context menu for search result");
        this.showSearchResultContextMenu(
          evt.pixel as [number, number],
          feature as Feature,
        );
        debug("Returning early from click handler - should not select");
        return;
      }

      // Removed single-click popup for trail segments - use long press instead
    });

    // Add pointer event listeners to detect multi-touch gestures
    const mapTarget = this.map.getTargetElement();
    if (mapTarget) {
      // Track active pointers
      let activePointers = 0;

      mapTarget.addEventListener("pointerdown", (e) => {
        activePointers++;
        if (activePointers >= 2) {
          // Multi-touch gesture started - disable automatic rotation
          serviceInstance.multiTouchActive = true;

          if (!isTrackingRotation) {
            rotationAtGestureStart = this.view.getRotation();
            lastRotation = rotationAtGestureStart;
            accumulatedRotation = 0;
            isTrackingRotation = true;
            hasRotatedSignificantly = false;
            debug(
              "Multi-touch gesture started with rotation at %s°",
              ((rotationAtGestureStart * 180) / Math.PI).toFixed(1),
            );
          }
        }
      });

      mapTarget.addEventListener("pointerup", (e) => {
        activePointers = Math.max(0, activePointers - 1);
        if (activePointers < 2) {
          // Multi-touch gesture ended
          serviceInstance.multiTouchActive = false;

          if (isTrackingRotation) {
            isTrackingRotation = false;
            debug(
              "Multi-touch gesture ended, accumulated rotation: %s°",
              ((accumulatedRotation * 180) / Math.PI).toFixed(1),
            );
          }
        }
      });

      mapTarget.addEventListener("pointercancel", (e) => {
        activePointers = Math.max(0, activePointers - 1);
        if (activePointers < 2) {
          serviceInstance.multiTouchActive = false;
          if (isTrackingRotation) {
            isTrackingRotation = false;
          }
        }
      });
    }

    // TODO: Consider disabling all animations for smoother experience
    // const originalAnimate = this.view.animate.bind(this.view);
    // this.view.animate = function(options: any) {
    //   if (Array.isArray(options)) {
    //     options = options.map(opt => ({ ...opt, duration: 0 }));
    //   } else {
    //     options = { ...options, duration: 0 };
    //   }
    //   return originalAnimate(options);
    // };

    // Detect when zooming starts/stops
    // The isZooming flag is now set by the customMouseWheelZoom handleEvent override above

    debug("🎯 MAP SERVICE FULLY INITIALIZED - zoom detection should be active");
    debug.info("Map service initialized");

    // Setup cache grid overlay listener
    this.setupCacheGridListener();

    // Setup drag detection to switch to free mode
    this.setupDragDetection();
  }

  private setupDragDetection() {
    let dragStartPixel: [number, number] | null = null;
    let isDragging = false;
    const DRAG_THRESHOLD = 100; // pixels

    // Listen for pointer down to start tracking
    this.map.on("singleclick", () => {
      // Reset on click (not a drag)
      isDragging = false;
      dragStartPixel = null;
    });

    // Use the viewport element for pointer events
    const viewport = this.map.getViewport();

    viewport.addEventListener("pointerdown", (evt) => {
      // Only track if in a follow mode AND not in multi-touch
      if (
        (this.mode === "follow" ||
          this.mode === "follow-north" ||
          this.mode === "follow-heading") &&
        !this.multiTouchActive
      ) {
        dragStartPixel = [evt.clientX, evt.clientY];
        isDragging = true;
        debug("Drag started at pixel: %o", dragStartPixel);
      }
    });

    viewport.addEventListener("pointermove", (evt) => {
      // Stop tracking if multi-touch becomes active
      if (this.multiTouchActive) {
        isDragging = false;
        dragStartPixel = null;
        return;
      }

      if (isDragging && dragStartPixel) {
        const currentPixel: [number, number] = [evt.clientX, evt.clientY];
        const distance = Math.sqrt(
          Math.pow(currentPixel[0] - dragStartPixel[0], 2) +
            Math.pow(currentPixel[1] - dragStartPixel[1], 2),
        );

        if (distance > DRAG_THRESHOLD) {
          debug(
            "User dragged %d pixels, switching to free mode",
            Math.round(distance),
          );
          this.setMode("free");
          isDragging = false;
          dragStartPixel = null;
        }
      }
    });

    viewport.addEventListener("pointerup", () => {
      isDragging = false;
      dragStartPixel = null;
    });
  }

  // ============================================================================
  // Position Management
  // ============================================================================

  updatePosition(position: Position) {
    if (!position.lat || !position.lng) return;

    // Store the last known position
    this.lastKnownPosition = { lat: position.lat, lng: position.lng };
    debug("Stored GPS position:", this.lastKnownPosition);

    // Always update the marker position
    const coords = fromLonLat([position.lng, position.lat]);
    this.positionFeature.setGeometry(new Point(coords));

    // Store position metadata on the feature
    this.positionFeature.set("accuracy", position.accuracy);
    this.positionFeature.set(
      "headingUnreliable",
      position.headingUnreliable ?? false,
    );

    // Force style re-evaluation when heading reliability changes
    this.positionFeature.changed();

    // Only center map if in follow modes
    if (
      this.mode === "follow" ||
      this.mode === "follow-north" ||
      this.mode === "follow-heading"
    ) {
      // Don't update center while zooming or animating
      if (!this.isZooming && !this.isAnimating) {
        debug("📍 CENTERING MAP - mode=%s", this.mode);

        // Apply offset if needed (for follow-heading mode when debug overlay is not showing)
        const centerCoords = this.getOffsetCenter(coords);
        this.view.setCenter(centerCoords);

        if (centerCoords !== coords) {
          debug("📍 Offset center for follow-heading mode (bottom quarter)");
        }
      }
    } else {
      debug("⚠️ NOT centering - mode=%s (not follow)", this.mode);
    }

    // After updating geometry, we need to re-apply the current heading rotation
    // because changing geometry can reset the Icon's rotation
    if (this.positionMarkerIcon && this.currentHeading !== 0) {
      const mapRotation = this.view.getRotation();
      const markerRotation =
        (this.currentHeading * Math.PI) / 180 + mapRotation;
      this.positionMarkerIcon.setRotation(markerRotation);
      debugRotation(
        "Re-applied rotation after GPS update: %s°",
        this.currentHeading.toFixed(1),
      );
    }

    // Force re-render after position update to show new position
    this.map.render();

    // NEVER use GPS heading - compass handles all heading updates at 60fps
    // This prevents GPS from interfering with smooth compass rotation
  }

  // Update only the heading of the position marker (called at high frequency from compass)
  private lastLogTime = 0;
  private updateCount = 0;

  updatePositionHeading(heading: number) {
    const now = Date.now();
    this.updateCount++;

    // Log actual update rate every second
    if (now - this.lastLogTime >= 1000) {
      debug(`🎯 ACTUAL COMPASS UPDATE RATE: ${this.updateCount} updates/sec`);
      this.updateCount = 0;
      this.lastLogTime = now;
    }

    debug(
      "🔥 updatePositionHeading CALLED with heading=%s, mode=%s",
      heading,
      this.mode,
    );
    const mapRotDeg = (this.view.getRotation() * 180) / Math.PI;
    debugRotation(
      "🎯 updatePositionHeading: %s° (map rot: %s°, north mode: %s)",
      heading.toFixed(1),
      mapRotDeg.toFixed(1),
      Math.abs(mapRotDeg) < 0.1,
    );
    this.currentHeading = heading;
    this.lastHeadingUpdateTime = now;

    // Skip all rotation updates while zooming or multi-touch gestures
    if (this.isZooming || this.multiTouchActive) {
      debug(
        "✅ SKIPPING ROTATION - %s ACTIVE",
        this.isZooming ? "ZOOM" : "MULTI-TOUCH",
      );
      this.updateMarkerHeading();
      return;
    }

    // Handle manual transitions first
    if (this.transitionState && this.transitionState.active) {
      const elapsed = Date.now() - this.transitionState.startTime;
      const progress = Math.min(elapsed / this.transitionState.duration, 1);

      // Ease in-out cubic
      const easedProgress =
        progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      // Update offset
      this.transitionState.currentOffset =
        this.transitionState.fromOffset +
        (this.transitionState.toOffset - this.transitionState.fromOffset) *
          easedProgress;

      // Update rotation
      // If transitioning TO follow-heading mode, continuously update target to current heading
      if (this.mode === "follow-heading") {
        this.transitionState.toRotation = -(heading * Math.PI) / 180;
      }

      let rotationDiff =
        this.transitionState.toRotation - this.transitionState.fromRotation;
      // Normalize to shortest path
      while (rotationDiff > Math.PI) rotationDiff -= 2 * Math.PI;
      while (rotationDiff < -Math.PI) rotationDiff += 2 * Math.PI;

      this.transitionState.currentRotation =
        this.transitionState.fromRotation + rotationDiff * easedProgress;

      // Apply the transition
      const geometry = this.positionFeature.getGeometry();
      if (geometry) {
        const gpsCoords = (geometry as Point).getCoordinates();

        // Calculate offset center with current transition values
        const size = this.map.getSize();
        if (
          size &&
          size[1] >= 500 &&
          this.transitionState.currentOffset > 0.001 &&
          !this.isDebugOverlayShowing
        ) {
          const resolution = this.view.getResolution();
          if (resolution) {
            const offsetPixels = size[1] * this.transitionState.currentOffset;
            const offsetDistance = offsetPixels * resolution;

            const offsetX =
              offsetDistance * Math.sin(this.transitionState.currentRotation);
            const offsetY =
              offsetDistance * Math.cos(this.transitionState.currentRotation);

            const newCenter = [gpsCoords[0] - offsetX, gpsCoords[1] + offsetY];

            this.view.setRotation(this.transitionState.currentRotation);
            this.view.setCenter(newCenter);
          }
        } else {
          // No offset or small screen
          this.view.setRotation(this.transitionState.currentRotation);
          this.view.setCenter(gpsCoords);
        }
      }

      // Check if transition is complete
      if (progress >= 1) {
        this.transitionState.active = false;
        debug("Transition complete");
      }

      this.lastMapRotation = this.transitionState.currentRotation;
      this.updateMarkerHeading();
      return;
    }

    // Normal mode handling (no transition)
    if (this.mode === "follow-heading") {
      // DIRECTLY apply rotation with minimal smoothing since we're already getting smoothed compass data at 30fps
      const targetRotation = -(heading * Math.PI) / 180;
      const currentRotation = this.view.getRotation();

      // Calculate shortest angular distance
      let angleDiff = targetRotation - currentRotation;
      while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
      while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

      // Apply with very high responsiveness (0.8 = 80% each frame)
      const newRotation = currentRotation + angleDiff * 0.8;

      // Update with offset
      const geometry = this.positionFeature.getGeometry();
      if (geometry) {
        const gpsCoords = (geometry as Point).getCoordinates();
        const newCenter = this.getOffsetCenter(gpsCoords, newRotation);

        this.view.setRotation(newRotation);
        this.view.setCenter(newCenter);
      } else {
        this.view.setRotation(newRotation);
      }

      this.lastMapRotation = newRotation;
    } else if (this.mode === "follow-north") {
      // In follow-north mode, keep map at north (but this shouldn't trigger during transitions)
      const currentRotation = this.view.getRotation();
      if (Math.abs(currentRotation) > 0.01) {
        // Smooth rotation to north
        const newRotation = currentRotation * 0.9;
        this.view.setRotation(newRotation);
        this.lastMapRotation = newRotation;
      }
    }

    this.updateMarkerHeading();
  }

  private updateMarkerHeading() {
    // Always update the icon rotation, even during zoom
    if (this.positionMarkerIcon) {
      const mapRotation = this.view.getRotation();
      // In north mode (mapRotation = 0), marker should point to absolute heading
      // In compass mode (map rotates), marker rotation compensates for map rotation
      const markerRotation =
        (this.currentHeading * Math.PI) / 180 + mapRotation;
      const prevRotation = this.positionMarkerIcon.getRotation();

      // Always set the rotation, even if it seems the same
      // OpenLayers might have reset it internally
      this.positionMarkerIcon.setRotation(markerRotation);

      // FORCE the style to be re-evaluated by setting a dummy property
      // This is a workaround for OpenLayers caching
      this.positionFeature.set("_rotation_hack", Math.random());

      const isNorthMode = Math.abs(mapRotation) < 0.001;
      debugRotation(
        "🔄 %s: heading=%s°, mapRot=%s°, prevRot=%s° → newRot=%s°",
        isNorthMode ? "NORTH" : "COMPASS",
        this.currentHeading.toFixed(1),
        ((mapRotation * 180) / Math.PI).toFixed(1),
        ((prevRotation * 180) / Math.PI).toFixed(1),
        ((markerRotation * 180) / Math.PI).toFixed(1),
      );

      // Always force re-render when we update heading
      // Don't check for geometry - it might be set later
      if (this.positionFeature) {
        this.positionFeature.changed();
      }
      if (this.positionSource) {
        this.positionSource.changed();
      }
      // Always render to show marker updates
      this.map.render();
    }
  }

  // ============================================================================
  // Track Management
  // ============================================================================

  updateRealTrack(points: Position[]) {
    const coords = points
      .filter((p) => p.lat && p.lng)
      .map((p) => fromLonLat([p.lng!, p.lat!]));

    if (coords.length > 1) {
      this.realTrackFeature.setGeometry(new LineString(coords));
    }
  }

  updateSmoothTrack(points: Position[]) {
    const coords = points
      .filter((p) => p.lat && p.lng)
      .map((p) => fromLonLat([p.lng!, p.lat!]));

    if (coords.length > 1) {
      this.smoothTrackFeature.setGeometry(new LineString(coords));
    }
  }

  clearTracks() {
    this.realTrackFeature.setGeometry(undefined);
    this.smoothTrackFeature.setGeometry(undefined);
  }

  updateTrackStyle(settings: Partial<TrackDisplaySettings>) {
    this.trackSettings = { ...this.trackSettings, ...settings };
    this.realTrackLayer.setStyle(this.getTrackStyle(this.trackSettings));
    this.smoothTrackLayer.setStyle(
      this.getTrackStyle({
        ...this.trackSettings,
        lineOpacity: (this.trackSettings.lineOpacity || 0.8) * 0.6,
      }),
    );
  }

  updateRouteStyle(settings: Partial<TrackDisplaySettings>) {
    debug("Updating route style with:", settings);
    this.routeSettings = { ...this.routeSettings, ...settings };
    debug("New route settings:", this.routeSettings);
    this.routeLayer.setStyle(this.getRouteStyle());

    // Refresh selected features to use new route settings
    this.selectedFeatureLayer.changed();
    this.temporaryPlaceLayer.changed();
  }

  // ============================================================================
  // Route Management
  // ============================================================================

  setRoute(route: Route) {
    this.routeSource.clear();

    if (route.trackPoints && route.trackPoints.length > 1) {
      const coords = route.trackPoints
        .filter((p) => p.lat && p.lng)
        .map((p) => fromLonLat([p.lng!, p.lat!]));

      const routeFeature = new Feature({
        geometry: new LineString(coords),
        route: route,
      });

      this.routeSource.addFeature(routeFeature);
    }

    // Add waypoint markers
    route.waypoints.forEach((waypoint, index) => {
      let coord: number[] | undefined;

      if (Array.isArray(waypoint)) {
        // Coordinate
        coord = fromLonLat(waypoint);
      } else if (
        "position" in waypoint &&
        waypoint.position?.lat &&
        waypoint.position?.lng
      ) {
        // Waypoint with position
        coord = fromLonLat([waypoint.position.lng, waypoint.position.lat]);
      } else if ("lat" in waypoint && waypoint.lat && waypoint.lng) {
        // Position
        coord = fromLonLat([waypoint.lng!, waypoint.lat!]);
      } else if (
        "place" in waypoint &&
        waypoint.place?.geometry.type === "point"
      ) {
        // Waypoint with place
        coord = fromLonLat(waypoint.place.geometry.position);
      }

      if (coord) {
        const waypointFeature = new Feature({
          geometry: new Point(coord),
          type: "waypoint",
          index: index,
          data: waypoint,
        });
        this.routeSource.addFeature(waypointFeature);
      }
    });
  }

  clearRoute() {
    this.routeSource.clear();
  }

  // ============================================================================
  // Activity Track Management
  // ============================================================================

  setActivityTrack(
    trackPoints: Array<{ lat: number; lng: number; timestamp?: number }>,
  ) {
    this.realTrackSource.clear();

    if (trackPoints.length < 2) {
      return;
    }

    // Process track points to detect gaps (distance or time based)
    const segments: Array<Array<number[]>> = [];
    let currentSegment: Array<number[]> = [];
    const TIME_GAP_THRESHOLD = 120000; // 2 minutes in milliseconds
    const DISTANCE_GAP_THRESHOLD = 50; // 50 meters

    for (let i = 0; i < trackPoints.length; i++) {
      const point = trackPoints[i];
      const coords = fromLonLat([point.lng, point.lat]);

      // Check for gap if we have previous point
      const prevPoint = trackPoints[i - 1];
      if (i > 0 && prevPoint) {
        let shouldStartNewSegment = false;

        // Check time gap
        if (point.timestamp && prevPoint.timestamp) {
          const timeDiff = point.timestamp - prevPoint.timestamp;
          if (timeDiff > TIME_GAP_THRESHOLD) {
            shouldStartNewSegment = true;
            debug(
              "Activity track time gap: %d seconds at point %d",
              timeDiff / 1000,
              i,
            );
          }
        }

        // Check distance gap
        const distance = calculateDistance(
          prevPoint.lat,
          prevPoint.lng,
          point.lat,
          point.lng,
        );
        if (distance > DISTANCE_GAP_THRESHOLD) {
          shouldStartNewSegment = true;
          debug("Activity track distance gap: %dm at point %d", distance, i);
        }

        if (shouldStartNewSegment) {
          // Start a new segment if there's a gap
          if (currentSegment.length > 1) {
            segments.push(currentSegment);
          }
          currentSegment = [coords];
        } else {
          currentSegment.push(coords);
        }
      } else {
        currentSegment.push(coords);
      }
    }

    // Add the last segment
    if (currentSegment.length > 1) {
      segments.push(currentSegment);
    }

    // Create a feature for each segment
    segments.forEach((segment, index) => {
      const trackFeature = new Feature({
        geometry: new LineString(segment),
        type: "activity-track",
        segmentIndex: index,
      });
      this.realTrackSource.addFeature(trackFeature);
    });

    debug(
      "Set activity track with %d segments from %d points",
      segments.length,
      trackPoints.length,
    );
  }

  clearActivityTrack() {
    this.realTrackSource.clear();
    debug("Cleared activity track");
  }

  // ============================================================================
  // Places Management
  // ============================================================================

  addPlace(place: Place) {
    const feature = this.createPlaceFeature(place);
    if (feature) {
      this.placesSource.addFeature(feature);
    }
  }

  updatePlaces(places: Place[]) {
    this.placesSource.clear();
    places.forEach((place) => {
      const feature = this.createPlaceFeature(place);
      if (feature) {
        this.placesSource.addFeature(feature);
      }
    });
  }

  private createPlaceFeature(place: Place): Feature | null {
    let geometry;

    switch (place.geometry.type) {
      case "point":
        geometry = new Point(fromLonLat(place.geometry.position));
        break;
      case "line":
        const lineCoords = place.geometry.positions.map((pos) =>
          fromLonLat(pos),
        );
        geometry = new LineString(lineCoords);
        break;
      case "polygon":
        const polyCoords = place.geometry.positions.map((pos) =>
          fromLonLat(pos),
        );
        geometry = new Polygon([polyCoords]);
        break;
      default:
        return null;
    }

    return new Feature({
      geometry,
      place,
      type: "place",
    });
  }

  // ============================================================================
  // Friends Management
  // ============================================================================

  updateFriends(friends: Array<{ friend: Friend; position: Position }>) {
    debug("updateFriends called with %d friends", friends.length);
    this.friendsSource.clear();

    friends.forEach(({ friend, position }) => {
      debug(
        "Adding friend marker: %s at lat=%s, lng=%s",
        friend.username,
        position.lat,
        position.lng,
      );
      if (position.lat && position.lng) {
        const coords = fromLonLat([position.lng, position.lat]);
        debug("Transformed coords: %o", coords);
        const feature = new Feature({
          geometry: new Point(coords),
          friend,
          type: "friend",
        });
        this.friendsSource.addFeature(feature);
        debug("Feature added to friendsSource");
      }
    });

    // Force layer refresh
    this.friendsLayer.changed();
    this.map.render();
    debug(
      "Friends layer updated, feature count: %d",
      this.friendsSource.getFeatures().length,
    );
  }

  // ============================================================================
  // Loading State Management
  // ============================================================================

  onLoadingChange(callback: (loading: boolean) => void): () => void {
    this.loadingCallbacks.add(callback);
    return () => {
      this.loadingCallbacks.delete(callback);
    };
  }

  private notifyLoadingState(loading: boolean) {
    this.loadingCallbacks.forEach((callback) => callback(loading));
  }

  onModeChange(callback: (mode: MapMode) => void): () => void {
    this.modeCallbacks.add(callback);
    // Immediately notify with current mode
    callback(this.mode);
    return () => {
      this.modeCallbacks.delete(callback);
    };
  }

  private notifyModeChange(mode: MapMode) {
    this.modeCallbacks.forEach((callback) => callback(mode));
  }

  // ============================================================================
  // Map Styles
  // ============================================================================

  setBaseMapStyle(styleId: MapStyleId, url: string, transition = true) {
    if (!transition) {
      // Immediate change
      this.baseLayer.setSource(
        new XYZ({
          url,
          attributions: styleId.startsWith("osm")
            ? "© OpenStreetMap contributors"
            : undefined,
        }),
      );
      return;
    }

    // Create new base layer that will replace the old one
    const newBaseLayer = new TileLayer({
      source: new XYZ({
        url,
        attributions: styleId.startsWith("osm")
          ? "© OpenStreetMap contributors"
          : undefined,
      }),
      opacity: 0,
      zIndex: 0,
      preload: 2,
    });

    // Add new layer at the same position as the old base layer
    const layers = this.map.getLayers();
    const baseIndex = layers.getArray().indexOf(this.baseLayer);
    layers.insertAt(baseIndex + 1, newBaseLayer);

    // Two-stage fade with tile loading detection
    let stage = 0;
    let fadeInOpacity = 0;
    let tilesLoaded = false;

    // Notify loading started
    this.notifyLoadingState(true);

    // Listen for tile loading
    const source = newBaseLayer.getSource();
    let tileLoadTimeout: NodeJS.Timeout;

    const startTileLoadListener = () => {
      let checkInterval: NodeJS.Timeout;
      let lastLoadTime = Date.now();
      let hasReceivedEvents = false;

      // Listen for any tile loading activity
      const onTileLoad = () => {
        hasReceivedEvents = true;
        lastLoadTime = Date.now();
        clearTimeout(tileLoadTimeout);

        // Start checking if loading is complete
        if (!checkInterval) {
          checkInterval = setInterval(() => {
            // Check if source has stopped loading for 500ms
            if (Date.now() - lastLoadTime > 500) {
              // All tiles have loaded
              tilesLoaded = true;
              source?.un("tileloadstart", onTileLoad);
              source?.un("tileloadend", onTileLoad);
              clearInterval(checkInterval);
              clearTimeout(tileLoadTimeout);
              this.notifyLoadingState(false);
            }
          }, 100);
        }
      };

      source?.on("tileloadstart", onTileLoad);
      source?.on("tileloadend", onTileLoad);

      // Quick check for cached tiles (no events)
      setTimeout(() => {
        if (!hasReceivedEvents) {
          // Tiles are cached, no loading needed
          tilesLoaded = true;
          source?.un("tileloadstart", onTileLoad);
          source?.un("tileloadend", onTileLoad);
          if (checkInterval) clearInterval(checkInterval);
          clearTimeout(tileLoadTimeout);
          this.notifyLoadingState(false);
        }
      }, 100);

      // Fallback timeout for slow loading
      tileLoadTimeout = setTimeout(() => {
        if (!tilesLoaded) {
          tilesLoaded = true;
          source?.un("tileloadstart", onTileLoad);
          source?.un("tileloadend", onTileLoad);
          if (checkInterval) clearInterval(checkInterval);
          this.notifyLoadingState(false);
        }
      }, 3000);
    };

    const fadeInInterval = setInterval(() => {
      switch (stage) {
        case 0:
          // Fade to 5%
          fadeInOpacity = Math.min(fadeInOpacity + 0.02, 0.05);
          newBaseLayer.setOpacity(fadeInOpacity);
          if (fadeInOpacity >= 0.05) {
            stage = 1;
            startTileLoadListener(); // Start listening for tiles
          }
          break;

        case 1:
          // Wait for tiles to load
          if (tilesLoaded) {
            stage = 2;
          }
          break;

        case 2:
          // Complete fade to 100%
          fadeInOpacity = Math.min(fadeInOpacity + 0.1, 1);
          newBaseLayer.setOpacity(fadeInOpacity);
          if (fadeInOpacity >= 1) {
            clearInterval(fadeInInterval);
            newBaseLayer.setOpacity(1);

            // Remove old base layer and update reference
            this.map.removeLayer(this.baseLayer);
            this.baseLayer = newBaseLayer;
          }
          break;
      }
    }, 30);
  }

  setOverlayMapStyle(
    styleId: MapStyleId,
    url: string,
    opacity = 0.5,
    transition = true,
  ) {
    if (!this.overlayLayer) {
      // Create new overlay layer
      this.overlayLayer = new TileLayer({
        source: new XYZ({ url }),
        opacity: transition ? 0 : opacity,
        zIndex: 1,
        preload: 2,
      });
      this.map.getLayers().insertAt(1, this.overlayLayer);

      // Fade in if transitioning
      if (transition) {
        let currentOpacity = 0;
        const interval = setInterval(() => {
          currentOpacity += opacity / 10;
          if (currentOpacity >= opacity) {
            clearInterval(interval);
            this.overlayLayer!.setOpacity(opacity);
          } else {
            this.overlayLayer!.setOpacity(currentOpacity);
          }
        }, 30);
      }
    } else if (transition) {
      // Crossfade transition for existing overlay
      const newOverlayLayer = new TileLayer({
        source: new XYZ({ url }),
        opacity: 0,
        zIndex: 1,
        preload: 2,
      });

      // Add new overlay at same position
      const layers = this.map.getLayers();
      const overlayIndex = layers.getArray().indexOf(this.overlayLayer);
      layers.insertAt(overlayIndex + 1, newOverlayLayer);

      // Two-stage fade with tile loading detection
      let stage = 0;
      let fadeInOpacity = 0;
      let fadeOutOpacity = this.overlayLayer.getOpacity();
      let tilesLoaded = false;
      const targetOpacity = opacity;
      const firstTarget = 0.05 * targetOpacity;

      // Notify loading started
      this.notifyLoadingState(true);

      // Listen for tile loading
      const source = newOverlayLayer.getSource();
      let tileLoadTimeout: NodeJS.Timeout;

      const startTileLoadListener = () => {
        let checkInterval: NodeJS.Timeout;
        let lastLoadTime = Date.now();
        let hasReceivedEvents = false;

        // Listen for any tile loading activity
        const onTileLoad = () => {
          hasReceivedEvents = true;
          lastLoadTime = Date.now();
          clearTimeout(tileLoadTimeout);

          // Start checking if loading is complete
          if (!checkInterval) {
            checkInterval = setInterval(() => {
              // Check if source has stopped loading for 500ms
              if (Date.now() - lastLoadTime > 500) {
                // All tiles have loaded
                tilesLoaded = true;
                source?.un("tileloadstart", onTileLoad);
                source?.un("tileloadend", onTileLoad);
                clearInterval(checkInterval);
                clearTimeout(tileLoadTimeout);
                this.notifyLoadingState(false);
              }
            }, 100);
          }
        };

        source?.on("tileloadstart", onTileLoad);
        source?.on("tileloadend", onTileLoad);

        // Quick check for cached tiles (no events)
        setTimeout(() => {
          if (!hasReceivedEvents) {
            // Tiles are cached, no loading needed
            tilesLoaded = true;
            source?.un("tileloadstart", onTileLoad);
            source?.un("tileloadend", onTileLoad);
            if (checkInterval) clearInterval(checkInterval);
            clearTimeout(tileLoadTimeout);
            this.notifyLoadingState(false);
          }
        }, 100);

        // Fallback timeout for slow loading
        tileLoadTimeout = setTimeout(() => {
          if (!tilesLoaded) {
            tilesLoaded = true;
            source?.un("tileloadstart", onTileLoad);
            source?.un("tileloadend", onTileLoad);
            if (checkInterval) clearInterval(checkInterval);
            this.notifyLoadingState(false);
          }
        }, 3000);
      };

      const fadeInInterval = setInterval(() => {
        switch (stage) {
          case 0:
            // Fade out old overlay while fading in new to 5%
            fadeInOpacity = Math.min(
              fadeInOpacity + firstTarget / 3,
              firstTarget,
            );
            fadeOutOpacity = Math.max(fadeOutOpacity - fadeOutOpacity / 3, 0);
            newOverlayLayer.setOpacity(fadeInOpacity);
            this.overlayLayer!.setOpacity(fadeOutOpacity);
            if (fadeInOpacity >= firstTarget) {
              stage = 1;
              startTileLoadListener();
            }
            break;

          case 1:
            // Wait for tiles
            if (tilesLoaded) {
              stage = 2;
            }
            break;

          case 2:
            // Complete fade to target
            fadeInOpacity = Math.min(
              fadeInOpacity + targetOpacity / 10,
              targetOpacity,
            );
            newOverlayLayer.setOpacity(fadeInOpacity);
            if (fadeInOpacity >= targetOpacity) {
              clearInterval(fadeInInterval);
              newOverlayLayer.setOpacity(targetOpacity);

              // Remove old overlay and update reference
              this.map.removeLayer(this.overlayLayer!);
              this.overlayLayer = newOverlayLayer;
            }
            break;
        }
      }, 30);
    } else {
      // Immediate change
      this.overlayLayer.setSource(new XYZ({ url }));
      this.overlayLayer.setOpacity(opacity);
    }
  }

  updateOverlayOpacity(opacity: number, smooth = true) {
    if (!this.overlayLayer) return;

    if (!smooth) {
      this.overlayLayer.setOpacity(opacity);
      return;
    }

    // Smooth opacity transition
    const currentOpacity = this.overlayLayer.getOpacity();
    const diff = opacity - currentOpacity;
    const steps = 10;
    const stepSize = diff / steps;
    let step = 0;

    const interval = setInterval(() => {
      step++;
      if (step >= steps) {
        clearInterval(interval);
        if (this.overlayLayer) {
          this.overlayLayer.setOpacity(opacity);
        }
      } else {
        if (this.overlayLayer) {
          this.overlayLayer.setOpacity(currentOpacity + stepSize * step);
        } else {
          // Overlay was removed during animation
          clearInterval(interval);
        }
      }
    }, 20);
  }

  removeOverlay(transition = true) {
    if (!this.overlayLayer) return;

    if (!transition) {
      this.map.removeLayer(this.overlayLayer);
      this.overlayLayer = undefined;
      return;
    }

    // Fade out then remove
    const currentOpacity = this.overlayLayer.getOpacity();
    let opacity = currentOpacity;

    const interval = setInterval(() => {
      opacity -= currentOpacity / 10;
      if (opacity <= 0) {
        clearInterval(interval);
        this.map.removeLayer(this.overlayLayer!);
        this.overlayLayer = undefined;
      } else {
        this.overlayLayer!.setOpacity(opacity);
      }
    }, 30);
  }

  // Transition to satellite underlay mode
  transitionToSatelliteUnderlay(
    mapStyleId: MapStyleId,
    mapStyleUrl: string,
    overlayOpacity = 0.7,
  ) {
    const satelliteUrl =
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

    // Create satellite layer underneath current base
    const satelliteLayer = new TileLayer({
      source: new XYZ({ url: satelliteUrl }),
      opacity: 0,
      zIndex: 0,
      preload: 2,
    });

    // Add satellite layer below current base
    const layers = this.map.getLayers();
    const baseIndex = layers.getArray().indexOf(this.baseLayer);
    layers.insertAt(baseIndex, satelliteLayer);

    // Create overlay from current map style
    const overlayLayer = new TileLayer({
      source: new XYZ({ url: mapStyleUrl }),
      opacity: 0,
      zIndex: 1,
      preload: 2,
    });
    layers.insertAt(baseIndex + 2, overlayLayer);

    // Two-stage transition with pauses
    let stage = 0;
    let progress = 0;

    const interval = setInterval(() => {
      switch (stage) {
        case 0:
          // Fade to 33%
          progress = 0.05;
          satelliteLayer.setOpacity(progress);
          overlayLayer.setOpacity(progress * overlayOpacity);
          this.baseLayer.setOpacity(1 - progress);
          stage = 1;
          setTimeout(() => {
            stage = 2;
          }, 500); // Pause 500ms at 33%
          break;

        case 2:
          // Fade to 90%
          progress = Math.min(progress + 0.1, 0.9);
          satelliteLayer.setOpacity(progress);
          overlayLayer.setOpacity(progress * overlayOpacity);
          this.baseLayer.setOpacity(1 - progress);
          if (progress >= 0.9) {
            stage = 3;
            setTimeout(() => {
              stage = 4;
            }, 500); // Pause 500ms before 100%
          }
          break;

        case 4:
          // Complete to 100%
          clearInterval(interval);

          // Final state: remove old base, update references
          this.map.removeLayer(this.baseLayer);
          this.baseLayer = satelliteLayer;
          this.baseLayer.setOpacity(1);

          if (this.overlayLayer) {
            this.map.removeLayer(this.overlayLayer);
          }
          this.overlayLayer = overlayLayer;
          this.overlayLayer.setOpacity(overlayOpacity);
          break;
      }
    }, 30);
  }

  // Transition from satellite underlay back to normal mode
  transitionFromSatelliteUnderlay(mapStyleId: MapStyleId, mapStyleUrl: string) {
    // Create new base layer
    const newBaseLayer = new TileLayer({
      source: new XYZ({
        url: mapStyleUrl,
        attributions: mapStyleId.startsWith("osm")
          ? "© OpenStreetMap contributors"
          : undefined,
      }),
      opacity: 0,
      zIndex: 0,
      preload: 2,
    });

    const layers = this.map.getLayers();
    const baseIndex = layers.getArray().indexOf(this.baseLayer);
    layers.insertAt(baseIndex + 1, newBaseLayer);

    // Animated transition with tile preload
    let step = 0;
    let pauseComplete = false;
    const overlayStartOpacity = this.overlayLayer?.getOpacity() || 0;

    const interval = setInterval(() => {
      if (step === 0) {
        // First step: fade new base to 5% opacity for tile loading
        newBaseLayer.setOpacity(0.05);
        this.baseLayer.setOpacity(0.95);
        if (this.overlayLayer) {
          this.overlayLayer.setOpacity(overlayStartOpacity * 0.95);
        }
        step = 1;

        // Pause for 1 second to let tiles load
        setTimeout(() => {
          pauseComplete = true;
        }, 1000);
        return;
      }

      if (!pauseComplete) return;

      step++;
      if (step >= 10) {
        clearInterval(interval);

        // Final state
        this.map.removeLayer(this.baseLayer);
        this.baseLayer = newBaseLayer;
        this.baseLayer.setOpacity(1);

        if (this.overlayLayer) {
          this.map.removeLayer(this.overlayLayer);
          this.overlayLayer = undefined;
        }
      } else {
        // Continue fade in new base
        newBaseLayer.setOpacity(step / 10);
        // Fade out satellite
        this.baseLayer.setOpacity(1 - step / 10);
        // Fade out overlay if exists
        if (this.overlayLayer) {
          this.overlayLayer.setOpacity(overlayStartOpacity * (1 - step / 10));
        }
      }
    }, 30);
  }

  // ============================================================================
  // View Controls
  // ============================================================================

  getView() {
    return this.view;
  }

  getMode() {
    return this.mode;
  }

  setMode(mode: MapMode) {
    debug("🎮 Setting map mode from '%s' to '%s'", this.mode, mode);

    // Save mode to localStorage for persistence
    localStorage.setItem("mapMode", mode);
    const previousMode = this.mode;

    // If we're in the middle of a transition, capture current state
    let currentOffset = 0;
    let currentRotation = this.view.getRotation();

    if (this.transitionState && this.transitionState.active) {
      // Continue from current transition point
      currentOffset = this.transitionState.currentOffset;
      currentRotation = this.transitionState.currentRotation;
      debug(
        "Continuing from existing transition: offset=%s, rotation=%s°",
        currentOffset.toFixed(2),
        ((currentRotation * 180) / Math.PI).toFixed(1),
      );
    } else {
      // Determine current offset based on previous mode
      if (previousMode === "follow-heading") {
        currentOffset = 0.2;
      } else if (previousMode === "follow" || previousMode === "follow-north") {
        currentOffset = 0;
      }
    }

    // Start manual transition for mode changes between follow modes
    if (
      previousMode !== mode &&
      ((previousMode === "follow" && mode === "follow-heading") ||
        (previousMode === "follow-north" && mode === "follow-heading") ||
        (previousMode === "follow-heading" && mode === "follow") ||
        (previousMode === "follow-heading" && mode === "follow-north") ||
        (previousMode === "follow" && mode === "follow-north") ||
        (previousMode === "follow-north" && mode === "follow"))
    ) {
      // Calculate target rotation based on mode
      let targetRotation = currentRotation;
      if (mode === "follow-north") {
        // Going to follow-north mode - rotate to north
        targetRotation = 0;
      } else if (mode === "follow") {
        // Going to follow mode - keep current rotation (free rotation)
        targetRotation = currentRotation;
      } else if (mode === "follow-heading") {
        // Going to follow-heading mode - rotate to match current heading
        targetRotation = -(this.currentHeading * Math.PI) / 180;
      }

      // Calculate target offset
      let targetOffset = 0;
      if (mode === "follow-heading") {
        targetOffset = 0.2;
      }

      // Initialize transition state from current values
      this.transitionState = {
        active: true,
        startTime: Date.now(),
        duration: 750, // 0.75 seconds for faster transition
        // Offset
        fromOffset: currentOffset,
        toOffset: targetOffset,
        currentOffset: currentOffset,
        // Rotation
        fromRotation: currentRotation,
        toRotation: targetRotation,
        currentRotation: currentRotation,
      };

      debug(
        "Starting manual transition: offset %s->%s, rotation %s->%s",
        this.transitionState.fromOffset.toFixed(2),
        this.transitionState.toOffset.toFixed(2),
        ((this.transitionState.fromRotation * 180) / Math.PI).toFixed(1),
        ((this.transitionState.toRotation * 180) / Math.PI).toFixed(1),
      );
    }

    // Stop rotation animation when leaving follow-heading mode
    if (previousMode === "follow-heading" && mode !== "follow-heading") {
      this.stopRotationAnimation();
    }

    // Skip OpenLayers animation when we have a manual transition
    if (previousMode !== mode && mode !== "free" && !this.transitionState) {
      const geometry = this.positionFeature.getGeometry();
      if (geometry) {
        const currentCoords = (geometry as Point).getCoordinates();

        // Calculate target based on the NEW mode (not yet set)
        // Temporarily set mode to calculate target, then restore
        const savedMode = this.mode;
        this.mode = mode;
        const targetCoords = this.getOffsetCenter(currentCoords);
        this.mode = savedMode;

        // Check if we actually need to animate (position might already be correct)
        const currentCenter = this.view.getCenter();
        const distance = currentCenter
          ? Math.sqrt(
              Math.pow(targetCoords[0] - currentCenter[0], 2) +
                Math.pow(targetCoords[1] - currentCenter[1], 2),
            )
          : Infinity;

        // Check if we need to rotate (only for follow-north mode)
        const currentRotation = this.view.getRotation();
        const needsRotation =
          previousMode === "follow-heading" &&
          mode === "follow-north" &&
          Math.abs(currentRotation) > 0.01;

        // Only animate if we need to move OR rotate
        const resolution = this.view.getResolution();
        if (resolution && (distance > resolution * 10 || needsRotation)) {
          // Cancel any existing animation timeout
          if (this.animationTimeout) {
            clearTimeout(this.animationTimeout);
            this.animationTimeout = undefined;
          }

          // Set animating flag to prevent position updates from interfering
          this.isAnimating = true;

          // Build animation options
          const animationOptions: any = {
            center: targetCoords,
            duration: 600,
          };

          // Add rotation if needed (only when going to follow-north)
          if (needsRotation) {
            animationOptions.rotation = 0;
          }

          // Animate both center and rotation together
          this.view.animate(animationOptions);

          // Use a timeout to clear the animating flag
          this.animationTimeout = window.setTimeout(() => {
            this.isAnimating = false;
            this.animationTimeout = undefined;
            debug("Animation complete, resuming position tracking");
          }, 650);
        }
      }
    }

    // Now actually set the mode
    this.mode = mode;

    // Notify listeners of mode change
    this.notifyModeChange(mode);
  }

  setDebugOverlayShowing(showing: boolean) {
    this.isDebugOverlayShowing = showing;
    debug("Debug overlay showing: %s", showing);
  }

  centerOnPosition(position?: Position) {
    if (position?.lat && position?.lng) {
      const coords = fromLonLat([position.lng, position.lat]);
      this.animateToCenter(coords);
    } else {
      // Center on current GPS position marker
      const geometry = this.positionFeature.getGeometry();
      if (geometry) {
        const coords = (geometry as Point).getCoordinates();
        this.animateToCenter(coords);
      }
    }
  }

  /**
   * Fit the map view to a bounding box
   * @param bounds Object with minLon, minLat, maxLon, maxLat in degrees
   * @param options Optional padding and duration
   */
  /**
   * Convert GeoJSON coordinates to OpenLayers format
   */
  private convertGeoJSONToOLCoords(geojson: any): number[][][] | null {
    if (!geojson || !geojson.coordinates) {
      return null;
    }

    const type = geojson.type;
    const coords = geojson.coordinates;

    // Convert coordinates based on GeoJSON type
    if (type === "Polygon") {
      // For Polygon, coordinates are already in the right structure
      // Just need to transform from [lon, lat] to map projection
      return coords.map((ring: number[][]) =>
        ring.map((coord: number[]) => fromLonLat([coord[0], coord[1]])),
      );
    } else if (type === "MultiPolygon") {
      // For MultiPolygon, take the first polygon
      // (we could handle multiple polygons, but for simplicity just use the first)
      if (coords.length > 0) {
        return coords[0].map((ring: number[][]) =>
          ring.map((coord: number[]) => fromLonLat([coord[0], coord[1]])),
        );
      }
    } else if (type === "LineString") {
      // Convert LineString to a closed polygon (if it's meant to be a boundary)
      debug("Converting LineString to polygon");
      const transformedCoords = coords.map((coord: number[]) =>
        fromLonLat([coord[0], coord[1]]),
      );

      // Check if it's closed (first and last points are the same)
      const first = transformedCoords[0];
      const last = transformedCoords[transformedCoords.length - 1];
      const isClosed = first[0] === last[0] && first[1] === last[1];

      if (!isClosed) {
        // Close the polygon
        transformedCoords.push(first);
      }

      // Return as a single ring polygon
      return [transformedCoords];
    } else if (type === "Point") {
      // Can't create a polygon from a point
      debug("Cannot create polygon from Point geometry");
      return null;
    }

    debug("Unsupported GeoJSON type for polygon conversion: %s", type);
    return null;
  }

  fitBounds(
    bounds: { minLon: number; minLat: number; maxLon: number; maxLat: number },
    options?: { padding?: number; duration?: number },
  ) {
    debug("fitBounds called with bounds:", bounds);

    // Transform the corner points
    const bottomLeft = fromLonLat([bounds.minLon, bounds.minLat]);
    const topRight = fromLonLat([bounds.maxLon, bounds.maxLat]);

    // Create extent array [minX, minY, maxX, maxY]
    const extent = [
      bottomLeft[0], // minX
      bottomLeft[1], // minY
      topRight[0], // maxX
      topRight[1], // maxY
    ];

    debug("Calculated extent:", extent);

    const padding = options?.padding ?? 50; // Default 50px padding
    const duration = options?.duration ?? 800; // Default 800ms animation

    debug("Calling view.fit with extent and padding:", padding);
    debug(
      "Extent values: minX=%f, minY=%f, maxX=%f, maxY=%f",
      extent[0],
      extent[1],
      extent[2],
      extent[3],
    );

    // Ensure we're in free mode and not animating
    if (this.mode !== "free") {
      debug("Setting mode to free before fitting bounds");
      this.setMode("free");
    }

    // Cancel any ongoing animations
    this.view.cancelAnimations();

    // Get current zoom and center for debugging
    const currentZoom = this.view.getZoom();
    const currentCenter = this.view.getCenter();
    debug("Current zoom: %d, center: %o", currentZoom, currentCenter);

    // Check if extent is valid
    if (extent[0] >= extent[2] || extent[1] >= extent[3]) {
      debug("ERROR: Invalid extent - minX >= maxX or minY >= maxY");
      return;
    }

    // Try a more direct approach - calculate the resolution needed
    const size = this.map.getSize();
    if (size) {
      const extentWidth = extent[2] - extent[0];
      const extentHeight = extent[3] - extent[1];

      // Account for padding
      const effectivePadding = padding * 2;
      const effectiveWidth = size[0] - effectivePadding;
      const effectiveHeight = size[1] - effectivePadding;

      // Calculate resolution needed for each dimension
      const resolutionX = extentWidth / effectiveWidth;
      const resolutionY = extentHeight / effectiveHeight;

      // Use the larger resolution to ensure everything fits
      let targetResolution = Math.max(resolutionX, resolutionY);

      // Calculate center
      const centerX = (extent[0] + extent[2]) / 2;
      const centerY = (extent[1] + extent[3]) / 2;

      // Get current zoom and resolution
      const currentZoom = this.view.getZoom() || 15;
      const currentResolution = this.view.getResolution() || targetResolution;

      // Convert zoom level 20 to resolution for max zoom constraint
      const maxZoomResolution = this.view.getResolutionForZoom(20);

      // Ensure we show at least 1 mile (unless already zoomed in more)
      const mileInDegrees = 1 / 69; // 1 degree latitude ≈ 69 miles
      const mileInMeters = mileInDegrees * 111320; // Convert to Web Mercator units (meters at equator)
      const minExtentSize = mileInMeters;
      const minResolution =
        minExtentSize / Math.min(effectiveWidth, effectiveHeight);

      // Don't zoom in more than level 20 (smaller resolution = more zoomed in)
      // Only apply when trying to zoom IN (targetResolution < maxZoomResolution)
      // Allow zooming OUT freely (targetResolution > currentResolution)
      if (
        targetResolution < maxZoomResolution &&
        targetResolution < currentResolution
      ) {
        debug(
          "Limiting zoom IN to level 20 (resolution %f)",
          maxZoomResolution,
        );
        targetResolution = maxZoomResolution;
      }

      // Ensure we show at least 1 mile when zooming IN
      // Only apply when trying to zoom IN (targetResolution < minResolution)
      // Allow zooming OUT freely (targetResolution > currentResolution)
      if (
        targetResolution < minResolution &&
        targetResolution < currentResolution
      ) {
        debug(
          "Ensuring at least 1 mile shown when zooming IN (resolution %f)",
          minResolution,
        );
        targetResolution = minResolution;
      }

      debug(
        "Calculated target resolution: %f, center: [%f, %f]",
        targetResolution,
        centerX,
        centerY,
      );

      // Try setting directly first (same fix as in displaySelectedFeature)
      debug("Setting resolution directly to %f for boundary", targetResolution);

      // Cancel any ongoing animations
      this.view.cancelAnimations();

      // Set resolution and center directly
      this.view.setResolution(targetResolution);
      this.view.setCenter([centerX, centerY]);

      // Check if it actually changed
      const immediateRes = this.view.getResolution();
      debug(
        "Immediately after setResolution for boundary: res=%f",
        immediateRes,
      );

      if (immediateRes && Math.abs(immediateRes - targetResolution) > 0.01) {
        debug(
          "ERROR: Boundary resolution did not change! target=%f, actual=%f",
          targetResolution,
          immediateRes,
        );

        // Try view.fit as fallback
        debug("Attempting view.fit for boundary as fallback");
        this.view.fit(extent, {
          padding: [padding, padding, padding, padding],
          duration: duration,
        });
      }
    } else {
      // Fallback to original fit method
      this.view.fit(extent, {
        padding: [padding, padding, padding, padding],
        duration,
      });
    }

    // Check if the view actually changed
    setTimeout(() => {
      const newZoom = this.view.getZoom();
      const newCenter = this.view.getCenter();
      debug("After fit - zoom: %d, center: %o", newZoom, newCenter);
      if (currentZoom === newZoom && currentCenter?.[0] === newCenter?.[0]) {
        debug("WARNING: View did not change after fit!");
      }
    }, duration + 100);
  }

  /**
   * Find connected segments for a relation's member ways
   * Uses ALL member ways as initial segments
   */
  findConnectedSegmentsForRelation(
    memberWays: any[],
    allElements: any[],
    relationName?: string,
  ): any[] {
    if (memberWays.length === 0) return [];

    // Use first member to determine search parameters
    const firstMember = memberWays[0];
    const highway = firstMember.tags?.highway;

    // Get bounds of all member ways for search radius
    let minLat = Infinity,
      maxLat = -Infinity,
      minLon = Infinity,
      maxLon = -Infinity;
    for (const way of memberWays) {
      if (way.geometry && way.geometry.length > 0) {
        for (const point of way.geometry) {
          minLat = Math.min(minLat, point.lat);
          maxLat = Math.max(maxLat, point.lat);
          minLon = Math.min(minLon, point.lon);
          maxLon = Math.max(maxLon, point.lon);
        }
      }
    }

    const centerLat = (minLat + maxLat) / 2;
    const centerLon = (minLon + maxLon) / 2;
    const MAX_SEARCH_RADIUS = 3 / 69;

    const searchMinLat = centerLat - MAX_SEARCH_RADIUS;
    const searchMaxLat = centerLat + MAX_SEARCH_RADIUS;
    const searchMinLon = centerLon - MAX_SEARCH_RADIUS;
    const searchMaxLon = centerLon + MAX_SEARCH_RADIUS;

    const nearbyElements = allElements.filter((e: any) => {
      if (e.type !== "way" || !e.geometry || e.geometry.length === 0)
        return false;
      const elemLat = e.geometry[0].lat;
      const elemLon = e.geometry[0].lon;
      return (
        elemLat >= searchMinLat &&
        elemLat <= searchMaxLat &&
        elemLon >= searchMinLon &&
        elemLon <= searchMaxLon
      );
    });

    const allWays = nearbyElements;
    const targetName = relationName;

    const sameNameWays = targetName
      ? allWays.filter(
          (e: any) =>
            e.tags?.name === targetName &&
            e.geometry &&
            e.geometry.length > 0 &&
            (e.tags?.highway === "cycleway" ||
              e.tags?.highway === "path" ||
              e.tags?.highway === "track" ||
              e.tags?.highway === "footway"),
        )
      : [];

    // Collect endpoints from ALL member ways
    const trailEndpoints: Array<{ lat: number; lon: number }> = [];
    for (const way of memberWays) {
      if (way.geometry && way.geometry.length > 0) {
        trailEndpoints.push(way.geometry[0]);
        trailEndpoints.push(way.geometry[way.geometry.length - 1]);
      }
    }

    const SEARCH_RADIUS = 1 / 69;
    const relevantCycleways: any[] = [];
    const relevantIds = new Set<string>();

    const isTargetStreet =
      highway &&
      !["cycleway", "path", "footway", "track", "pedestrian"].includes(highway);

    for (const element of allWays) {
      if (element.geometry && element.geometry.length > 0) {
        const elHighway = element.tags?.highway;
        const isRelevantType =
          elHighway === "cycleway" ||
          elHighway === "path" ||
          elHighway === "footway" ||
          elHighway === "pedestrian" ||
          element.tags?.bicycle === "yes" ||
          (isTargetStreet && elHighway === highway);

        if (isRelevantType) {
          if (targetName && element.tags?.name === targetName) {
            const elementId = `${element.type}_${element.id}`;
            if (!relevantIds.has(elementId)) {
              relevantIds.add(elementId);
              relevantCycleways.push(element);
            }
            continue;
          }

          let isNearby = false;
          const wayStart = element.geometry[0];
          const wayEnd = element.geometry[element.geometry.length - 1];

          for (const endpoint of trailEndpoints) {
            if (
              (Math.abs(wayStart.lat - endpoint.lat) < SEARCH_RADIUS &&
                Math.abs(wayStart.lon - endpoint.lon) < SEARCH_RADIUS) ||
              (Math.abs(wayEnd.lat - endpoint.lat) < SEARCH_RADIUS &&
                Math.abs(wayEnd.lon - endpoint.lon) < SEARCH_RADIUS)
            ) {
              isNearby = true;
              break;
            }
          }

          if (isNearby) {
            const elementId = `${element.type}_${element.id}`;
            if (!relevantIds.has(elementId)) {
              relevantIds.add(elementId);
              relevantCycleways.push(element);
            }
          }
        }
      }
    }

    const allCycleways = relevantCycleways;
    debug(
      "Final filter: %d relevant cycleways/paths for relation",
      allCycleways.length,
    );

    // Helper to check if two segments are connected - COPIED FROM OVERPASSSERVICE
    const areConnected = (seg1: any, seg2: any): boolean => {
      if (!seg1.geometry || !seg2.geometry) return false;
      const seg1Start = seg1.geometry[0];
      const seg1End = seg1.geometry[seg1.geometry.length - 1];
      const seg2Start = seg2.geometry[0];
      const seg2End = seg2.geometry[seg2.geometry.length - 1];
      // More lenient threshold for connections (about 50 meters)
      const threshold = 0.0005;
      // Check all possible connections
      return (
        (Math.abs(seg1Start.lat - seg2Start.lat) < threshold &&
          Math.abs(seg1Start.lon - seg2Start.lon) < threshold) ||
        (Math.abs(seg1Start.lat - seg2End.lat) < threshold &&
          Math.abs(seg1Start.lon - seg2End.lon) < threshold) ||
        (Math.abs(seg1End.lat - seg2Start.lat) < threshold &&
          Math.abs(seg1End.lon - seg2Start.lon) < threshold) ||
        (Math.abs(seg1End.lat - seg2End.lat) < threshold &&
          Math.abs(seg1End.lon - seg2End.lon) < threshold)
      );
    };

    // Improved connection algorithm - COPIED FROM OVERPASSSERVICE
    const visited = new Set<string>();
    const connected: any[] = [];
    const connectorSegments = new Set<string>();
    const primaryTrailIds = new Set<string>();
    const connectedNamedRoutes = new Set<string>(); // Track connected named routes (yellow)

    // Start with ALL member ways as initial segments
    const initialSegments: any[] = [...memberWays];

    // Add all initial segments to visited and connected
    for (const segment of initialSegments) {
      const segId = `${segment.type}_${segment.id}`;
      visited.add(segId);
      connected.push(segment);
      primaryTrailIds.add(segId);
    }

    const queue: any[] = [...initialSegments];
    let processed = 0;
    const maxProcessed = 30;
    const startTime = Date.now();
    const TIMEOUT_MS = 3000;

    while (
      queue.length > 0 &&
      processed < maxProcessed &&
      connected.length < 50
    ) {
      if (processed > 0 && processed % 5 === 0) {
        const elapsed = Date.now() - startTime;
        if (elapsed > TIMEOUT_MS) {
          debug(
            "Relation search timeout after %.1fs (processed %d, found %d)",
            elapsed / 1000,
            processed,
            connected.length,
          );
          break;
        }
      }

      const current = queue.shift()!;
      processed++;

      if (!current.geometry || current.geometry.length === 0) continue;

      for (const seg of allCycleways) {
        const segId = `${seg.type}_${seg.id}`;
        if (visited.has(segId)) continue;

        if (!areConnected(current, seg)) continue;

        // For same-name segments, add immediately as PRIMARY
        if (seg.tags?.name === targetName) {
          visited.add(segId);
          connected.push(seg);
          primaryTrailIds.add(segId);
          queue.push(seg);
          debug("Added same-name segment: %s (%s)", seg.tags?.name, seg.id);
        }
        // Connected named route (different name) - mark as YELLOW
        else if (seg.tags?.name && processed < 20) {
          visited.add(segId);
          connected.push(seg);
          connectedNamedRoutes.add(segId);
          queue.push(seg);
          debug("Added connected named route: %s", seg.tags.name);
        }
        // Unnamed ways - don't add to connected at all, let them show as gray nearby
        // (overpassService has complex logic to check if they're continuations, we skip that)
      }
    }

    debug(
      "Relation BFS found %d connected segments after %d iterations",
      connected.length,
      processed,
    );

    // IMPORTANT: Add ALL same-name segments as primary, even if disconnected - FROM OVERPASSSERVICE
    if (targetName) {
      const connectedIds = new Set(
        connected.map((c: any) => `${c.type}_${c.id}`),
      );
      for (const element of allCycleways) {
        const elementId = `${element.type}_${element.id}`;
        if (element.tags?.name === targetName && !connectedIds.has(elementId)) {
          debug(
            "Adding disconnected same-name segment: %s (%s)",
            element.tags?.name,
            element.id,
          );
          connected.push(element);
          primaryTrailIds.add(elementId); // Mark as primary since it has the same name
        }
      }
    }

    debug(
      "After adding disconnected same-name segments: %d total segments",
      connected.length,
    );

    // CRITICAL: Get ALL ways within 1 mile of ANY point along connected segments - FROM OVERPASSSERVICE
    const allNearbyWays: any[] = [];
    const nearbyDistance = 1 / 69; // 1 mile in degrees
    const connectedIds = new Set(
      connected.map((c: any) => `${c.type}_${c.id}`),
    );

    // Collect endpoints from all connected segments
    const connectedPoints: Array<{ lat: number; lon: number }> = [];
    for (const seg of connected) {
      if (seg.geometry && seg.geometry.length > 0) {
        connectedPoints.push(seg.geometry[0]);
        connectedPoints.push(seg.geometry[seg.geometry.length - 1]);
        if (seg.geometry.length > 20) {
          const midIdx = Math.floor(seg.geometry.length / 2);
          connectedPoints.push(seg.geometry[midIdx]);
        }
      }
    }

    for (const element of allWays) {
      if (element.geometry && element.geometry.length > 0) {
        const elHighway = element.tags?.highway;
        const isRelevantType =
          elHighway === "cycleway" ||
          elHighway === "path" ||
          elHighway === "footway" ||
          elHighway === "track" ||
          elHighway === "pedestrian" ||
          element.tags?.bicycle === "yes" ||
          (isTargetStreet && elHighway === highway);

        if (!isRelevantType) continue;

        // Check endpoints for proximity
        let isNearby = false;
        const checkPoints = [
          element.geometry[0],
          element.geometry[element.geometry.length - 1],
        ];

        outer: for (const checkPoint of checkPoints) {
          for (const connectedPoint of connectedPoints) {
            if (
              Math.abs(checkPoint.lat - connectedPoint.lat) < nearbyDistance &&
              Math.abs(checkPoint.lon - connectedPoint.lon) < nearbyDistance
            ) {
              isNearby = true;
              break outer;
            }
          }
        }

        if (isNearby) {
          const elementId = `${element.type}_${element.id}`;
          const isConnected = connectedIds.has(elementId);
          const isPrimary = primaryTrailIds.has(elementId);
          const isConnectedRoute = connectedNamedRoutes.has(elementId);

          const wayWithDebugInfo = {
            ...element,
            _debug_connected: isConnected,
            _debug_primary: isPrimary,
            _debug_same_name: element.tags?.name === targetName,
            _debug_is_connector: connectorSegments.has(elementId),
            _debug_connected_route: isConnectedRoute, // For yellow highlighting
          };
          allNearbyWays.push(wayWithDebugInfo);
        }
      }
    }

    debug(
      "Returning %d connected + %d total nearby ways for visualization",
      connected.length,
      allNearbyWays.length,
    );

    // Return all nearby ways with debug info, or at least the connected segments
    if (allNearbyWays.length > 0) {
      return allNearbyWays;
    } else if (connected.length > 0) {
      debug(
        "No nearby ways found, returning %d connected segments",
        connected.length,
      );
      return connected.map((seg: any) => {
        const segId = `${seg.type}_${seg.id}`;
        return {
          ...seg,
          _debug_connected: true,
          _debug_primary: primaryTrailIds.has(segId),
          _debug_same_name: seg.tags?.name === targetName,
          _debug_connected_route: connectedNamedRoutes.has(segId),
        };
      });
    } else {
      // Return the member ways as fallback
      return memberWays;
    }
  }

  /**
   * Find connected segments for a way using the full algorithm from overpassService
   */
  findConnectedSegments(targetElement: any, allElements: any[]): any[] {
    const targetName = targetElement.tags?.name;
    const highway = targetElement.tags?.highway;

    const isTrailOrPath =
      highway &&
      [
        "cycleway",
        "path",
        "footway",
        "track",
        "pedestrian",
        "primary",
        "secondary",
        "tertiary",
        "residential",
        "unclassified",
        "service",
        "living_street",
      ].includes(highway);

    if (!isTrailOrPath || !targetElement.geometry) {
      return [targetElement];
    }

    // Get target center for distance filtering
    const targetLat =
      targetElement.geometry[Math.floor(targetElement.geometry.length / 2)].lat;
    const targetLon =
      targetElement.geometry[Math.floor(targetElement.geometry.length / 2)].lon;
    const MAX_SEARCH_RADIUS = 3 / 69;

    const minLat = targetLat - MAX_SEARCH_RADIUS;
    const maxLat = targetLat + MAX_SEARCH_RADIUS;
    const minLon = targetLon - MAX_SEARCH_RADIUS;
    const maxLon = targetLon + MAX_SEARCH_RADIUS;

    const nearbyElements = allElements.filter((e: any) => {
      if (e.type !== "way" || !e.geometry || e.geometry.length === 0)
        return false;
      const elemLat = e.geometry[0].lat;
      const elemLon = e.geometry[0].lon;
      return (
        elemLat >= minLat &&
        elemLat <= maxLat &&
        elemLon >= minLon &&
        elemLon <= maxLon
      );
    });

    const allWays = nearbyElements;

    const sameNameWays = targetName
      ? allWays.filter(
          (e: any) =>
            e.tags?.name === targetName &&
            e.geometry &&
            e.geometry.length > 0 &&
            (e.tags?.highway === "cycleway" ||
              e.tags?.highway === "path" ||
              e.tags?.highway === "track" ||
              e.tags?.highway === "footway"),
        )
      : [];

    debug(
      "Found %d ways with name '%s'",
      sameNameWays.length,
      targetName || "UNNAMED",
    );

    const trailEndpoints: Array<{ lat: number; lon: number }> = [];
    for (const way of sameNameWays) {
      if (way.geometry && way.geometry.length > 0) {
        trailEndpoints.push(way.geometry[0]);
        trailEndpoints.push(way.geometry[way.geometry.length - 1]);
      }
    }

    const SEARCH_RADIUS = 1 / 69;
    const relevantCycleways: any[] = [];
    const relevantIds = new Set<string>();

    const isTargetStreet =
      highway &&
      !["cycleway", "path", "footway", "track", "pedestrian"].includes(highway);

    for (const element of allWays) {
      if (element.geometry && element.geometry.length > 0) {
        const elHighway = element.tags?.highway;
        const isRelevantType =
          elHighway === "cycleway" ||
          elHighway === "path" ||
          elHighway === "footway" ||
          elHighway === "pedestrian" ||
          element.tags?.bicycle === "yes" ||
          (isTargetStreet && elHighway === highway);

        if (isRelevantType) {
          if (targetName && element.tags?.name === targetName) {
            const elementId = `${element.type}_${element.id}`;
            if (!relevantIds.has(elementId)) {
              relevantIds.add(elementId);
              relevantCycleways.push(element);
            }
            continue;
          }

          let isNearby = false;
          const wayStart = element.geometry[0];
          const wayEnd = element.geometry[element.geometry.length - 1];

          for (const endpoint of trailEndpoints) {
            if (
              (Math.abs(wayStart.lat - endpoint.lat) < SEARCH_RADIUS &&
                Math.abs(wayStart.lon - endpoint.lon) < SEARCH_RADIUS) ||
              (Math.abs(wayEnd.lat - endpoint.lat) < SEARCH_RADIUS &&
                Math.abs(wayEnd.lon - endpoint.lon) < SEARCH_RADIUS)
            ) {
              isNearby = true;
              break;
            }
          }

          if (isNearby) {
            const elementId = `${element.type}_${element.id}`;
            if (!relevantIds.has(elementId)) {
              relevantIds.add(elementId);
              relevantCycleways.push(element);
            }
          }
        }
      }
    }

    const allCycleways = relevantCycleways;
    debug(
      "Final filter: %d relevant cycleways/paths (includes %d same-name)",
      allCycleways.length,
      sameNameWays.length,
    );

    // Helper to check if two segments are connected - COPIED FROM OVERPASSSERVICE
    const areConnected = (seg1: any, seg2: any): boolean => {
      if (!seg1.geometry || !seg2.geometry) return false;
      const seg1Start = seg1.geometry[0];
      const seg1End = seg1.geometry[seg1.geometry.length - 1];
      const seg2Start = seg2.geometry[0];
      const seg2End = seg2.geometry[seg2.geometry.length - 1];
      // More lenient threshold for connections (about 50 meters)
      const threshold = 0.0005;
      // Check all possible connections
      return (
        (Math.abs(seg1Start.lat - seg2Start.lat) < threshold &&
          Math.abs(seg1Start.lon - seg2Start.lon) < threshold) ||
        (Math.abs(seg1Start.lat - seg2End.lat) < threshold &&
          Math.abs(seg1Start.lon - seg2End.lon) < threshold) ||
        (Math.abs(seg1End.lat - seg2Start.lat) < threshold &&
          Math.abs(seg1End.lon - seg2Start.lon) < threshold) ||
        (Math.abs(seg1End.lat - seg2End.lat) < threshold &&
          Math.abs(seg1End.lon - seg2End.lon) < threshold)
      );
    };

    // Improved connection algorithm - COPIED FROM OVERPASSSERVICE
    const visited = new Set<string>();
    const connected: any[] = [];
    const connectorSegments = new Set<string>();
    const primaryTrailIds = new Set<string>();
    const connectedNamedRoutes = new Set<string>(); // Track connected named routes (yellow)

    let initialSegments: any[] = [targetElement];
    if (targetName && sameNameWays.length > 1) {
      initialSegments = sameNameWays;
    }

    // Add all initial segments to visited and connected
    for (const segment of initialSegments) {
      const segId = `${segment.type}_${segment.id}`;
      visited.add(segId);
      connected.push(segment);
      primaryTrailIds.add(segId);
    }

    const queue: any[] = [...initialSegments];
    let processed = 0;
    const maxProcessed = 30;
    const startTime = Date.now();
    const TIMEOUT_MS = 3000;

    while (
      queue.length > 0 &&
      processed < maxProcessed &&
      connected.length < 50
    ) {
      if (processed > 0 && processed % 5 === 0) {
        const elapsed = Date.now() - startTime;
        if (elapsed > TIMEOUT_MS) {
          debug(
            "Search timeout after %.1fs (processed %d, found %d)",
            elapsed / 1000,
            processed,
            connected.length,
          );
          break;
        }
      }

      const current = queue.shift()!;
      processed++;

      if (!current.geometry || current.geometry.length === 0) continue;

      for (const seg of allCycleways) {
        const segId = `${seg.type}_${seg.id}`;
        if (visited.has(segId)) continue;

        if (!areConnected(current, seg)) continue;

        // For same-name segments, add immediately as PRIMARY
        if (seg.tags?.name === targetName) {
          visited.add(segId);
          connected.push(seg);
          primaryTrailIds.add(segId);
          queue.push(seg);
          debug("Added same-name segment: %s (%s)", seg.tags?.name, seg.id);
        }
        // Connected named route (different name) - mark as YELLOW
        else if (seg.tags?.name && processed < 20) {
          visited.add(segId);
          connected.push(seg);
          connectedNamedRoutes.add(segId);
          queue.push(seg);
          debug("Added connected named route: %s", seg.tags.name);
        }
        // Unnamed ways - don't add to connected at all, let them show as gray nearby
        // (overpassService has complex logic to check if they're continuations, we skip that)
      }
    }

    debug(
      "BFS found %d connected segments after %d iterations",
      connected.length,
      processed,
    );

    // IMPORTANT: Add ALL same-name segments as primary, even if disconnected - FROM OVERPASSSERVICE
    if (targetName) {
      const connectedIds = new Set(
        connected.map((c: any) => `${c.type}_${c.id}`),
      );
      for (const element of allCycleways) {
        const elementId = `${element.type}_${element.id}`;
        if (element.tags?.name === targetName && !connectedIds.has(elementId)) {
          debug(
            "Adding disconnected same-name segment: %s (%s)",
            element.tags?.name,
            element.id,
          );
          connected.push(element);
          primaryTrailIds.add(elementId); // Mark as primary since it has the same name
        }
      }
    }

    debug(
      "After adding disconnected same-name segments: %d total segments",
      connected.length,
    );

    // CRITICAL: Get ALL ways within 1 mile of ANY point along connected segments - FROM OVERPASSSERVICE
    const allNearbyWays: any[] = [];
    const nearbyDistance = 1 / 69; // 1 mile in degrees
    const connectedIds2 = new Set(
      connected.map((c: any) => `${c.type}_${c.id}`),
    );

    // Collect endpoints from all connected segments
    const connectedPoints: Array<{ lat: number; lon: number }> = [];
    for (const seg of connected) {
      if (seg.geometry && seg.geometry.length > 0) {
        connectedPoints.push(seg.geometry[0]);
        connectedPoints.push(seg.geometry[seg.geometry.length - 1]);
        if (seg.geometry.length > 20) {
          const midIdx = Math.floor(seg.geometry.length / 2);
          connectedPoints.push(seg.geometry[midIdx]);
        }
      }
    }

    for (const element of allWays) {
      if (element.geometry && element.geometry.length > 0) {
        const elHighway = element.tags?.highway;
        const isRelevantType =
          elHighway === "cycleway" ||
          elHighway === "path" ||
          elHighway === "footway" ||
          elHighway === "track" ||
          elHighway === "pedestrian" ||
          element.tags?.bicycle === "yes" ||
          (isTargetStreet && elHighway === highway);

        if (!isRelevantType) continue;

        // Check endpoints for proximity
        let isNearby = false;
        const checkPoints = [
          element.geometry[0],
          element.geometry[element.geometry.length - 1],
        ];

        outer: for (const checkPoint of checkPoints) {
          for (const connectedPoint of connectedPoints) {
            if (
              Math.abs(checkPoint.lat - connectedPoint.lat) < nearbyDistance &&
              Math.abs(checkPoint.lon - connectedPoint.lon) < nearbyDistance
            ) {
              isNearby = true;
              break outer;
            }
          }
        }

        if (isNearby) {
          const elementId = `${element.type}_${element.id}`;
          const isConnected = connectedIds2.has(elementId);
          const isPrimary = primaryTrailIds.has(elementId);
          const isConnectedRoute = connectedNamedRoutes.has(elementId);

          const wayWithDebugInfo = {
            ...element,
            _debug_connected: isConnected,
            _debug_primary: isPrimary,
            _debug_same_name: element.tags?.name === targetName,
            _debug_is_connector: connectorSegments.has(elementId),
            _debug_connected_route: isConnectedRoute, // For yellow highlighting
          };
          allNearbyWays.push(wayWithDebugInfo);
        }
      }
    }

    debug(
      "Returning %d connected + %d total nearby ways for visualization",
      connected.length,
      allNearbyWays.length,
    );

    // Return all nearby ways with debug info, or at least the connected segments
    if (allNearbyWays.length > 0) {
      return allNearbyWays;
    } else if (connected.length > 0) {
      debug(
        "No nearby ways found, returning %d connected segments",
        connected.length,
      );
      return connected.map((seg: any) => {
        const segId = `${seg.type}_${seg.id}`;
        return {
          ...seg,
          _debug_connected: true,
          _debug_primary: primaryTrailIds.has(segId),
          _debug_same_name: seg.tags?.name === targetName,
          _debug_connected_route: connectedNamedRoutes.has(segId),
        };
      });
    } else {
      return [targetElement];
    }
  }

  /**
   * Display selected feature geometry (trails, parks, etc) on the map
   * @param geometryElements Array of Overpass elements with geometry
   * @param skipFit If true, don't zoom to fit the features (just display them)
   */
  displaySelectedFeature(
    geometryElements: any[],
    overpassService?: any,
    clearPrevious: boolean = true,
    temporary: boolean = false,
    skipFit: boolean = false,
  ) {
    // Clear previous selected features only if requested
    if (clearPrevious) {
      this.selectedFeatureSource.clear();
    }

    debug(
      "Displaying %d geometry elements (clearPrevious=%s, temporary=%s)",
      geometryElements?.length || 0,
      clearPrevious,
      temporary,
    );

    if (!geometryElements || geometryElements.length === 0) {
      debug("No geometry elements to display");
      return;
    }

    // Special handling for administrative boundaries from Nominatim
    debug("Checking for admin boundary: length=%d", geometryElements.length);
    if (geometryElements.length === 1) {
      debug("Single element, tags: %o", geometryElements[0].tags);
      debug("Has bounds? %s", !!geometryElements[0].bounds);
      debug("Has geojson? %s", !!geometryElements[0].geojson);
      debug("_from_nominatim? %s", geometryElements[0].tags?._from_nominatim);
    }

    if (
      geometryElements.length === 1 &&
      geometryElements[0].tags?._from_nominatim === "true" &&
      geometryElements[0].bounds
    ) {
      const element = geometryElements[0];
      const bounds = element.bounds;
      debug("Administrative boundary from Nominatim with bounds: %o", bounds);

      // Create a polygon feature from the bounds or GeoJSON
      const features: Feature[] = [];

      if (element.geojson && element.geojson.coordinates) {
        // Convert GeoJSON to OpenLayers feature
        debug("Creating polygon from GeoJSON geometry");
        const coords = this.convertGeoJSONToOLCoords(element.geojson);
        if (coords) {
          const polygon = new Polygon(coords);
          const feature = new Feature({
            geometry: polygon,
            elementType: "polygon",
            element: element,
          });

          // Set debug properties for styling
          feature.set("_debug_primary", true);
          feature.set("_from_nominatim", true);

          features.push(feature);
          debug("Created polygon feature for boundary");
        }
      } else {
        // Fallback: create a rectangle from bounds
        debug("Creating rectangle from bounds");
        const bottomLeft = fromLonLat([bounds.minlon, bounds.minlat]);
        const bottomRight = fromLonLat([bounds.maxlon, bounds.minlat]);
        const topRight = fromLonLat([bounds.maxlon, bounds.maxlat]);
        const topLeft = fromLonLat([bounds.minlon, bounds.maxlat]);

        const polygon = new Polygon([
          [bottomLeft, bottomRight, topRight, topLeft, bottomLeft],
        ]);

        const feature = new Feature({
          geometry: polygon,
          elementType: "polygon",
          element: element,
        });

        feature.set("_debug_primary", true);
        feature.set("_from_nominatim", true);

        features.push(feature);
        debug("Created rectangle feature for boundary");
      }

      // Add features to the appropriate layer
      if (features.length > 0) {
        const targetSource = temporary
          ? this.temporaryPlaceSource
          : this.selectedFeatureSource;
        targetSource.addFeatures(features);
        debug(
          "Added %d boundary features to %s layer",
          features.length,
          temporary ? "temporary place" : "selected feature",
        );
      }

      // Zoom to the boundary box
      debug("Calling fitBounds with:", {
        minLat: bounds.minlat,
        maxLat: bounds.maxlat,
        minLon: bounds.minlon,
        maxLon: bounds.maxlon,
      });

      // Set mode to free before zooming
      this.setMode("free");
      this.setRotation(0);

      this.fitBounds(
        {
          minLat: bounds.minlat,
          maxLat: bounds.maxlat,
          minLon: bounds.minlon,
          maxLon: bounds.maxlon,
        },
        {
          padding: 100, // More padding for cities
          duration: 1000, // Longer animation
        },
      );

      debug("fitBounds called - zoom should have changed");
      return;
    }

    const features: Feature[] = [];
    const processedWayIds = new Set<number>(); // Track processed ways to avoid duplicates

    // First pass: collect all primary/connected endpoints for distance checking
    const primaryEndpoints: Array<{ lat: number; lon: number }> = [];
    for (const element of geometryElements) {
      if (element._debug_primary || element._debug_connected) {
        if (element.geometry && element.geometry.length > 0) {
          primaryEndpoints.push(element.geometry[0]);
          primaryEndpoints.push(element.geometry[element.geometry.length - 1]);
        } else if (element.type === "relation" && element.members) {
          // Collect endpoints from relation members
          for (const member of element.members) {
            if (member.geometry && member.geometry.length > 0) {
              primaryEndpoints.push(member.geometry[0]);
              primaryEndpoints.push(
                member.geometry[member.geometry.length - 1],
              );
            }
          }
        }
      }
    }

    debug(
      "Collected %d primary/connected endpoints for distance checking",
      primaryEndpoints.length,
    );

    // Second pass: flatten and filter elements
    const allElements: any[] = [];
    const nearbyDistance = 1 / 69; // 1 mile in degrees

    for (const element of geometryElements) {
      if (element.elements && element.elements.length > 0) {
        debug(
          "Unpacking %d sub-elements for %s",
          element.elements.length,
          element.tags?.name,
        );
        allElements.push(...element.elements);
      } else if (element.type === "relation" && element.members) {
        // Handle relations with members
        debug(
          "Processing relation %s with %d members",
          element.id,
          element.members?.length || 0,
        );

        // Check if this is a park/multipolygon relation
        const isParkRelation =
          element.tags?.leisure === "park" ||
          element.tags?.type === "multipolygon";

        for (const member of element.members) {
          if (member.type === "way" && member.geometry) {
            // Check member role - for multipolygons, only include "outer" ways
            const memberRole = (member as any).role || "";

            // For park multipolygons, skip inner ways (holes)
            if (isParkRelation && memberRole === "inner") {
              debug("Skipping inner way (hole) in park multipolygon");
              continue;
            }

            // Always include primary and connected relations
            if (element._debug_primary || element._debug_connected) {
              // Include all members of primary/connected relations
            } else if (primaryEndpoints.length > 0) {
              // For unconnected relations, check if any member is within 1 mile
              let isNearby = false;

              if (member.geometry && member.geometry.length > 0) {
                const wayLat = member.geometry[0].lat;
                const wayLon = member.geometry[0].lon;

                for (const endpoint of primaryEndpoints) {
                  if (
                    Math.abs(wayLat - endpoint.lat) < nearbyDistance &&
                    Math.abs(wayLon - endpoint.lon) < nearbyDistance
                  ) {
                    isNearby = true;
                    break;
                  }
                }
              }

              // Skip this member if it's too far away
              if (!isNearby) {
                continue;
              }
            }

            // Try to look up the way in the cache if we have overpassService
            let wayElement = member;
            // Note: lastSearchResults caching removed with overpassService.ts deletion
            // This was optional optimization, not critical functionality
            /*
            if (overpassService && overpassService.lastSearchResults) {
              const cachedWay = overpassService.lastSearchResults.find(
                (e: any) => e.type === "way" && e.id === member.ref,
              );
              if (cachedWay) {
                debug("Found cached way %s with tags", member.ref);
                // Merge cached data with member geometry
                wayElement = {
                  ...cachedWay,
                  geometry: member.geometry,
                  _debug_connected: element._debug_connected,
                  _debug_primary: element._debug_primary,
                  _debug_same_name: element._debug_same_name,
                  _debug_is_connector: element._debug_is_connector,
                  _debug_connected_route: element._debug_connected_route,
                };
              } else {
                // Create a basic way element from the member
                wayElement = {
                  type: "way",
                  id: member.ref,
                  geometry: member.geometry,
                  tags: {},
                  _debug_connected: element._debug_connected,
                  _debug_primary: element._debug_primary,
                  _debug_same_name: element._debug_same_name,
                  _debug_is_connector: element._debug_is_connector,
                  _debug_connected_route: element._debug_connected_route,
                };
              }
            }
            */

            // Mark park relation ways to be rendered as polygons
            if (isParkRelation) {
              wayElement._is_park_boundary = true;
              wayElement._debug_primary = true; // Ensure park boundaries are primary for zoom
              wayElement.tags = wayElement.tags || {};
              // Force it to be treated as an area
              wayElement.tags.area = "yes";
              debug(
                "Marked way %s as park boundary for polygon rendering",
                wayElement.id,
              );
            }

            allElements.push(wayElement);
          }
        }
      } else {
        allElements.push(element);
      }
    }

    debug("Processing %d total elements after unpacking", allElements.length);

    // Log first few elements to see what we're working with
    allElements.slice(0, 3).forEach((elem) => {
      debug(
        "Element: type=%s, id=%s, name=%s, _debug_primary=%s, _debug_connected=%s",
        elem.type,
        elem.id,
        elem.tags?.name || "unnamed",
        elem._debug_primary,
        elem._debug_connected,
      );
    });

    for (const element of allElements) {
      // Skip duplicate ways
      if (
        element.type === "way" &&
        element.id &&
        typeof element.id === "number"
      ) {
        if (processedWayIds.has(element.id)) {
          debug("Skipping duplicate way %d", element.id);
          continue;
        }
        processedWayIds.add(element.id);
      }

      if (element.type === "node" && element.lat && element.lon) {
        // Single point feature
        debug("Creating point feature at %f,%f", element.lat, element.lon);
        const point = new Point(fromLonLat([element.lon, element.lat]));
        const feature = new Feature({ geometry: point });
        feature.set("elementType", "node");
        features.push(feature);
      } else if (element.type === "way" && element.geometry) {
        // Way with geometry (line or polygon)
        debug("Creating way feature with %d points", element.geometry.length);
        debug("  Way tags: %o", element.tags);
        debug("  _is_park_boundary: %s", element._is_park_boundary);
        debug("  _from_park_relation: %s", element._from_park_relation);

        const coords = element.geometry.map((node: any) =>
          fromLonLat([node.lon, node.lat]),
        );

        // Check if the way is closed (first and last points are the same)
        const firstPoint = coords[0];
        const lastPoint = coords[coords.length - 1];
        const isClosed =
          coords.length > 2 &&
          Math.abs(firstPoint[0] - lastPoint[0]) < 0.0001 &&
          Math.abs(firstPoint[1] - lastPoint[1]) < 0.0001;

        // Debug log
        if (element.tags?.name) {
          debug(
            "Way '%s': %d points, closed=%s, first=[%.6f,%.6f], last=[%.6f,%.6f]",
            element.tags.name,
            coords.length,
            isClosed,
            firstPoint[0],
            firstPoint[1],
            lastPoint[0],
            lastPoint[1],
          );
        }

        // Check if it should be rendered as a polygon (area tags + closed way)
        // Also check if it's from a park relation (marked by overpass service)
        const isPolygon =
          (isClosed &&
            element.tags &&
            (element.tags.area === "yes" ||
              element.tags.leisure === "park" ||
              element.tags.landuse === "forest" ||
              element.tags.natural === "wood" ||
              element.tags.building === "yes")) ||
          element._from_park_relation === true ||
          element._is_park_boundary === true;

        // For park boundaries, we might need to close the way if it's not already
        if (element._is_park_boundary && !isClosed && coords.length > 2) {
          debug("Closing park boundary way for polygon rendering");
          coords.push(coords[0]); // Close the polygon
        }

        if (isPolygon && coords.length > 3) {
          // It's a polygon - already closed
          debug("Rendering as polygon: %s", element.tags?.name || "unnamed");
          const polygon = new Polygon([coords]);
          const feature = new Feature({ geometry: polygon });
          feature.set("elementType", "polygon");
          features.push(feature);
        } else {
          // LineString for paths, trails, cycleways
          // IMPORTANT: Do NOT close the line even if endpoints are near
          debug("Rendering as line: %s", element.tags?.name || "unnamed");

          // Make a copy of coords to avoid modifying original
          let lineCoords = [...coords];

          // Check if the line appears closed but shouldn't be
          if (lineCoords.length > 2) {
            const first = lineCoords[0];
            const last = lineCoords[lineCoords.length - 1];
            const distance = Math.sqrt(
              Math.pow(first[0] - last[0], 2) + Math.pow(first[1] - last[1], 2),
            );

            // If endpoints are very close (likely same point), remove the last point
            // unless it's explicitly marked as an area
            if (distance < 1 && !element.tags?.area) {
              // 1 meter in map units
              debug("Removing closing point - distance: %f", distance);
              lineCoords.pop();
            }
          }

          const lineString = new LineString(lineCoords);
          const feature = new Feature({ geometry: lineString });
          feature.set("elementType", "line");

          // Just store the whole original element!
          feature.set("element", element);

          // Still need these for styling
          if (element._debug_connected !== undefined) {
            feature.set("_debug_connected", element._debug_connected);
          }
          if (element._debug_primary !== undefined) {
            feature.set("_debug_primary", element._debug_primary);
          }
          if (element._debug_same_name !== undefined) {
            feature.set("_debug_same_name", element._debug_same_name);
          }
          if (element._debug_is_connector !== undefined) {
            feature.set("_debug_is_connector", element._debug_is_connector);
          }
          if (element._debug_connected_route !== undefined) {
            feature.set(
              "_debug_connected_route",
              element._debug_connected_route,
            );
          }

          features.push(feature);
        }
      }
    }

    // Sort features so primary trail segments render last (on top)
    features.sort((a, b) => {
      const aPrimary = a.get("_debug_primary") || false;
      const bPrimary = b.get("_debug_primary") || false;
      const aConnected = a.get("_debug_connected") || false;
      const bConnected = b.get("_debug_connected") || false;

      // Order: unconnected (gray) first, then connected (light green), then primary (green) last
      if (!aConnected && bConnected) return -1;
      if (aConnected && !bConnected) return 1;
      if (!aPrimary && bPrimary) return -1;
      if (aPrimary && !bPrimary) return 1;
      return 0;
    });

    // Add all features to the appropriate source
    const targetSource = temporary
      ? this.temporaryPlaceSource
      : this.selectedFeatureSource;
    const targetLayer = temporary
      ? this.temporaryPlaceLayer
      : this.selectedFeatureLayer;
    debug(
      "Adding %d features to %s layer",
      features.length,
      temporary ? "temporary place" : "selected feature",
    );
    targetSource.addFeatures(features);

    // Force layer refresh on mobile devices - timing issue
    // Mobile devices may not render immediately after adding features
    if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
      debug("Mobile device detected - forcing layer refresh");
      targetLayer?.changed();
      // Also force a render frame
      this.map.renderSync();
    }

    // Fit view to show only primary features (not connected or nearby ones)
    const primaryFeatures = features.filter((f) => {
      const isPrimary = f.get("_debug_primary");
      const element = f.get("element");

      // Debug what we're filtering
      if (isPrimary) {
        debug(
          "Primary feature: %s (id: %s)",
          element?.tags?.name || "unnamed",
          element?.id,
        );
      }

      return isPrimary === true;
    });

    const isMobile = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    debug(
      "[%s] Feature breakdown: %d primary, %d total",
      isMobile ? "MOBILE" : "DESKTOP",
      primaryFeatures.length,
      features.length,
    );

    // Extra debug on mobile when no features
    if (isMobile && features.length === 0) {
      debug("CRITICAL [MOBILE]: No features created from elements!");
      debug("allElements count: %d", allElements.length);
      if (geometryElements && geometryElements.length > 0) {
        debug(
          "First element type: %s, has geometry: %s",
          geometryElements[0].type,
          !!geometryElements[0].geometry,
        );
      }
    }

    // Log some sample features to debug
    features.slice(0, 5).forEach((f) => {
      const element = f.get("element");
      debug(
        "Feature: primary=%s, connected=%s, name=%s, id=%s",
        f.get("_debug_primary"),
        f.get("_debug_connected"),
        element?.tags?.name || "unnamed",
        element?.id,
      );
    });

    // Skip fitting if requested
    if (skipFit) {
      debug("Skipping fit - just displaying features");
      return;
    }

    if (primaryFeatures.length > 0) {
      // Create a temporary source to calculate extent of just primary features
      const tempSource = new VectorSource();
      tempSource.addFeatures(primaryFeatures);
      let extent = tempSource.getExtent();

      debug(
        "Fitting view to %d primary features only (ignoring %d other features)",
        primaryFeatures.length,
        features.length - primaryFeatures.length,
      );
      debug("Extent:", extent);
      debug(
        "Extent values: minX=%f, minY=%f, maxX=%f, maxY=%f",
        extent[0],
        extent[1],
        extent[2],
        extent[3],
      );

      // Check if extent is valid
      if (!extent || extent.includes(Infinity) || extent.includes(-Infinity)) {
        debug("ERROR: Invalid extent - contains Infinity!");
        return;
      }

      // Use view.fit() directly - same as what routes use
      debug(
        "🔵 FITTING TO BOUNDS: extent=[%.2f, %.2f, %.2f, %.2f]",
        extent[0],
        extent[1],
        extent[2],
        extent[3],
      );
      debug("🔵 Current zoom: %f", this.view.getZoom());

      // First check if the original extent already fits in current view
      const padding = 100;
      const mapSize = this.map.getSize();
      const effectiveWidth = mapSize ? mapSize[0] - padding * 2 : 0;
      const effectiveHeight = mapSize ? mapSize[1] - padding * 2 : 0;

      const resolution = this.view.getResolution() || 1;
      const originalWidthPx = (extent[2] - extent[0]) / resolution;
      const originalHeightPx = (extent[3] - extent[1]) / resolution;

      // Always fit to extent (allow both zoom in and zoom out)
      // Ensure minimum extent of 0.25 miles before zooming
      const width = extent[2] - extent[0];
      const height = extent[3] - extent[1];
      const minExtentMeters = 0.25 * 1609.34; // 0.25 miles in meters

      if (width < minExtentMeters || height < minExtentMeters) {
        const centerX = (extent[0] + extent[2]) / 2;
        const centerY = (extent[1] + extent[3]) / 2;
        const halfExtent = minExtentMeters / 2;
        extent = [
          centerX - halfExtent,
          centerY - halfExtent,
          centerX + halfExtent,
          centerY + halfExtent,
        ];
        debug(
          "🔵 Expanded extent to minimum 0.25 miles: [%.2f, %.2f, %.2f, %.2f]",
          extent[0],
          extent[1],
          extent[2],
          extent[3],
        );
      }

      debug("🔵 Calling view.fit() with extent and padding");
      this.view.fit(extent, {
        padding: [100, 100, 100, 100],
        duration: 800,
      });

      setTimeout(() => {
        debug("🔵 After fit - new zoom: %f", this.view.getZoom());
      }, 900);
      return;
    } else {
      debug(
        "WARNING: No primary features found! Falling back to all %d features",
        features.length,
      );

      // If no primary features, try to fit to connected features at least
      const connectedFeatures = features.filter(
        (f) => f.get("_debug_connected") === true,
      );

      if (connectedFeatures.length > 0) {
        const tempSource = new VectorSource();
        tempSource.addFeatures(connectedFeatures);
        let extent = tempSource.getExtent();
        debug(
          "Fitting to %d connected features instead",
          connectedFeatures.length,
        );

        // Check if extent already fits before zooming
        const padding = 100;
        const mapSize = this.map.getSize();
        const effectiveWidth = mapSize ? mapSize[0] - padding * 2 : 0;
        const effectiveHeight = mapSize ? mapSize[1] - padding * 2 : 0;
        const resolution = this.view.getResolution() || 1;
        const originalWidthPx = (extent[2] - extent[0]) / resolution;
        const originalHeightPx = (extent[3] - extent[1]) / resolution;

        if (
          originalWidthPx <= effectiveWidth &&
          originalHeightPx <= effectiveHeight
        ) {
          debug("Connected features already fit, just centering");
          const centerX = (extent[0] + extent[2]) / 2;
          const centerY = (extent[1] + extent[3]) / 2;
          this.view.animate({ center: [centerX, centerY], duration: 800 });
        } else {
          // Ensure minimum extent of 0.25 miles
          const width = extent[2] - extent[0];
          const height = extent[3] - extent[1];
          const minExtentMeters = 0.25 * 1609.34;

          if (width < minExtentMeters || height < minExtentMeters) {
            const centerX = (extent[0] + extent[2]) / 2;
            const centerY = (extent[1] + extent[3]) / 2;
            const halfExtent = minExtentMeters / 2;
            extent = [
              centerX - halfExtent,
              centerY - halfExtent,
              centerX + halfExtent,
              centerY + halfExtent,
            ];
          }

          this.view.fit(extent, {
            padding: [100, 100, 100, 100],
            duration: 800,
          });
        }
      } else {
        // Last resort - fit all using the correct source
        const targetSource = temporary
          ? this.temporaryPlaceSource
          : this.selectedFeatureSource;
        let extent = targetSource.getExtent();
        debug(
          "No primary or connected features, fitting to all %d features from %s",
          features.length,
          temporary ? "temporary place source" : "selected feature source",
        );

        // Check if extent already fits before zooming
        const padding = 100;
        const mapSize = this.map.getSize();
        const effectiveWidth = mapSize ? mapSize[0] - padding * 2 : 0;
        const effectiveHeight = mapSize ? mapSize[1] - padding * 2 : 0;
        const resolution = this.view.getResolution() || 1;
        const originalWidthPx = (extent[2] - extent[0]) / resolution;
        const originalHeightPx = (extent[3] - extent[1]) / resolution;

        if (
          originalWidthPx <= effectiveWidth &&
          originalHeightPx <= effectiveHeight
        ) {
          debug("All features already fit, just centering");
          const centerX = (extent[0] + extent[2]) / 2;
          const centerY = (extent[1] + extent[3]) / 2;
          this.view.animate({ center: [centerX, centerY], duration: 800 });
        } else {
          // Ensure minimum extent of 0.25 miles
          const width = extent[2] - extent[0];
          const height = extent[3] - extent[1];
          const minExtentMeters = 0.25 * 1609.34;

          if (width < minExtentMeters || height < minExtentMeters) {
            const centerX = (extent[0] + extent[2]) / 2;
            const centerY = (extent[1] + extent[3]) / 2;
            const halfExtent = minExtentMeters / 2;
            extent = [
              centerX - halfExtent,
              centerY - halfExtent,
              centerX + halfExtent,
              centerY + halfExtent,
            ];
          }

          this.view.fit(extent, {
            padding: [100, 100, 100, 100],
            duration: 800,
          });
        }
      }
    }
  }

  /**
   * Clear selected feature from the map (routes)
   */
  clearSelectedFeature() {
    this.selectedFeatureSource.clear();
  }

  /**
   * Display temporary place overlay (like parks) - won't interfere with routes
   * @param skipFit If true, don't zoom to fit the place (just show it)
   */
  displayTemporaryPlace(
    geometryElements: any[],
    overpassService?: any,
    skipFit: boolean = false,
  ) {
    // Set mode to free and reset rotation for proper zoom
    this.setMode("free");
    this.setRotation(0);

    // Clear previous temporary places, but don't clear routes (selectedFeatureSource)
    this.temporaryPlaceSource.clear();

    // Add to temporary place layer
    this.displaySelectedFeature(
      geometryElements,
      overpassService,
      false,
      true,
      skipFit,
    );
  }

  /**
   * Clear all temporary place overlays
   */
  clearTemporaryPlaces() {
    this.temporaryPlaceSource.clear();
  }

  /**
   * Search cached ways by text query
   * @param query - Text to search for (words can be in any order)
   * @returns Array of matched features sorted by relevance
   *
   * NOTE: Disabled after overpassService.ts removal
   * This method used the old overpassService which has been replaced by overpassWorkerService
   */
  async searchCachedWaysByText(query: string): Promise<any[]> {
    debug("searchCachedWaysByText is disabled - overpassService removed");
    return [];
  }

  /**
   * Search for nearest cached way at the given coordinates
   */
  private async searchNearestCachedWay(
    lat: number,
    lon: number,
    pixel: [number, number],
  ) {
    try {
      const overpassWorker = getOverpassWorkerService();

      // Check if just the center tile is cached
      const GRID_SIZE = 0.2;
      const centerGridLat = Math.round(lat / GRID_SIZE) * GRID_SIZE;
      const centerGridLon = Math.round(lon / GRID_SIZE) * GRID_SIZE;
      const gridStatus = await overpassWorker.checkGridStatus(
        centerGridLat,
        centerGridLon,
      );

      debug(
        "Grid status for %.1f, %.1f: %s",
        centerGridLat,
        centerGridLon,
        gridStatus,
      );

      // If center tile is not cached, show fetching popup and fetch ONLY center tile
      if (gridStatus === "none" || gridStatus === "failed") {
        debug(
          "Center tile not cached, showing fetching message and fetching single tile",
        );
        this.showFetchingPopup(pixel, lat, lon, centerGridLat, centerGridLon);
        // Fetch only the center tile (gridRange: 1 = single tile, onlyCached: false to allow scheduling)
        await overpassWorker.searchLocalBikeFeatures(lat, lon, 1, false);
        return;
      }

      if (gridStatus === "fetching") {
        debug("Center tile already fetching, showing fetching message");
        this.showFetchingPopup(pixel, lat, lon, centerGridLat, centerGridLon);
        return;
      }

      // Center tile is cached, query for elements in just the single tile under the pointer
      const cachedElements = await overpassWorker.searchLocalBikeFeatures(
        lat,
        lon,
        1,
      );

      if (!cachedElements || cachedElements.length === 0) {
        debug("No cached elements found near %.4f, %.4f", lat, lon);
        // Show a simple popup with just Google Maps option
        this.showLocationPopup(pixel, lat, lon);
        return;
      }

      // Count shared lanes for debugging
      const sharedLanes = cachedElements.filter(
        (e) =>
          e.type === "way" &&
          (e.tags?.cycleway ||
            e.tags?.["cycleway:left"] ||
            e.tags?.["cycleway:right"] ||
            e.tags?.["cycleway:both"]),
      );

      debug(
        "Found %d cached elements, %d with cycleway tags",
        cachedElements.length,
        sharedLanes.length,
      );

      // Filter to only ways with geometry (excluding nature_reserve but including shared lanes)
      const ways = cachedElements.filter((e) => {
        if (e.type !== "way" || !e.geometry || e.geometry.length === 0) {
          return false;
        }

        // Exclude nature reserves
        if (e.tags?.leisure === "nature_reserve") {
          return false;
        }

        // Include regular bike infrastructure
        if (e.tags?.highway || e.tags?.leisure || e.tags?.natural) {
          return true;
        }

        // Include any way with bike lane tags
        if (
          e.tags?.cycleway ||
          e.tags?.["cycleway:left"] ||
          e.tags?.["cycleway:right"] ||
          e.tags?.["cycleway:both"]
        ) {
          debug("Including shared lane: %s", e.tags?.name || "unnamed");
          return true;
        }

        // Include streets marked as bike-friendly
        if (e.tags?.bicycle === "yes" || e.tags?.bicycle === "designated") {
          return true;
        }

        return false;
      });

      if (ways.length === 0) {
        debug("No ways found in cached elements");
        return;
      }

      // Find the nearest way segment
      // Check paths/highways first (they take precedence), then check area boundaries
      let nearestWay = null;
      let minDistance = Infinity;
      let parkMatch = null;

      for (const way of ways) {
        if (!way.geometry) continue;

        // Check if this is a closed boundary (park, leisure area, etc.) - NOT highways/paths
        const isClosed =
          way.geometry.length > 2 &&
          Math.abs(
            way.geometry[0].lat - way.geometry[way.geometry.length - 1].lat,
          ) < 0.00001 &&
          Math.abs(
            way.geometry[0].lon - way.geometry[way.geometry.length - 1].lon,
          ) < 0.00001;

        const isAreaFeature =
          !way.tags?.highway &&
          (way.tags?.leisure || way.tags?.natural || way.tags?.landuse);
        const isBoundary = isClosed && isAreaFeature;

        if (isBoundary) {
          // For area boundaries (parks, etc.), check if point is inside - save for later
          const isInside = this.pointInPolygon(lat, lon, way.geometry);
          if (isInside && !parkMatch) {
            parkMatch = way;
          }
          continue;
        }

        // For paths, roads, and other linear features, calculate distance to each segment
        for (let i = 0; i < way.geometry.length - 1; i++) {
          const p1 = way.geometry[i];
          const p2 = way.geometry[i + 1];

          // Calculate distance from point to line segment
          const distance = this.pointToLineDistance(
            lat,
            lon,
            p1.lat,
            p1.lon,
            p2.lat,
            p2.lon,
          );

          if (distance < minDistance) {
            minDistance = distance;
            nearestWay = way;
          }
        }
      }

      // If no path/road was found within range, use park match if available
      // Scale threshold based on zoom level to make trails easier to hit when zoomed out
      // At zoom 15+: 20m, zoom 14: 40m, zoom 13: 80m, zoom 12: 160m, etc.
      const zoom = this.map.getView().getZoom() || 15;
      const baseDistance = 20; // 20 meters at high zoom
      const zoomThreshold = 15; // Start scaling below this zoom
      const maxDistanceMeters =
        zoom >= zoomThreshold
          ? baseDistance
          : baseDistance * Math.pow(2, zoomThreshold - zoom);

      if (!nearestWay || minDistance * 111000 > maxDistanceMeters) {
        if (parkMatch) {
          nearestWay = parkMatch;
          minDistance = 0;
        }
      }

      if (nearestWay) {
        const distanceMeters = minDistance * 111000; // Convert degrees to meters (approximate)
        const name = nearestWay.tags?.name || "Unnamed way";

        debug(
          "Found nearest way: %s (%.2f meters away, max: %.2f)",
          name,
          distanceMeters,
          maxDistanceMeters,
        );

        if (distanceMeters <= maxDistanceMeters) {
          // Show popup with way details
          this.showNearestWayPopup(pixel, nearestWay);
        } else {
          debug(
            "Nearest way is too far (%.2f meters > %.2f meters), showing location popup instead",
            distanceMeters,
            maxDistanceMeters,
          );
          this.showLocationPopup(pixel, lat, lon);
        }
      } else {
        debug("No nearby way found");
        this.showLocationPopup(pixel, lat, lon);
      }
    } catch (err) {
      debug.error("Error searching for nearest cached way:", err);
    }
  }

  /**
   * Show popup for any location with Google Maps option
   */
  private showLocationPopup(pixel: [number, number], lat: number, lon: number) {
    // Remove any existing popups
    const existingLocationMenu = document.getElementById("map-location-popup");
    if (existingLocationMenu) {
      existingLocationMenu.remove();
    }
    const existingWayMenu = document.getElementById("map-nearest-way-popup");
    if (existingWayMenu) {
      existingWayMenu.remove();
    }
    const existingFetchingMenu = document.getElementById("map-fetching-popup");
    if (existingFetchingMenu) {
      existingFetchingMenu.remove();
    }

    // Create popup menu
    const menu = document.createElement("div");
    menu.id = "map-location-popup";
    // Get map position to calculate absolute position
    const mapElement = this.map.getTargetElement();
    const mapBounds = mapElement?.getBoundingClientRect();
    const left = (mapBounds?.left || 0) + pixel[0];
    const top = (mapBounds?.top || 0) + pixel[1];

    menu.style.cssText = `
      position: fixed;
      left: ${left}px;
      top: ${top}px;
      background: rgba(30, 30, 30, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      padding: 8px;
      z-index: 100000;
      min-width: 200px;
      backdrop-filter: blur(10px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    `;

    // Add title
    const title = document.createElement("div");
    title.style.cssText = `
      color: #fff;
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 8px;
      padding-right: 20px;
    `;
    title.innerHTML = `📍 Location`;
    menu.appendChild(title);

    // Add coordinates
    const coords = document.createElement("div");
    coords.style.cssText = `
      color: rgba(255, 255, 255, 0.7);
      font-size: 12px;
      margin-bottom: 8px;
    `;
    coords.innerHTML = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
    menu.appendChild(coords);

    // Add Google Maps button
    const googleMapsBtn = document.createElement("button");
    googleMapsBtn.style.cssText = `
      display: block;
      width: 100%;
      padding: 6px 8px;
      background: rgba(66, 133, 244, 0.2);
      border: 1px solid rgba(66, 133, 244, 0.4);
      border-radius: 4px;
      color: #4285f4;
      font-size: 12px;
      cursor: pointer;
      text-align: left;
    `;
    googleMapsBtn.textContent = "🗺️ Open in Google Maps";
    googleMapsBtn.onclick = () => {
      // Use current zoom level
      const zoom = Math.round(this.view.getZoom() || 17);
      const googleMapsUrl = `https://www.google.com/maps/@${lat},${lon},${zoom}z`;
      debug("Opening Google Maps at: %.6f, %.6f, zoom: %d", lat, lon, zoom);
      window.open(googleMapsUrl, "_blank");
      menu.remove();
    };
    menu.appendChild(googleMapsBtn);

    // Add close button
    const closeBtn = document.createElement("button");
    closeBtn.style.cssText = `
      position: absolute;
      top: 4px;
      right: 4px;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.5);
      font-size: 18px;
      cursor: pointer;
      padding: 4px;
    `;
    closeBtn.innerHTML = "×";
    closeBtn.onclick = () => menu.remove();
    menu.appendChild(closeBtn);

    // Add to document body to ensure it's above all other elements
    document.body.appendChild(menu);

    // Adjust position if menu goes off screen
    const menuRect = menu.getBoundingClientRect();
    const mapRect = this.map.getTargetElement()?.getBoundingClientRect();

    if (mapRect) {
      // Adjust if going off right edge
      if (menuRect.right > mapRect.right) {
        menu.style.left = `${mapRect.left + pixel[0] - menuRect.width}px`;
      }
      // Adjust if going off bottom edge
      if (menuRect.bottom > mapRect.bottom) {
        menu.style.top = `${mapRect.top + pixel[1] - menuRect.height}px`;
      }
    }

    // Store reference to check if this popup is from long press
    (menu as any).__isLongPressPopup = true;

    // Close menu on next REAL map click (not the long press release)
    const mapClickHandler = () => {
      // Check if we should ignore this click because it's from the long press that created this popup
      if ((menu as any).__isLongPressPopup && this.isLongPressClick) {
        debug(
          "Ignoring map click on location popup - this is the release click",
        );
        return;
      }

      menu.remove();
      this.map.un("click", mapClickHandler);
      document.removeEventListener("click", docClickHandler);
    };

    // Add handler immediately - we'll check inside if we should ignore
    this.map.on("click", mapClickHandler);

    // Also close if clicking outside the map entirely
    const docClickHandler = (e: MouseEvent) => {
      const mapElement = this.map.getTargetElement();
      if (
        !menu.contains(e.target as Node) &&
        !mapElement?.contains(e.target as Node)
      ) {
        menu.remove();
        this.map.un("click", mapClickHandler);
        document.removeEventListener("click", docClickHandler);
      }
    };
    document.addEventListener("click", docClickHandler);
  }

  /**
   * Show fetching popup when tile needs to be loaded
   */
  private showFetchingPopup(
    pixel: [number, number],
    lat: number,
    lon: number,
    targetGridLat?: number,
    targetGridLon?: number,
  ) {
    // Remove any existing popups
    const existingFetchingMenu = document.getElementById("map-fetching-popup");
    if (existingFetchingMenu) {
      existingFetchingMenu.remove();
    }
    const existingWayMenu = document.getElementById("map-nearest-way-popup");
    if (existingWayMenu) {
      existingWayMenu.remove();
    }
    const existingLocationMenu = document.getElementById("map-location-popup");
    if (existingLocationMenu) {
      existingLocationMenu.remove();
    }

    // Create popup menu
    const menu = document.createElement("div");
    menu.id = "map-fetching-popup";
    // Get map position to calculate absolute position
    const mapElement = this.map.getTargetElement();
    const mapBounds = mapElement?.getBoundingClientRect();
    const left = (mapBounds?.left || 0) + pixel[0];
    const top = (mapBounds?.top || 0) + pixel[1];

    menu.style.cssText = `
      position: fixed;
      left: ${left}px;
      top: ${top}px;
      background: rgba(30, 30, 30, 0.95);
      border: 1px solid rgba(255, 193, 7, 0.4);
      border-radius: 8px;
      padding: 12px;
      z-index: 100000;
      min-width: 200px;
      backdrop-filter: blur(10px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    `;

    // Add title with spinner
    const title = document.createElement("div");
    title.style.cssText = `
      color: #ffc107;
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 8px;
      padding-right: 20px;
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    // Add spinner
    const spinner = document.createElement("div");
    spinner.style.cssText = `
      width: 16px;
      height: 16px;
      border: 2px solid rgba(255, 193, 7, 0.3);
      border-top-color: #ffc107;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    `;
    title.appendChild(spinner);

    const titleText = document.createElement("span");
    titleText.textContent = "Fetching tile data...";
    title.appendChild(titleText);

    menu.appendChild(title);

    // Add info message
    const info = document.createElement("div");
    info.style.cssText = `
      color: rgba(255, 255, 255, 0.7);
      font-size: 12px;
      line-height: 1.4;
    `;
    info.innerHTML = `Downloading bike infrastructure data for this area. This will only happen once.`;
    menu.appendChild(info);

    // Add close button
    const closeBtn = document.createElement("button");
    closeBtn.style.cssText = `
      position: absolute;
      top: 4px;
      right: 4px;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.5);
      font-size: 18px;
      cursor: pointer;
      padding: 4px;
    `;
    closeBtn.innerHTML = "×";
    closeBtn.onclick = () => menu.remove();
    menu.appendChild(closeBtn);

    // Add spinner animation style if not already added
    if (!document.getElementById("spinner-style")) {
      const style = document.createElement("style");
      style.id = "spinner-style";
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }

    // Add to document body to ensure it's above all other elements
    document.body.appendChild(menu);

    // Adjust position if menu goes off screen
    const menuRect = menu.getBoundingClientRect();
    const mapRect = this.map.getTargetElement()?.getBoundingClientRect();

    if (mapRect) {
      // Adjust if going off right edge
      if (menuRect.right > mapRect.right) {
        menu.style.left = `${mapRect.left + pixel[0] - menuRect.width}px`;
      }
      // Adjust if going off bottom edge
      if (menuRect.bottom > mapRect.bottom) {
        menu.style.top = `${mapRect.top + pixel[1] - menuRect.height}px`;
      }
    }

    // Listen for cache update event to show results - but ONLY for the specific tile we're waiting for
    const cacheUpdateHandler = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const eventData = customEvent.detail;

      // If we have a target tile, only respond to that specific tile being cached
      if (targetGridLat !== undefined && targetGridLon !== undefined) {
        const eventGridLat = eventData?.gridLat;
        const eventGridLon = eventData?.gridLon;

        if (eventGridLat !== targetGridLat || eventGridLon !== targetGridLon) {
          debug(
            "Ignoring cache update for %.1f, %.1f (waiting for %.1f, %.1f)",
            eventGridLat,
            eventGridLon,
            targetGridLat,
            targetGridLon,
          );
          return;
        }
      }

      debug("Cache updated for our tile at %.4f, %.4f", lat, lon);

      // Remove the fetching popup
      menu.remove();

      // Clean up listener
      window.removeEventListener("cacheUpdated", cacheUpdateHandler);

      // Check if there's an active search - if so, let the search service handle re-querying
      const searchService = getSearchService();
      if (searchService.hasActiveSearch()) {
        debug(
          "Active search detected - letting search service handle re-query",
        );
        return;
      }

      // No active search - show results from cached tile only (don't schedule more fetches)
      const overpassWorker = getOverpassWorkerService();
      const cachedElements = await overpassWorker.searchLocalBikeFeatures(
        lat,
        lon,
        1,
        true,
      );

      if (!cachedElements || cachedElements.length === 0) {
        debug("No elements found in cached tile at %.4f, %.4f", lat, lon);
        this.showLocationPopup(pixel, lat, lon);
        return;
      }

      // Find nearest way and show popup
      const ways = cachedElements.filter((e: any) => {
        if (e.type !== "way" || !e.geometry || e.geometry.length === 0)
          return false;
        if (e.tags?.leisure === "nature_reserve") return false;
        return (
          e.tags?.highway ||
          e.tags?.leisure ||
          e.tags?.natural ||
          e.tags?.cycleway ||
          e.tags?.["cycleway:left"] ||
          e.tags?.["cycleway:right"] ||
          e.tags?.["cycleway:both"] ||
          e.tags?.bicycle === "yes" ||
          e.tags?.bicycle === "designated"
        );
      });

      if (ways.length === 0) {
        debug("No ways found in cached elements");
        this.showLocationPopup(pixel, lat, lon);
        return;
      }

      let nearestWay = null;
      let minDistance = Infinity;

      for (const way of ways) {
        if (!way.geometry) continue;
        for (let i = 0; i < way.geometry.length - 1; i++) {
          const p1 = way.geometry[i];
          const p2 = way.geometry[i + 1];
          const distance = this.pointToLineDistance(
            lat,
            lon,
            p1.lat,
            p1.lon,
            p2.lat,
            p2.lon,
          );
          if (distance < minDistance) {
            minDistance = distance;
            nearestWay = way;
          }
        }
      }

      if (nearestWay) {
        const distanceMeters = minDistance * 111000; // Convert degrees to meters (approximate)
        const maxDistanceMeters = 100; // Only show if within 100 meters

        debug(
          "Found nearest way: %s (%.2f meters away)",
          nearestWay.tags?.name || "unnamed",
          distanceMeters,
        );

        if (distanceMeters <= maxDistanceMeters) {
          this.showNearestWayPopup(pixel, nearestWay);
        } else {
          debug(
            "Nearest way is too far (%.2f meters), showing location popup instead",
            distanceMeters,
          );
          this.showLocationPopup(pixel, lat, lon);
        }
      } else {
        this.showLocationPopup(pixel, lat, lon);
      }
    };

    window.addEventListener("cacheUpdated", cacheUpdateHandler);

    // Store cleanup function on menu for later removal
    (menu as any).__cleanupHandler = () => {
      window.removeEventListener("cacheUpdated", cacheUpdateHandler);
    };

    // Cleanup on manual close
    closeBtn.onclick = () => {
      (menu as any).__cleanupHandler?.();
      menu.remove();
    };
  }

  /**
   * Calculate distance from point to line segment
   */
  private pointToLineDistance(
    px: number,
    py: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ): number {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;

    if (lenSq !== 0) {
      param = dot / lenSq;
    }

    let xx, yy;

    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    const dx = px - xx;
    const dy = py - yy;

    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Check if a point is inside a polygon using ray casting algorithm
   */
  private pointInPolygon(
    lat: number,
    lon: number,
    polygon: Array<{ lat: number; lon: number }>,
  ): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].lon,
        yi = polygon[i].lat;
      const xj = polygon[j].lon,
        yj = polygon[j].lat;

      const intersect =
        yi > lat !== yj > lat &&
        lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }

  /**
   * Show popup for nearest way found by long press
   */
  private showNearestWayPopup(pixel: [number, number], way: any) {
    const name = way.tags?.name || "Unnamed way";
    const tags = way.tags || {};

    // Remove any existing popups
    const existingWayMenu = document.getElementById("map-nearest-way-popup");
    if (existingWayMenu) {
      existingWayMenu.remove();
    }
    const existingLocationMenu = document.getElementById("map-location-popup");
    if (existingLocationMenu) {
      existingLocationMenu.remove();
    }
    const existingFetchingMenu = document.getElementById("map-fetching-popup");
    if (existingFetchingMenu) {
      existingFetchingMenu.remove();
    }

    // Create popup menu
    const menu = document.createElement("div");
    menu.id = "map-nearest-way-popup";
    // Get map position to calculate absolute position
    const mapElement = this.map.getTargetElement();
    const mapBounds = mapElement?.getBoundingClientRect();
    const left = (mapBounds?.left || 0) + pixel[0];
    const top = (mapBounds?.top || 0) + pixel[1];

    menu.style.cssText = `
      position: fixed;
      left: ${left}px;
      top: ${top}px;
      background: rgba(30, 30, 30, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      padding: 8px;
      z-index: 100000;
      min-width: 200px;
      max-width: 300px;
      backdrop-filter: blur(10px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    `;

    // Add title with search icon
    const title = document.createElement("div");
    title.style.cssText = `
      color: #fff;
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 8px;
      padding-right: 20px;
    `;
    title.innerHTML = `🔍 ${name}`;
    menu.appendChild(title);

    // Add details
    const details = document.createElement("div");
    details.style.cssText = `
      color: rgba(255, 255, 255, 0.7);
      font-size: 12px;
      line-height: 1.4;
    `;

    let detailsHtml =
      '<div style="opacity: 0.7; margin-bottom: 4px;">Found by location search</div>';

    // Type info
    if (tags.highway) {
      detailsHtml += `<div>Type: ${tags.highway.replace(/_/g, " ")}</div>`;
    } else if (tags.leisure) {
      detailsHtml += `<div>Type: ${tags.leisure.replace(/_/g, " ")}</div>`;
    } else if (tags.natural) {
      detailsHtml += `<div>Type: ${tags.natural.replace(/_/g, " ")}</div>`;
    }

    // Surface info
    if (tags.surface) {
      detailsHtml += `<div>Surface: ${tags.surface.replace(/_/g, " ")}</div>`;
    }

    // Width info
    if (tags.width) {
      detailsHtml += `<div>Width: ${tags.width}</div>`;
    }

    // Bicycle access
    if (tags.bicycle) {
      detailsHtml += `<div>Bicycle: ${tags.bicycle}</div>`;
    }

    // OSM ID for reference
    detailsHtml += `<div style="margin-top: 4px; opacity: 0.5;">OSM: ${way.type}/${way.id}</div>`;

    details.innerHTML = detailsHtml;
    menu.appendChild(details);

    // Add action button to select and show
    const actions = document.createElement("div");
    actions.style.cssText = `
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    `;

    // Check if this is a route or a place
    // On long press, show more highway types as routes (even if filtered from search)
    const isRoute =
      tags.highway === "cycleway" ||
      tags.highway === "path" ||
      tags.highway === "footway" ||
      tags.highway === "steps" ||
      tags.highway === "tertiary" ||
      tags.highway === "secondary" ||
      tags.highway === "primary" ||
      tags.highway === "residential" ||
      tags.highway === "service" ||
      tags.highway === "track" ||
      tags.route === "bicycle" ||
      tags.bicycle === "designated" ||
      name.toLowerCase().includes("trail") ||
      name.toLowerCase().includes("veloway") ||
      name.toLowerCase().includes("road") ||
      name.toLowerCase().includes("street") ||
      name.toLowerCase().includes("avenue") ||
      name.toLowerCase().includes("boulevard");

    const selectBtn = document.createElement("button");
    selectBtn.style.cssText = `
      display: block;
      width: 100%;
      padding: 6px 8px;
      margin-bottom: 4px;
      background: rgba(255, 193, 7, 0.2);
      border: 1px solid rgba(255, 193, 7, 0.4);
      border-radius: 4px;
      color: #ffc107;
      font-size: 12px;
      cursor: pointer;
      text-align: left;
    `;

    if (isRoute) {
      // Show route icon and "Activate Route"
      selectBtn.innerHTML = `
        <svg width="16" height="14" viewBox="0 0 24 20" fill="none" style="vertical-align: middle; margin-right: 4px;">
          <path
            d="M 2,14 C 6,14 6,6 10,6 C 14,6 14,14 18,14 C 22,14 22,6 22,6"
            stroke="#60a5fa"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            fill="none"
          />
        </svg>
        <span>Activate Route</span>
      `;
      selectBtn.style.display = "flex";
      selectBtn.style.alignItems = "center";
      selectBtn.onclick = async () => {
        debug("Activating route from nearest way popup");
        const coords =
          way.geometry && way.geometry.length > 0
            ? [
                way.geometry[Math.floor(way.geometry.length / 2)].lon,
                way.geometry[Math.floor(way.geometry.length / 2)].lat,
              ]
            : [0, 0];

        const fakeFeature = new Feature({
          geometry: new Point(fromLonLat(coords)),
        });
        fakeFeature.set("place_id", `osm_${way.type}_${way.id}`);
        fakeFeature.set("name", name);
        fakeFeature.set("osm_type", way.type);
        fakeFeature.set("osm_id", way.id);
        fakeFeature.set("tags", tags);

        menu.remove();
        await this.handleSearchResultClick(fakeFeature);
      };

      // Add Zoom to Fit button for routes too
      const zoomBtn = document.createElement("button");
      zoomBtn.style.cssText = `
        display: flex;
        width: 100%;
        padding: 6px 8px;
        margin-bottom: 4px;
        background: rgba(66, 133, 244, 0.2);
        border: 1px solid rgba(66, 133, 244, 0.4);
        border-radius: 4px;
        color: #4285f4;
        font-size: 12px;
        cursor: pointer;
        text-align: left;
        align-items: center;
      `;
      zoomBtn.innerHTML = `
        <span style="font-size: 16px; margin-right: 4px;">🔍</span>
        <span>Zoom to Fit</span>
      `;
      zoomBtn.onclick = async () => {
        debug("Zooming to fit route from nearest way popup");
        const coords =
          way.geometry && way.geometry.length > 0
            ? [
                way.geometry[Math.floor(way.geometry.length / 2)].lon,
                way.geometry[Math.floor(way.geometry.length / 2)].lat,
              ]
            : [0, 0];

        const fakeFeature = new Feature({
          geometry: new Point(fromLonLat(coords)),
        });
        fakeFeature.set("place_id", `osm_${way.type}_${way.id}`);
        fakeFeature.set("name", name);
        fakeFeature.set("osm_type", way.type);
        fakeFeature.set("osm_id", way.id);
        fakeFeature.set("tags", tags);

        await this.handlePlaceClick(fakeFeature, false);
      };
      actions.appendChild(zoomBtn);

      // Track if popup is still open
      let popupOpen = true;

      // Show route bounds immediately when popup opens (without zoom)
      (async () => {
        const coords =
          way.geometry && way.geometry.length > 0
            ? [
                way.geometry[Math.floor(way.geometry.length / 2)].lon,
                way.geometry[Math.floor(way.geometry.length / 2)].lat,
              ]
            : [0, 0];

        const fakeFeature = new Feature({
          geometry: new Point(fromLonLat(coords)),
        });
        fakeFeature.set("place_id", `osm_${way.type}_${way.id}`);
        fakeFeature.set("name", name);
        fakeFeature.set("osm_type", way.type);
        fakeFeature.set("osm_id", way.id);
        fakeFeature.set("tags", tags);

        await this.handlePlaceClick(fakeFeature, true); // skipFit = true

        // Only display if popup is still open
        if (!popupOpen) {
          this.clearTemporaryPlaces();
          debug("Popup closed before geometry loaded, clearing display");
        }
      })();

      // Mark popup as closed when it's removed
      const originalRemove = menu.remove.bind(menu);
      (menu as any).remove = () => {
        popupOpen = false;
        this.clearTemporaryPlaces();
        originalRemove();
      };
    } else {
      // Show pin icon and "Zoom to Fit"
      selectBtn.innerHTML = `
        <span style="font-size: 16px; margin-right: 4px;">🔍</span>
        <span>Zoom to Fit</span>
      `;
      selectBtn.style.display = "flex";
      selectBtn.style.alignItems = "center";
      selectBtn.onclick = async () => {
        debug("Zooming to fit place from nearest way popup");
        const coords =
          way.geometry && way.geometry.length > 0
            ? [
                way.geometry[Math.floor(way.geometry.length / 2)].lon,
                way.geometry[Math.floor(way.geometry.length / 2)].lat,
              ]
            : [0, 0];

        const fakeFeature = new Feature({
          geometry: new Point(fromLonLat(coords)),
        });
        fakeFeature.set("place_id", `osm_${way.type}_${way.id}`);
        fakeFeature.set("name", name);
        fakeFeature.set("osm_type", way.type);
        fakeFeature.set("osm_id", way.id);
        fakeFeature.set("tags", tags);

        // Don't close popup - just zoom to fit
        await this.handlePlaceClick(fakeFeature, false);
      };

      // Track if popup is still open
      let popupOpen = true;

      // Show place bounds immediately when popup opens (without zoom)
      (async () => {
        const coords =
          way.geometry && way.geometry.length > 0
            ? [
                way.geometry[Math.floor(way.geometry.length / 2)].lon,
                way.geometry[Math.floor(way.geometry.length / 2)].lat,
              ]
            : [0, 0];

        const fakeFeature = new Feature({
          geometry: new Point(fromLonLat(coords)),
        });
        fakeFeature.set("place_id", `osm_${way.type}_${way.id}`);
        fakeFeature.set("name", name);
        fakeFeature.set("osm_type", way.type);
        fakeFeature.set("osm_id", way.id);
        fakeFeature.set("tags", tags);

        await this.handlePlaceClick(fakeFeature, true); // skipFit = true

        // Only display if popup is still open
        if (!popupOpen) {
          this.clearTemporaryPlaces();
          debug("Popup closed before geometry loaded, clearing display");
        }
      })();

      // Mark popup as closed when it's removed
      const originalRemove = menu.remove.bind(menu);
      (menu as any).remove = () => {
        popupOpen = false;
        this.clearTemporaryPlaces();
        originalRemove();
      };
    }
    actions.appendChild(selectBtn);

    // Add Google Maps button
    const googleMapsBtn = document.createElement("button");
    googleMapsBtn.style.cssText = `
      display: block;
      width: 100%;
      padding: 6px 8px;
      margin-bottom: 4px;
      background: rgba(66, 133, 244, 0.2);
      border: 1px solid rgba(66, 133, 244, 0.4);
      border-radius: 4px;
      color: #4285f4;
      font-size: 12px;
      cursor: pointer;
      text-align: left;
    `;
    googleMapsBtn.textContent = "🗺️ Open in Google Maps";
    googleMapsBtn.onclick = () => {
      // Get the location of the way
      let lat = 0,
        lon = 0;
      if (way.geometry && way.geometry.length > 0) {
        const midPoint = Math.floor(way.geometry.length / 2);
        lat = way.geometry[midPoint].lat;
        lon = way.geometry[midPoint].lon;
      }

      // Create Google Maps URL - use current zoom level
      const zoom = Math.round(this.view.getZoom() || 17);
      const googleMapsUrl = `https://www.google.com/maps/@${lat},${lon},${zoom}z`;

      debug("Opening Google Maps at: %.6f, %.6f, zoom: %d", lat, lon, zoom);

      // Open in new tab
      window.open(googleMapsUrl, "_blank");
      menu.remove();
    };
    actions.appendChild(googleMapsBtn);

    menu.appendChild(actions);

    // Add close button
    const closeBtn = document.createElement("button");
    closeBtn.style.cssText = `
      position: absolute;
      top: 4px;
      right: 4px;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.5);
      font-size: 18px;
      cursor: pointer;
      padding: 4px;
    `;
    closeBtn.innerHTML = "×";
    closeBtn.onclick = () => {
      menu.remove();
    };
    menu.appendChild(closeBtn);

    // Add to document body to ensure it's above all other elements
    document.body.appendChild(menu);

    // Adjust position if menu goes off screen
    const menuRect = menu.getBoundingClientRect();
    const mapRect = this.map.getTargetElement()?.getBoundingClientRect();

    if (mapRect) {
      // Adjust if going off right edge
      if (menuRect.right > mapRect.right) {
        menu.style.left = `${mapRect.left + pixel[0] - menuRect.width}px`;
      }
      // Adjust if going off bottom edge
      if (menuRect.bottom > mapRect.bottom) {
        menu.style.top = `${mapRect.top + pixel[1] - menuRect.height}px`;
      }
    }

    // Store reference to check if this popup is from long press
    (menu as any).__isLongPressPopup = true;

    // Store cleanup function to clear place/route overlay when menu is removed
    const originalRemove = menu.remove.bind(menu);
    menu.remove = () => {
      this.clearTemporaryPlaces();
      originalRemove();
    };

    // Close menu on next REAL map click (not the long press release)
    const mapClickHandler = () => {
      // Check if we should ignore this click because it's from the long press that created this popup
      if ((menu as any).__isLongPressPopup && this.isLongPressClick) {
        debug(
          "Ignoring map click on long press popup - this is the release click",
        );
        return;
      }

      menu.remove();
      this.map.un("click", mapClickHandler);
      document.removeEventListener("click", docClickHandler);
    };

    // Add handler immediately - we'll check inside if we should ignore
    this.map.on("click", mapClickHandler);

    // Also close if clicking outside the map entirely
    const docClickHandler = (e: MouseEvent) => {
      const mapElement = this.map.getTargetElement();
      if (
        !menu.contains(e.target as Node) &&
        !mapElement?.contains(e.target as Node)
      ) {
        menu.remove();
        document.removeEventListener("click", docClickHandler);
        this.map.un("click", mapClickHandler);
      }
    };
    document.addEventListener("click", docClickHandler);
  }

  /**
   * Show popup for a trail segment (selected feature)
   */
  private showTrailSegmentPopup(pixel: [number, number], feature: Feature) {
    const element = feature.get("element");
    if (!element) return;

    const name = element.tags?.name || "Unnamed trail";
    const tags = element.tags || {};

    // Remove any existing popup
    const existingMenu = document.getElementById("map-trail-popup");
    if (existingMenu) {
      existingMenu.remove();
    }

    // Create popup menu (same style as search result popup)
    const menu = document.createElement("div");
    menu.id = "map-trail-popup";
    // Get map position to calculate absolute position
    const mapElement = this.map.getTargetElement();
    const mapBounds = mapElement?.getBoundingClientRect();
    const left = (mapBounds?.left || 0) + pixel[0];
    const top = (mapBounds?.top || 0) + pixel[1];

    menu.style.cssText = `
      position: fixed;
      left: ${left}px;
      top: ${top}px;
      background: rgba(30, 30, 30, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      padding: 8px;
      z-index: 100000;
      min-width: 200px;
      max-width: 300px;
      backdrop-filter: blur(10px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    `;

    // Add title
    const title = document.createElement("div");
    title.style.cssText = `
      color: #fff;
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 8px;
      padding-right: 20px;
    `;
    title.textContent = name;
    menu.appendChild(title);

    // Add details
    const details = document.createElement("div");
    details.style.cssText = `
      color: rgba(255, 255, 255, 0.7);
      font-size: 12px;
      line-height: 1.4;
    `;

    let detailsHtml = "";

    // Type info
    if (tags.highway) {
      detailsHtml += `<div>Type: ${tags.highway.replace(/_/g, " ")}</div>`;
    }

    // Surface info
    if (tags.surface) {
      detailsHtml += `<div>Surface: ${tags.surface.replace(/_/g, " ")}</div>`;
    }

    // Width info
    if (tags.width) {
      detailsHtml += `<div>Width: ${tags.width}</div>`;
    }

    // MTB scale
    if (tags["mtb:scale"]) {
      detailsHtml += `<div>MTB Scale: ${tags["mtb:scale"]}</div>`;
    }

    // Bicycle access
    if (tags.bicycle) {
      detailsHtml += `<div>Bicycle: ${tags.bicycle}</div>`;
    }

    // Connection status (from debug info)
    if (element._debug_connected === true) {
      detailsHtml += `<div style="color: #4ade80;">✓ Connected segment</div>`;
    } else if (element._debug_primary === true) {
      detailsHtml += `<div style="color: #22c55e;">★ Primary segment</div>`;
    }

    // OSM ID for reference
    detailsHtml += `<div style="margin-top: 4px; opacity: 0.5;">OSM: ${element.type}/${element.id}</div>`;

    details.innerHTML = detailsHtml;
    menu.appendChild(details);

    // Add action buttons (if not the primary segment)
    const isPrimary = element._debug_primary === true;
    if (!isPrimary) {
      const actions = document.createElement("div");
      actions.style.cssText = `
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
      `;

      const selectBtn = document.createElement("button");
      selectBtn.style.cssText = `
        display: block;
        width: 100%;
        padding: 6px 8px;
        margin-bottom: 4px;
        background: rgba(255, 193, 7, 0.2);
        border: 1px solid rgba(255, 193, 7, 0.4);
        border-radius: 4px;
        color: #ffc107;
        font-size: 12px;
        cursor: pointer;
        text-align: left;
      `;
      // Create SVG icon for route
      selectBtn.innerHTML = `
      <svg width="16" height="14" viewBox="0 0 24 20" fill="none" style="vertical-align: middle; margin-right: 4px;">
        <path
          d="M 2,14 C 6,14 6,6 10,6 C 14,6 14,14 18,14 C 22,14 22,6 22,6"
          stroke="#60a5fa"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          fill="none"
        />
      </svg>
      <span>Activate Route</span>
    `;
      selectBtn.style.display = "flex";
      selectBtn.style.alignItems = "center";
      selectBtn.onclick = async () => {
        debug(
          "Selecting segment from trail popup: %s (id: %s)",
          name,
          element.id,
        );

        // Create a fake search result feature to pass to handleSearchResultClick
        // Use the actual coordinates if available
        let coords = [0, 0];
        if (element.geometry && element.geometry.length > 0) {
          const midPoint = Math.floor(element.geometry.length / 2);
          coords = [
            element.geometry[midPoint].lon,
            element.geometry[midPoint].lat,
          ];
        }

        const fakeFeature = new Feature({
          geometry: new Point(fromLonLat(coords)),
        });
        fakeFeature.set("place_id", `osm_${element.type}_${element.id}`);
        fakeFeature.set("name", name);
        fakeFeature.set("osm_type", element.type);
        fakeFeature.set("osm_id", element.id);
        fakeFeature.set("tags", tags);

        await this.handleSearchResultClick(fakeFeature);
        menu.remove();
      };
      actions.appendChild(selectBtn);

      menu.appendChild(actions);
    } else {
      // Show "Active Route" button for primary segment
      const actions = document.createElement("div");
      actions.style.cssText = `
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
      `;

      const activeBtn = document.createElement("button");
      activeBtn.style.cssText = `
        display: block;
        width: 100%;
        padding: 6px 8px;
        margin-bottom: 4px;
        background: rgba(34, 197, 94, 0.2);
        border: 1px solid rgba(34, 197, 94, 0.4);
        border-radius: 4px;
        color: #22c55e;
        font-size: 12px;
        cursor: default;
        text-align: left;
      `;
      activeBtn.textContent = "✓ Active Route";
      activeBtn.disabled = true;
      actions.appendChild(activeBtn);

      menu.appendChild(actions);
    }

    // Add close button
    const closeBtn = document.createElement("button");
    closeBtn.style.cssText = `
      position: absolute;
      top: 4px;
      right: 4px;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.5);
      font-size: 18px;
      cursor: pointer;
      padding: 4px;
    `;
    closeBtn.innerHTML = "×";
    closeBtn.onclick = () => menu.remove();
    menu.appendChild(closeBtn);

    // Add to document body to ensure it's above all other elements
    document.body.appendChild(menu);

    // Adjust position if menu goes off screen
    const menuRect = menu.getBoundingClientRect();
    const mapRect = this.map.getTargetElement()?.getBoundingClientRect();

    if (mapRect) {
      // Adjust if going off right edge
      if (menuRect.right > mapRect.right) {
        menu.style.left = `${mapRect.left + pixel[0] - menuRect.width}px`;
      }
      // Adjust if going off bottom edge
      if (menuRect.bottom > mapRect.bottom) {
        menu.style.top = `${mapRect.top + pixel[1] - menuRect.height}px`;
      }
    }

    // Close menu when clicking elsewhere
    const closeOnClick = (e: MouseEvent) => {
      if (!menu.contains(e.target as Node)) {
        menu.remove();
        document.removeEventListener("click", closeOnClick);
      }
    };
    setTimeout(() => {
      document.addEventListener("click", closeOnClick);
    }, 100);
  }

  /**
   * Show context menu for a search result
   */
  private showSearchResultContextMenu(
    pixel: [number, number],
    feature: Feature,
  ) {
    const name = feature.get("name") || "Unknown";
    const tags = feature.get("tags") || {};
    const osmType = feature.get("osm_type");
    const placeType = feature.get("type");

    debug(
      "showSearchResultContextMenu - name=%s, tags=%o, osm_type=%s, type=%s",
      name,
      tags,
      osmType,
      placeType,
    );

    // Remove any existing context menu
    const existingMenu = document.getElementById("map-search-context-menu");
    if (existingMenu) {
      existingMenu.remove();
    }

    // Create context menu
    const menu = document.createElement("div");
    menu.id = "map-search-context-menu";
    // Get map position to calculate absolute position
    const mapElement = this.map.getTargetElement();
    const mapBounds = mapElement?.getBoundingClientRect();
    const left = (mapBounds?.left || 0) + pixel[0];
    const top = (mapBounds?.top || 0) + pixel[1];

    menu.style.cssText = `
      position: fixed;
      left: ${left}px;
      top: ${top}px;
      background: rgba(30, 30, 30, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      padding: 8px;
      z-index: 100000;
      min-width: 200px;
      max-width: 300px;
      backdrop-filter: blur(10px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    `;

    // Add title
    const title = document.createElement("div");
    title.style.cssText = `
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 8px;
      color: #fff;
      padding: 4px 8px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    `;
    title.textContent = name;
    menu.appendChild(title);

    // Add details
    const details = document.createElement("div");
    details.style.cssText = `
      font-size: 12px;
      color: rgba(255, 255, 255, 0.7);
      padding: 4px 8px;
    `;

    const detailItems: string[] = [];

    if (tags.highway) detailItems.push(`Type: ${tags.highway}`);
    if (tags.surface) detailItems.push(`Surface: ${tags.surface}`);
    if (tags.bicycle) detailItems.push(`Bicycle: ${tags.bicycle}`);
    if (tags.oneway) detailItems.push(`One-way: ${tags.oneway}`);
    if (tags.lit) detailItems.push(`Lit: ${tags.lit}`);
    if (tags.width) detailItems.push(`Width: ${tags.width}`);
    if (tags.operator) detailItems.push(`Operator: ${tags.operator}`);

    details.innerHTML = detailItems.join("<br>") || "No additional details";
    menu.appendChild(details);

    // Add actions
    const actions = document.createElement("div");
    actions.style.cssText = `
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    `;

    // Check if this is a route (path/trail/cycleway) or a place
    const isRoute =
      placeType === "cycleway" ||
      placeType === "path" ||
      tags.highway === "cycleway" ||
      tags.highway === "path" ||
      tags.highway === "footway" ||
      tags.highway === "steps" ||
      tags.highway === "tertiary" ||
      tags.highway === "secondary" ||
      tags.highway === "primary" ||
      tags.highway === "residential" ||
      tags.highway === "service" ||
      tags.highway === "track" ||
      tags.route === "bicycle" ||
      tags.bicycle === "designated" ||
      name.toLowerCase().includes("trail") ||
      name.toLowerCase().includes("veloway") ||
      name.toLowerCase().includes("road") ||
      name.toLowerCase().includes("street") ||
      name.toLowerCase().includes("avenue") ||
      name.toLowerCase().includes("boulevard");

    debug(
      "Search result context menu - isRoute=%s, placeType=%s, tags=%o, name=%s",
      isRoute,
      placeType,
      tags,
      name,
    );

    const selectBtn = document.createElement("button");
    selectBtn.style.cssText = `
      display: block;
      width: 100%;
      padding: 6px 8px;
      margin-bottom: 4px;
      background: rgba(255, 193, 7, 0.2);
      border: 1px solid rgba(255, 193, 7, 0.4);
      border-radius: 4px;
      color: #ffc107;
      font-size: 12px;
      cursor: pointer;
      text-align: left;
    `;

    if (isRoute) {
      // Show route icon and "Activate Route"
      selectBtn.innerHTML = `
        <svg width="16" height="14" viewBox="0 0 24 20" fill="none" style="vertical-align: middle; margin-right: 4px;">
          <path
            d="M 2,14 C 6,14 6,6 10,6 C 14,6 14,14 18,14 C 22,14 22,6 22,6"
            stroke="#60a5fa"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            fill="none"
          />
        </svg>
        <span>Activate Route</span>
      `;
      selectBtn.style.display = "flex";
      selectBtn.style.alignItems = "center";
      selectBtn.onclick = async () => {
        debug("Activating route from context menu");
        menu.remove();
        await this.handleSearchResultClick(feature);
      };

      // Add Zoom to Fit button for routes too
      const zoomBtn = document.createElement("button");
      zoomBtn.style.cssText = `
        display: block;
        width: 100%;
        padding: 6px 8px;
        margin-bottom: 4px;
        background: rgba(66, 133, 244, 0.2);
        border: 1px solid rgba(66, 133, 244, 0.4);
        border-radius: 4px;
        color: #4285f4;
        font-size: 12px;
        cursor: pointer;
        text-align: left;
      `;
      zoomBtn.innerHTML = `<span style="font-size: 16px; margin-right: 4px;">🔍</span><span>Zoom to Fit</span>`;
      zoomBtn.style.display = "flex";
      zoomBtn.style.alignItems = "center";
      zoomBtn.onclick = async () => {
        debug("Zooming to fit route from context menu");
        await this.handlePlaceClick(feature, false);
      };
      actions.appendChild(zoomBtn);
    } else {
      // Show pin icon and "Zoom to Fit"
      selectBtn.innerHTML = `
        <span style="font-size: 16px; margin-right: 4px;">🔍</span>
        <span>Zoom to Fit</span>
      `;
      selectBtn.style.display = "flex";
      selectBtn.style.alignItems = "center";
      selectBtn.onclick = async () => {
        debug("Zooming to fit place from context menu");
        // Don't close popup - just zoom to fit
        await this.handlePlaceClick(feature, false);
      };
    }
    actions.appendChild(selectBtn);

    // Add Google Maps button
    const googleMapsBtn = document.createElement("button");
    googleMapsBtn.style.cssText = `
      display: block;
      width: 100%;
      padding: 6px 8px;
      margin-bottom: 4px;
      background: rgba(66, 133, 244, 0.2);
      border: 1px solid rgba(66, 133, 244, 0.4);
      border-radius: 4px;
      color: #4285f4;
      font-size: 12px;
      cursor: pointer;
      text-align: left;
    `;
    googleMapsBtn.textContent = "🗺️ Open in Google Maps";
    googleMapsBtn.onclick = () => {
      // Get the location from the feature
      const geometry = feature.getGeometry();
      let lat = 0,
        lon = 0;
      if (geometry && geometry.getType() === "Point") {
        const coords = (geometry as any).getCoordinates();
        const lonLat = toLonLat(coords);
        lon = lonLat[0];
        lat = lonLat[1];
      }

      // Use current zoom level
      const zoom = Math.round(this.view.getZoom() || 17);
      const googleMapsUrl = `https://www.google.com/maps/@${lat},${lon},${zoom}z`;
      debug("Opening Google Maps at: %.6f, %.6f, zoom: %d", lat, lon, zoom);
      window.open(googleMapsUrl, "_blank");
      menu.remove();
    };
    actions.appendChild(googleMapsBtn);

    menu.appendChild(actions);

    // Add close button
    const closeBtn = document.createElement("button");
    closeBtn.style.cssText = `
      position: absolute;
      top: 4px;
      right: 4px;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.5);
      font-size: 18px;
      cursor: pointer;
      padding: 4px;
    `;
    closeBtn.innerHTML = "×";
    closeBtn.onclick = () => {
      menu.remove();
    };
    menu.appendChild(closeBtn);

    // Add to document body to ensure it's above all other elements
    document.body.appendChild(menu);

    // Adjust position if menu goes off screen
    const menuRect = menu.getBoundingClientRect();
    const mapRect = this.map.getTargetElement()?.getBoundingClientRect();

    if (mapRect) {
      // Adjust if going off right edge
      if (menuRect.right > mapRect.right) {
        menu.style.left = `${mapRect.left + pixel[0] - menuRect.width}px`;
      }
      // Adjust if going off bottom edge
      if (menuRect.bottom > mapRect.bottom) {
        menu.style.top = `${mapRect.top + pixel[1] - menuRect.height}px`;
      }
    }

    // Store cleanup function to clear place/route overlay when menu is removed
    const originalRemove = menu.remove.bind(menu);
    menu.remove = () => {
      this.clearTemporaryPlaces();
      originalRemove();
    };

    // Show place/route bounds immediately when popup opens (without zoom)
    (async () => {
      await this.handlePlaceClick(feature, true); // skipFit = true
    })();

    // Close menu when clicking elsewhere
    const closeOnClick = (e: MouseEvent) => {
      if (!menu.contains(e.target as Node)) {
        menu.remove();
        document.removeEventListener("click", closeOnClick);
      }
    };

    // Delay to prevent immediate closing
    setTimeout(() => {
      document.addEventListener("click", closeOnClick);
    }, 100);
  }

  /**
   * Show context menu for saved route markers
   */
  private showSavedRouteContextMenu(pixel: [number, number], feature: Feature) {
    const name = feature.get("name") || "Saved Route";
    const routeId = feature.get("routeId");

    // Remove any existing context menu
    const existingMenu = document.getElementById("map-saved-route-menu");
    if (existingMenu) {
      existingMenu.remove();
    }

    // Create context menu
    const menu = document.createElement("div");
    menu.id = "map-saved-route-menu";
    const mapElement = this.map.getTargetElement();
    const mapBounds = mapElement?.getBoundingClientRect();
    const left = (mapBounds?.left || 0) + pixel[0];
    const top = (mapBounds?.top || 0) + pixel[1];

    menu.style.cssText = `
      position: fixed;
      left: ${left}px;
      top: ${top}px;
      background: rgba(30, 30, 30, 0.95);
      border: 1px solid rgba(255, 193, 7, 0.4);
      border-radius: 8px;
      padding: 8px;
      z-index: 100000;
      min-width: 200px;
      max-width: 300px;
      backdrop-filter: blur(10px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    `;

    // Add title with star icon
    const title = document.createElement("div");
    title.style.cssText = `
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 8px;
      color: #fbbf24;
      padding: 4px 8px;
      border-bottom: 1px solid rgba(251, 191, 36, 0.2);
      display: flex;
      align-items: center;
      gap: 6px;
    `;
    title.innerHTML = `<span>⭐</span><span>${name}</span>`;
    menu.appendChild(title);

    // Add actions
    const actions = document.createElement("div");
    actions.style.cssText = `
      margin-top: 8px;
    `;

    const activateBtn = document.createElement("button");
    activateBtn.style.cssText = `
      display: flex;
      align-items: center;
      width: 100%;
      padding: 6px 8px;
      margin-bottom: 4px;
      background: rgba(255, 193, 7, 0.2);
      border: 1px solid rgba(255, 193, 7, 0.4);
      border-radius: 4px;
      color: #ffc107;
      font-size: 12px;
      cursor: pointer;
      text-align: left;
    `;
    activateBtn.innerHTML = `
      <svg width="16" height="14" viewBox="0 0 24 20" fill="none" style="vertical-align: middle; margin-right: 4px;">
        <path
          d="M 2,14 C 6,14 6,6 10,6 C 14,6 14,14 18,14 C 22,14 22,6 22,6"
          stroke="#60a5fa"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          fill="none"
        />
      </svg>
      <span>Activate Route</span>
    `;
    activateBtn.onclick = async () => {
      menu.remove();
      await this.handleSavedRouteClick(routeId);
    };
    actions.appendChild(activateBtn);

    // Add Google Maps button
    const googleMapsBtn = document.createElement("button");
    googleMapsBtn.style.cssText = `
      display: block;
      width: 100%;
      padding: 6px 8px;
      margin-bottom: 4px;
      background: rgba(66, 133, 244, 0.2);
      border: 1px solid rgba(66, 133, 244, 0.4);
      border-radius: 4px;
      color: #4285f4;
      font-size: 12px;
      cursor: pointer;
      text-align: left;
    `;
    googleMapsBtn.textContent = "🗺️ Open in Google Maps";
    googleMapsBtn.onclick = () => {
      // Get the location from the feature
      const geometry = feature.getGeometry();
      let lat = 0,
        lon = 0;
      if (geometry && geometry.getType() === "Point") {
        const coords = (geometry as any).getCoordinates();
        const lonLat = toLonLat(coords);
        lon = lonLat[0];
        lat = lonLat[1];
      }

      // Use current zoom level
      const zoom = Math.round(this.view.getZoom() || 17);
      const googleMapsUrl = `https://www.google.com/maps/@${lat},${lon},${zoom}z`;
      debug("Opening Google Maps at: %.6f, %.6f, zoom: %d", lat, lon, zoom);
      window.open(googleMapsUrl, "_blank");
      menu.remove();
    };
    actions.appendChild(googleMapsBtn);

    menu.appendChild(actions);

    // Add close button
    const closeBtn = document.createElement("button");
    closeBtn.style.cssText = `
      position: absolute;
      top: 4px;
      right: 4px;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.5);
      font-size: 18px;
      cursor: pointer;
      padding: 4px;
    `;
    closeBtn.innerHTML = "×";
    closeBtn.onclick = () => menu.remove();
    menu.appendChild(closeBtn);

    // Add to document body
    document.body.appendChild(menu);

    // Adjust position if menu goes off screen
    const menuRect = menu.getBoundingClientRect();
    const mapRect = this.map.getTargetElement()?.getBoundingClientRect();

    if (mapRect) {
      if (menuRect.right > mapRect.right) {
        menu.style.left = `${mapRect.left + pixel[0] - menuRect.width}px`;
      }
      if (menuRect.bottom > mapRect.bottom) {
        menu.style.top = `${mapRect.top + pixel[1] - menuRect.height}px`;
      }
    }

    // Close menu when clicking elsewhere
    const closeOnClick = (e: MouseEvent) => {
      if (!menu.contains(e.target as Node)) {
        menu.remove();
        document.removeEventListener("click", closeOnClick);
      }
    };

    setTimeout(() => {
      document.addEventListener("click", closeOnClick);
    }, 100);
  }

  /**
   * Handle click on a saved route marker - activates the route
   */
  private async handleSavedRouteClick(routeId: string) {
    debug("Activating saved route: %s", routeId);

    const { getRouteService } = await import("./routeService");
    const routeService = getRouteService();

    const savedRoutes = routeService.getSavedRoutes();
    const route = savedRoutes.find((r) => r.id === routeId);

    if (!route) {
      debug.error("Saved route not found: %s", routeId);
      return;
    }

    // Pan to the route center
    const coords = fromLonLat([route.centerLon || 0, route.centerLat || 0]);
    this.view.animate({
      center: coords,
      duration: 800,
    });

    // Display the route geometry
    if (route.geometry && route.geometry.length > 0) {
      const { getOverpassWorkerService } = await import(
        "./overpassWorkerService"
      );
      const overpassWorker = getOverpassWorkerService();

      this.clearSearchResults();
      this.setMode("free");
      this.setRotation(0);
      this.displaySelectedFeature(route.geometry, overpassWorker);
    }

    // Activate the route in routeService
    routeService.startRoute({
      routeName: route.name,
      routeId: route.id,
      totalDistance: route.totalDistance || 0,
      geometry: route.geometry,
      centerLat: route.centerLat,
      centerLon: route.centerLon,
    });

    debug("Activated saved route: %s", route.name);
  }

  /**
   * Handle click on a search result point
   */
  private async handleSearchResultClick(feature: Feature) {
    const placeId = feature.get("place_id");
    const name = feature.get("name");

    debug(
      "!!! handleSearchResultClick called - this should only happen from 'Activate Route' button!",
    );
    debug("Handling search result click: %s (place_id: %s)", name, placeId);

    // Extract osmType and osmId from place_id (for local results) or from direct properties (for Nominatim)
    let osmType: string | undefined;
    let osmId: string | undefined;

    const placeIdStr = String(placeId);
    if (placeIdStr.startsWith("osm_")) {
      // Local search results have OSM info in place_id like "osm_relation_15054153"
      const parts = placeIdStr.split("_");
      osmType = parts[1]; // node, way, or relation
      osmId = parts.slice(2).join("_"); // Join in case ID has underscores
    } else {
      // Nominatim results have separate fields
      osmType = feature.get("osm_type");
      osmId = String(feature.get("osm_id"));
    }

    debug(
      "Extracted osmType=%s, osmId=%s from placeId=%s",
      osmType,
      osmId,
      placeId,
    );

    // Get coordinates
    const geometry = feature.getGeometry();
    if (!(geometry instanceof Point)) {
      debug("ERROR: Geometry is not a Point");
      return;
    }

    const coords = geometry.getCoordinates();
    const lonLat = toLonLat(coords);
    const lat = lonLat[1];
    const lon = lonLat[0];

    // Dispatch event to trigger search busy state in React
    debug("Dispatching searchProcessingStart event");
    window.dispatchEvent(new CustomEvent("searchProcessingStart"));

    // Force a microtask to allow React to process the event
    await Promise.resolve();

    // Small delay to allow UI to update and spinner to render
    await new Promise((resolve) => setTimeout(resolve, 150));

    try {
      // Get cached data from worker - this includes member ways for relations
      // Only need the single tile (gridRange: 1) since we already found the feature at this location
      debug(
        "🔴 MAP MARKER CLICK: Fetching cached data at lat=%f, lon=%f",
        lat,
        lon,
      );
      const overpassWorker = getOverpassWorkerService();
      const cachedData = await overpassWorker.searchLocalBikeFeatures(
        lat,
        lon,
        1,
        true,
      );
      debug(
        "🔴 MAP MARKER CLICK: Got %d cached elements from worker",
        cachedData.length,
      );

      // Find the target element in cache by ID
      debug("Looking for element: osmType=%s, osmId=%s", osmType, osmId);
      debug("Cache has %d elements", cachedData.length);

      const targetElement = cachedData.find((e: any) => {
        const matches =
          e.id?.toString() === osmId ||
          e.id?.toString() === String(osmId) ||
          `osm_${e.type}_${e.id}` === placeId;
        return matches && e.type === osmType;
      });

      if (!targetElement) {
        debug("ERROR: Target element not found in cache!");
        debug("Searched for: type=%s, id=%s", osmType, osmId);
        // Log sample of what IS in the cache
        const sampleRelations = cachedData
          .filter((e: any) => e.type === "relation")
          .slice(0, 5);
        debug(
          "Sample relations in cache: %o",
          sampleRelations.map((r: any) => ({ id: r.id, name: r.tags?.name })),
        );
        throw new Error("Element not found in cache");
      }

      debug(
        "Found target element: type=%s, id=%s, name=%s, hasMembersArray=%s, membersCount=%d",
        targetElement.type,
        targetElement.id,
        targetElement.tags?.name,
        !!targetElement.members,
        targetElement.members?.length || 0,
      );

      // For relations, resolve member ways and find connections
      let displayElements: any[] = [];

      if (targetElement.type === "relation" && targetElement.members) {
        debug("Relation has %d members", targetElement.members.length);
        const memberWayIds = new Set<number>();
        for (const member of targetElement.members) {
          if (member.type === "way") {
            memberWayIds.add(
              typeof member.ref === "number" ? member.ref : Number(member.ref),
            );
          }
        }

        const memberWays = cachedData.filter(
          (e: any) =>
            e.type === "way" &&
            e.id !== undefined &&
            memberWayIds.has(Number(e.id)),
        );

        debug(
          "Found %d/%d member ways in cache",
          memberWays.length,
          memberWayIds.size,
        );

        if (memberWays.length > 0) {
          // For relations, use ALL member ways as initial segments and find connections
          // This matches the overpassService behavior
          displayElements = this.findConnectedSegmentsForRelation(
            memberWays,
            cachedData,
            targetElement.tags?.name,
          );
          debug(
            "Relation member ways + connected: %d total segments",
            displayElements.length,
          );
        } else {
          displayElements = [targetElement];
        }
      } else if (targetElement.type === "way") {
        // For ways, find connected segments
        displayElements = this.findConnectedSegments(targetElement, cachedData);
        debug("Found %d connected segments for way", displayElements.length);
      } else {
        displayElements = [targetElement];
      }

      if (displayElements.length > 0) {
        debug("Displaying %d geometry elements on map", displayElements.length);

        // Clear search results since we're now showing the selected feature
        this.clearSearchResults();

        // Set mode to free and rotate to north BEFORE displaying
        // so the map is ready for the fit operation
        this.setMode("free");
        this.setRotation(0);

        // Display the feature on the map - this will handle zooming
        this.displaySelectedFeature(displayElements, overpassWorker);

        debug(
          "displaySelectedFeature completed - should have zoomed to feature",
        );

        // Calculate total distance for the route
        const calculateDistance = (
          lat1: number,
          lon1: number,
          lat2: number,
          lon2: number,
        ): number => {
          const R = 3959; // Earth radius in miles
          const dLat = ((lat2 - lat1) * Math.PI) / 180;
          const dLon = ((lon2 - lon1) * Math.PI) / 180;
          const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((lat1 * Math.PI) / 180) *
              Math.cos((lat2 * Math.PI) / 180) *
              Math.sin(dLon / 2) *
              Math.sin(dLon / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          return R * c;
        };

        const totalDistance = displayElements.reduce(
          (sum: number, elem: any) => {
            if (elem.geometry?.coordinates) {
              const coords = elem.geometry.coordinates;
              let dist = 0;
              for (let i = 1; i < coords.length; i++) {
                const [lon1, lat1] = coords[i - 1];
                const [lon2, lat2] = coords[i];
                dist += calculateDistance(lat1, lon1, lat2, lon2) * 1609.34; // Convert miles to meters
              }
              return sum + dist;
            }
            return sum;
          },
          0,
        );

        // Activate the route
        const { getRouteService } = await import("./routeService");
        const routeService = getRouteService();
        routeService.startRoute({
          routeName: name || "Unknown Route",
          routeId: String(placeId),
          totalDistance: totalDistance,
          geometry: displayElements,
          centerLat: lat,
          centerLon: lon,
        });
        debug(
          "Started route tracking: %s, %.2f meters at %.4f, %.4f",
          name,
          totalDistance,
          lat,
          lon,
        );
      } else {
        debug("No geometry found for feature");

        // At least center on the location
        this.setMode("free");
        this.setRotation(0);

        // Create a 1-mile box around the point
        const mileInDegrees = 1 / 69;
        this.fitBounds({
          minLat: lat - mileInDegrees,
          maxLat: lat + mileInDegrees,
          minLon: lon - mileInDegrees,
          maxLon: lon + mileInDegrees,
        });
      }
    } catch (err) {
      debug.error("🔴 MAP MARKER: Failed to fetch feature geometry:", err);
      debug.error(
        "🔴 MAP MARKER: Error stack:",
        err instanceof Error ? err.stack : "no stack",
      );
      debug.error(
        "🔴 MAP MARKER: Error message:",
        err instanceof Error ? err.message : String(err),
      );
    } finally {
      // Dispatch event to clear search busy state
      debug("Dispatching searchProcessingEnd event");
      window.dispatchEvent(new CustomEvent("searchProcessingEnd"));
    }
  }

  /**
   * Handle click on a place (non-route feature) - show temporary overlay
   * @param skipFit If true, don't zoom to fit (just show bounds)
   */
  private async handlePlaceClick(feature: Feature, skipFit: boolean = true) {
    const placeId = feature.get("place_id");
    const name = feature.get("name");

    debug("handlePlaceClick called for: %s (place_id: %s)", name, placeId);

    // Extract osmType and osmId
    let osmType: string | undefined;
    let osmId: string | undefined;

    const placeIdStr = String(placeId);
    if (placeIdStr.startsWith("osm_")) {
      const parts = placeIdStr.split("_");
      osmType = parts[1];
      osmId = parts.slice(2).join("_");
    } else {
      osmType = feature.get("osm_type");
      osmId = String(feature.get("osm_id"));
    }

    // Get coordinates
    const geometry = feature.getGeometry();
    if (!(geometry instanceof Point)) {
      debug("ERROR: Geometry is not a Point");
      return;
    }

    const coords = geometry.getCoordinates();
    const lonLat = toLonLat(coords);
    const lat = lonLat[1];
    const lon = lonLat[0];

    try {
      const overpassWorker = getOverpassWorkerService();
      const cachedData = await overpassWorker.searchLocalBikeFeatures(
        lat,
        lon,
        3,
        true,
      );

      const targetElement = cachedData.find((e: any) => {
        return (
          (e.id?.toString() === osmId || e.id?.toString() === String(osmId)) &&
          e.type === osmType
        );
      });

      if (!targetElement) {
        debug("Target element not found in cache");
        return;
      }

      // Process geometry
      let displayElements: any[] = [];
      if (targetElement.type === "relation" && targetElement.members) {
        const memberWayIds = new Set<number>();
        for (const member of targetElement.members) {
          if (member.type === "way") {
            memberWayIds.add(
              typeof member.ref === "number" ? member.ref : Number(member.ref),
            );
          }
        }
        const memberWays = cachedData.filter(
          (e: any) =>
            e.type === "way" &&
            e.id !== undefined &&
            memberWayIds.has(Number(e.id)),
        );
        if (memberWays.length > 0) {
          displayElements = this.findConnectedSegmentsForRelation(
            memberWays,
            cachedData,
            targetElement.tags?.name,
          );
        } else {
          displayElements = [targetElement];
        }
      } else if (targetElement.type === "way") {
        displayElements = this.findConnectedSegments(targetElement, cachedData);
      } else {
        displayElements = [targetElement];
      }

      // Display as temporary place
      if (displayElements.length > 0) {
        this.displayTemporaryPlace(displayElements, overpassWorker, skipFit);
        debug("Displayed place overlay (skipFit=%s): %s", skipFit, name);
      }
    } catch (err) {
      debug("Error showing place:", err);
    }
  }

  /**
   * Display search results as yellow points on the map
   */
  displaySearchResults(results: any[]) {
    // Always clear ALL previous search results first
    const existingFeatures = this.searchResultsSource.getFeatures();
    debug("Clearing %d existing search markers", existingFeatures.length);

    // Remove features one by one to ensure they're gone
    existingFeatures.forEach((feature) => {
      this.searchResultsSource.removeFeature(feature);
    });

    // Also call clear for good measure
    this.searchResultsSource.clear();

    if (!results || results.length === 0) {
      debug("No search results to display");
      return;
    }

    const features: Feature[] = [];

    for (const result of results) {
      if (result.lat && result.lon) {
        const coords = fromLonLat([
          parseFloat(result.lon),
          parseFloat(result.lat),
        ]);
        const feature = new Feature({
          geometry: new Point(coords),
          name: result.display_name,
          place_id: result.place_id,
          osm_type: result.osm_type,
          osm_id: result.osm_id,
          type: result.type,
          tags: result.tags,
        });
        features.push(feature);
      }
    }

    debug("Adding %d search result points to map", features.length);
    this.searchResultsSource.addFeatures(features);
  }

  /**
   * Clear search results from the map
   */
  clearSearchResults() {
    const features = this.searchResultsSource.getFeatures();
    debug("clearSearchResults called - clearing %d markers", features.length);

    // Remove features one by one to ensure they're gone
    features.forEach((feature) => {
      this.searchResultsSource.removeFeature(feature);
    });

    // Also call clear for good measure
    this.searchResultsSource.clear();
  }

  /**
   * Display saved routes as clickable markers on the map
   */
  displaySavedRoutes(routes: any[]) {
    // Clear previous markers
    const existingFeatures = this.savedRoutesSource.getFeatures();
    existingFeatures.forEach((feature) => {
      this.savedRoutesSource.removeFeature(feature);
    });
    this.savedRoutesSource.clear();

    if (!routes || routes.length === 0) {
      debug("No saved routes to display");
      return;
    }

    const features: Feature[] = [];

    for (const route of routes) {
      if (route.centerLat && route.centerLon) {
        const coords = fromLonLat([route.centerLon, route.centerLat]);
        const feature = new Feature({
          geometry: new Point(coords),
          name: route.name,
          routeId: route.id,
          type: "saved_route",
        });
        features.push(feature);
      }
    }

    this.savedRoutesSource.addFeatures(features);
    debug("Displayed %d saved route markers on map", features.length);
  }

  /**
   * Get style for search result points with icons based on type
   */
  private getSearchResultStyle(feature: Feature): Style {
    const type = feature.get("type") || "";
    const tags = feature.get("tags") || {};
    const name = feature.get("name") || "";

    // Get current zoom level (resolution)
    const resolution = this.view.getResolution() || 20;
    // Show labels only when zoomed in (resolution < 10 means fairly zoomed in)
    const showLabel = resolution < 10;

    // Determine icon based on type - matching QuickIslandSearchDetail.tsx logic
    let icon = "📍"; // Default pin
    let bgColor = "#ffc107"; // Default yellow

    // Check for special trails first (like The Veloway)
    if (
      type === "cycleway" ||
      type === "path" ||
      tags.route === "bicycle" ||
      tags.bicycle === "designated" ||
      name.toLowerCase().includes("veloway") ||
      name.toLowerCase().includes("trail")
    ) {
      icon = "🚴";
      bgColor = "#f97316"; // Orange
    }
    // Simple icon mapping based on result type string
    else if (type.includes("restaurant") || type.includes("food")) {
      icon = "🍴";
      bgColor = "#ef4444"; // Red
    } else if (type.includes("cafe") || type.includes("coffee")) {
      icon = "☕";
      bgColor = "#8b5cf6"; // Purple
    } else if (type.includes("park") || type.includes("garden")) {
      icon = "🌳";
      bgColor = "#22c55e"; // Green
    } else if (type.includes("shop") || type.includes("store")) {
      icon = "🛍️";
      bgColor = "#3b82f6"; // Blue
    } else if (type.includes("hotel") || type.includes("lodging")) {
      icon = "🏨";
      bgColor = "#ec4899"; // Pink
    } else if (type.includes("hospital") || type.includes("medical")) {
      icon = "🏥";
      bgColor = "#dc2626"; // Dark red
    } else if (type.includes("school") || type.includes("university")) {
      icon = "🎓";
      bgColor = "#0ea5e9"; // Sky blue
    } else if (type.includes("station") || type.includes("transit")) {
      icon = "🚉";
      bgColor = "#6366f1"; // Indigo
    } else if (type.includes("parking")) {
      icon = "🅿️";
      bgColor = "#64748b"; // Slate
    } else if (type.includes("bank") || type.includes("atm")) {
      icon = "🏦";
      bgColor = "#059669"; // Emerald
    }

    // Create style with conditional text label
    const style: any = {
      image: new Icon({
        src:
          "data:image/svg+xml;utf8," +
          encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
              <!-- Pin shape -->
              <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 24 16 24s16-12 16-24c0-8.8-7.2-16-16-16z"
                    fill="${bgColor}" stroke="#fff" stroke-width="2"/>
              <!-- White circle for icon background -->
              <circle cx="16" cy="14" r="10" fill="white" opacity="0.95"/>
              <!-- Icon text -->
              <text x="16" y="19" font-size="14" text-anchor="middle" fill="${bgColor}">${icon}</text>
            </svg>`,
          ),
        anchor: [0.5, 1], // Anchor at bottom center of pin
        scale: 0.8,
      }),
    };

    // Only add text label when zoomed in
    if (showLabel) {
      const name = feature.get("name")?.split(",")[0] || "";

      // Split text into two lines if it's too long
      let line1 = "";
      let line2 = "";

      if (name.length > 20) {
        // Try to split at a word boundary near the middle
        const words = name.split(" ");
        const midPoint = Math.floor(words.length / 2);

        if (words.length > 1) {
          line1 = words.slice(0, midPoint).join(" ");
          line2 = words.slice(midPoint).join(" ");

          // Apply ellipsis if lines are still too long
          if (line1.length > 15) line1 = line1.substring(0, 14) + "…";
          if (line2.length > 15) line2 = line2.substring(0, 14) + "…";
        } else {
          // Single long word - just truncate
          line1 = name.substring(0, 14) + "…";
        }
      } else {
        line1 = name;
      }

      // Combine lines with newline (OpenLayers supports \n for multi-line)
      const displayText = line2 ? `${line1}\n${line2}` : line1;

      style.text = new Text({
        text: displayText,
        offsetY: line2 ? 12 : 8, // Adjust offset for 2 lines
        font: "11px Inter, system-ui, sans-serif",
        fill: new Fill({
          color: "#000",
        }),
        stroke: new Stroke({
          color: "#fff",
          width: 3,
        }),
        textAlign: "center",
        textBaseline: "top",
      });
    }

    return new Style(style);
  }

  /**
   * Get style for saved route markers - gold star pin
   */
  private getSavedRouteStyle(feature: Feature): Style {
    const name = feature.get("name") || "Route";

    // Get current zoom level (resolution)
    const resolution = this.view.getResolution() || 20;
    const showLabel = resolution < 10;

    // Gold star marker for saved routes
    const style: any = {
      image: new Icon({
        src:
          "data:image/svg+xml;utf8," +
          encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
              <!-- Pin shape -->
              <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 24 16 24s16-12 16-24c0-8.8-7.2-16-16-16z"
                    fill="#fbbf24" stroke="#fff" stroke-width="2"/>
              <!-- White circle for icon background -->
              <circle cx="16" cy="14" r="10" fill="white" opacity="0.95"/>
              <!-- Star icon -->
              <text x="16" y="19" font-size="14" text-anchor="middle" fill="#fbbf24">⭐</text>
            </svg>`,
          ),
        anchor: [0.5, 1],
        scale: 0.8,
      }),
    };

    // Add label when zoomed in
    if (showLabel) {
      style.text = new Text({
        text: name.length > 20 ? name.substring(0, 18) + "…" : name,
        offsetY: 8,
        font: "11px Inter, system-ui, sans-serif",
        fill: new Fill({
          color: "#000",
        }),
        stroke: new Stroke({
          color: "#fff",
          width: 3,
        }),
        textAlign: "center",
        textBaseline: "top",
      });
    }

    return new Style(style);
  }

  /**
   * Get style for selected features
   */
  private getSelectedFeatureStyle(feature: Feature): Style | Style[] {
    const elementType = feature.get("elementType");
    const debugConnected = feature.get("_debug_connected");
    const debugPrimary = feature.get("_debug_primary");
    const debugSameName = feature.get("_debug_same_name");
    const debugConnectedRoute = feature.get("_debug_connected_route");

    if (elementType === "polygon") {
      // Polygon style with green fill and white border
      return new Style({
        fill: new Fill({
          color: "rgba(34, 197, 94, 0.2)", // Semi-transparent green
        }),
        stroke: new Stroke({
          color: "#ffffff",
          width: 3,
          lineDash: [10, 5], // Dashed white border
        }),
      });
    } else if (elementType === "line") {
      // Get route settings from preferences
      const routeOpacity = this.routeSettings.lineOpacity ?? 0.8;
      const configuredRouteWidth = this.routeSettings.lineWidth || 4;
      const routeWidth = this.getMaxLineWidthForZoom(configuredRouteWidth);
      const routeColor = this.routeSettings.lineColor || "#22c55e";
      const showOutline = this.routeSettings.showOutline ?? true;
      const outlineColorSetting = this.routeSettings.outlineColor || "auto";

      // Choose color based on debug flags
      let lineColor = routeColor; // Default to route color from settings
      let lineWidth = routeWidth;

      // IMPORTANT: Handle undefined values - default to showing as connected/primary
      if (debugConnected === false) {
        // Gray for non-connected ways within 2 miles - use 50% opacity or less if route opacity is less
        const nearbyOpacity = Math.min(0.5, routeOpacity);
        lineColor = `rgba(170, 170, 170, ${nearbyOpacity})`;
        lineWidth = routeWidth * 0.33;
      } else if (debugPrimary === true || debugPrimary === undefined) {
        // Primary trail (same name as selected) - use route settings
        // TREAT UNDEFINED AS PRIMARY TO ENSURE RENDERING
        // Convert route color to rgba with opacity
        const rgbaColor = this.hexToRgba(routeColor, routeOpacity);
        lineColor = rgbaColor;
        lineWidth = routeWidth;
      } else if (debugConnectedRoute === true) {
        // Connected named routes (different name but directly connected) - yellow at 50% opacity
        lineColor = `rgba(255, 193, 7, ${routeOpacity * 0.5})`;
        lineWidth = routeWidth * 0.66;
      } else if (debugConnected === true) {
        // Other connected segments (e.g., unnamed connectors) - yellow at 50% opacity
        lineColor = `rgba(255, 193, 7, ${routeOpacity * 0.5})`;
        lineWidth = routeWidth * 0.66;
      } else if (debugSameName && !debugConnected) {
        // Orange for same-name but not connected (shouldn't happen much now)
        lineColor = `rgba(251, 146, 60, ${routeOpacity})`;
      } else {
        // FALLBACK: If no debug flags set, use route settings
        const rgbaColor = this.hexToRgba(routeColor, routeOpacity);
        lineColor = rgbaColor;
        lineWidth = routeWidth;
      }

      // Render with outline if enabled
      if (showOutline) {
        // Determine outline color based on line color brightness
        let baseOutlineColor: string;

        if (debugConnected === false) {
          // Gray routes - use white outline with more transparency
          baseOutlineColor = "rgba(255, 255, 255, 0.3)";
        } else if (debugPrimary === true || debugPrimary === undefined) {
          // Primary route - use setting (auto or custom color)
          baseOutlineColor =
            outlineColorSetting === "auto"
              ? this.getOutlineColor(routeColor)
              : outlineColorSetting;
        } else if (debugConnectedRoute === true || debugConnected === true) {
          // Yellow routes - yellow is bright, use black outline with some transparency
          baseOutlineColor = "rgba(0, 0, 0, 0.6)";
        } else if (debugSameName && !debugConnected) {
          // Orange routes - use white outline
          baseOutlineColor = "#ffffff";
        } else {
          // Fallback - use setting (auto or custom color)
          baseOutlineColor =
            outlineColorSetting === "auto"
              ? this.getOutlineColor(routeColor)
              : outlineColorSetting;
        }

        const outlineColor = baseOutlineColor;

        // If opacity is 1.0, use simpler two-stroke technique (faster)
        if (routeOpacity >= 1.0) {
          return [
            // White outline (wider)
            new Style({
              stroke: new Stroke({
                color: outlineColor,
                width: lineWidth + 2, // 1px on each side
                lineCap: "butt",
                lineJoin: "round",
              }),
            }),
            // Colored center line
            new Style({
              stroke: new Stroke({
                color: lineColor,
                width: lineWidth || 4,
                lineCap: "butt",
                lineJoin: "round",
              }),
            }),
          ];
        }

        // For semi-transparent lines, use edge outlines to prevent blending issues
        const geometry = feature.getGeometry();
        if (geometry && geometry instanceof LineString) {
          const lineCoords = geometry.getCoordinates();
          const { leftEdge, rightEdge } = this.lineToPolygon(
            lineCoords,
            lineWidth,
          );

          if (leftEdge.length > 0 && rightEdge.length > 0) {
            const styles: Style[] = [];

            // Draw left and right edge outlines first (no endcaps, continuous joins)
            styles.push(
              new Style({
                geometry: new LineString(leftEdge),
                stroke: new Stroke({
                  color: outlineColor,
                  width: 1,
                  lineCap: "butt",
                  lineJoin: "round",
                }),
              }),
            );
            styles.push(
              new Style({
                geometry: new LineString(rightEdge),
                stroke: new Stroke({
                  color: outlineColor,
                  width: 1,
                  lineCap: "butt",
                  lineJoin: "round",
                }),
              }),
            );

            // Draw center line on top using native OpenLayers rendering
            // Use butt endcaps to match the edge outline positioning
            styles.push(
              new Style({
                stroke: new Stroke({
                  color: lineColor,
                  width: lineWidth || 4,
                  lineCap: "butt",
                  lineJoin: "round",
                }),
              }),
            );

            return styles;
          }
        }
      }

      // No outline - just render the center line
      return new Style({
        stroke: new Stroke({
          color: lineColor,
          width: lineWidth || 4,
          lineCap: "butt",
          lineJoin: "round",
        }),
      });
    } else {
      // Node style
      return new Style({
        image: new CircleStyle({
          radius: 10,
          fill: new Fill({ color: "#22c55e" }),
          stroke: new Stroke({ color: "#ffffff", width: 3 }),
        }),
      });
    }
  }

  private animateToCenter(targetCoords: number[]) {
    const startCenter = this.view.getCenter();
    if (!startCenter) {
      this.view.setCenter(targetCoords);
      return;
    }

    const startTime = performance.now();
    const duration = 800;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Smooth easing
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      const x =
        startCenter[0] + (targetCoords[0] - startCenter[0]) * easeProgress;
      const y =
        startCenter[1] + (targetCoords[1] - startCenter[1]) * easeProgress;

      this.view.setCenter([x, y]);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  centerOnCurrentGPS(): boolean {
    // Cancel any existing animation timeout
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = undefined;
    }

    // Try geometry first (most up to date)
    const geometry = this.positionFeature.getGeometry();
    if (geometry) {
      this.isAnimating = true;
      this.view.animate({
        center: (geometry as Point).getCoordinates(),
        duration: 800,
      });
      this.animationTimeout = window.setTimeout(() => {
        this.isAnimating = false;
        this.animationTimeout = undefined;
      }, 850);
      return true;
    }

    // Fallback to last known position if no geometry yet
    if (this.lastKnownPosition) {
      const coords = fromLonLat([
        this.lastKnownPosition.lng,
        this.lastKnownPosition.lat,
      ]);
      this.isAnimating = true;
      this.view.animate({
        center: coords,
        duration: 800,
      });
      this.animationTimeout = window.setTimeout(() => {
        this.isAnimating = false;
        this.animationTimeout = undefined;
      }, 850);
      return true;
    }

    return false;
  }

  setRotation(rotation: number) {
    // DON'T ROTATE WHILE ZOOMING
    if (this.isZooming) {
      debug("⛔ BLOCKING setRotation - ZOOM IN PROGRESS");
      return;
    }

    const currentRotation = this.view.getRotation();
    debugRotation(
      "setRotation called: %s° (current: %s°)",
      ((rotation * 180) / Math.PI).toFixed(1),
      ((currentRotation * 180) / Math.PI).toFixed(1),
    );

    // Always set the rotation, even if it's the same
    // This ensures the marker rotation is recalculated correctly
    this.view.setRotation(rotation);

    // When map rotation changes, we need to update the marker's rotation
    // to maintain its absolute heading
    this.updateMarkerHeading();
  }

  getRotation(): number {
    return this.view.getRotation();
  }

  hasValidGPS(): boolean {
    return (
      this.lastKnownPosition !== null ||
      this.positionFeature.getGeometry() !== undefined
    );
  }

  onMapInteraction(callback: () => void): () => void {
    let activePointers = new Set<number>(); // Track active pointer IDs
    let firstPointerStart: [number, number] | null = null;
    let hasSeenMultiTouch = false;
    const DRAG_THRESHOLD = 50;

    const handleDragStart = (event: any) => {
      const pointerId = event.originalEvent?.pointerId || Date.now(); // Fallback ID
      activePointers.add(pointerId);

      debug.info(
        `🟡 POINTER DOWN: pointerId=${pointerId}, activePointers=${activePointers.size}`,
      );

      // If this is the second+ pointer, mark as multi-touch
      if (activePointers.size > 1) {
        hasSeenMultiTouch = true;
        firstPointerStart = null;
        debug.info(
          `🔴 MULTI-TOUCH DETECTED (${activePointers.size} pointers) - BLOCKING GPS exit`,
        );
        return;
      }

      // First pointer - start tracking if no multi-touch seen
      if (activePointers.size === 1 && !hasSeenMultiTouch) {
        firstPointerStart = [event.pixel[0], event.pixel[1]];
        debug.info(
          `🟢 FIRST POINTER at [${event.pixel[0]}, ${event.pixel[1]}] - GPS exit tracking ENABLED`,
        );
      }
    };

    const handleDrag = (event: any) => {
      // Only process if single pointer and no multi-touch seen
      if (!firstPointerStart || hasSeenMultiTouch || activePointers.size > 1)
        return;

      const currentCoords = [event.pixel[0], event.pixel[1]];
      const distance = Math.sqrt(
        Math.pow(currentCoords[0] - firstPointerStart[0], 2) +
          Math.pow(currentCoords[1] - firstPointerStart[1], 2),
      );

      debug.info(
        `🔵 Single pointer moved ${distance.toFixed(1)}px (${activePointers.size} active)`,
      );

      if (distance > DRAG_THRESHOLD) {
        debug.info(
          `🔴 SINGLE POINTER MOVED ${distance}px > ${DRAG_THRESHOLD}px - EXITING GPS MODE!`,
        );
        callback();
        firstPointerStart = null;
      }
    };

    const handleDragEnd = (event: any) => {
      const pointerId =
        event.originalEvent?.pointerId || Array.from(activePointers)[0];
      activePointers.delete(pointerId);

      debug.info(
        `🟡 POINTER UP: pointerId=${pointerId}, remaining=${activePointers.size}`,
      );

      // Reset everything when all pointers are gone
      if (activePointers.size === 0) {
        firstPointerStart = null;
        hasSeenMultiTouch = false;
        debug.info(`🔄 All pointers released - reset tracking`);
      }
    };

    // Listen for touch events
    this.map.on("pointerdown" as any, handleDragStart);
    this.map.on("pointerdrag" as any, handleDrag);
    this.map.on("pointerup" as any, handleDragEnd);

    return () => {
      this.map.un("pointerdown" as any, handleDragStart);
      this.map.un("pointerdrag" as any, handleDrag);
      this.map.un("pointerup" as any, handleDragEnd);
    };
  }

  // Set whether zoom gestures should center on GPS position
  setGPSCenteredZoom(enabled: boolean): void {
    this.isGPSZoomEnabled = enabled;
    if (enabled) {
      this.setupGPSCenteredZoom();
    } else {
      this.removeGPSCenteredZoom();
    }
  }

  private gpsZoomHandler?: (event: any) => void;

  private setupGPSCenteredZoom(): void {
    // Create bound handler to avoid issues with removal
    this.gpsZoomHandler = this.handleZoomEvent.bind(this);

    // Listen for view change events to intercept zoom
    this.view.on("change:resolution", this.gpsZoomHandler);
  }

  private removeGPSCenteredZoom(): void {
    if (this.gpsZoomHandler) {
      this.view.un("change:resolution", this.gpsZoomHandler);
      this.gpsZoomHandler = undefined;
    }
  }

  private isGPSZoomEnabled = false;

  private handleZoomEvent(_event: any): void {
    // Only re-center on GPS if GPS-centered zoom is explicitly enabled
    if (!this.isGPSZoomEnabled) return;

    const gpsCenter = this.getGPSCenter();
    if (gpsCenter && !this.view.getAnimating()) {
      // Re-center on GPS with offset for follow-heading mode
      const centerWithOffset = this.getOffsetCenter(gpsCenter);
      this.view.setCenter(centerWithOffset);
    }
  }

  zoomIn() {
    this.zoomToLevel(this.view.getZoom()! + 1);
  }

  zoomOut() {
    this.zoomToLevel(this.view.getZoom()! - 1);
  }

  setZoom(zoomLevel: number) {
    this.zoomToLevel(zoomLevel);
  }

  private zoomToLevel(zoomLevel: number) {
    // In follow modes, zoom centered on GPS position (with offset for follow-heading)
    if (
      this.mode === "follow" ||
      this.mode === "follow-north" ||
      this.mode === "follow-heading"
    ) {
      const gpsCenter = this.getGPSCenter();
      if (gpsCenter) {
        // Apply offset if in follow-heading mode
        const center = this.getOffsetCenter(gpsCenter);

        // Animate to new zoom level with appropriate center
        this.isAnimating = true;
        this.view.animate({
          zoom: zoomLevel,
          center: center,
          duration: 250,
        });
        setTimeout(() => {
          this.isAnimating = false;
        }, 300);
        return;
      }
    }

    // Free mode - just animate zoom normally
    this.isAnimating = true;
    this.view.animate({
      zoom: zoomLevel,
      duration: 250,
    });
    setTimeout(() => {
      this.isAnimating = false;
    }, 300);
  }

  private getOffsetCenter(baseCoords: number[], rotation?: number): number[] {
    // Check if camera is in fullscreen overlay mode
    const cameraService = getCameraService();
    const cameraSettings = cameraService.getSettings();
    const isCameraOverlay =
      cameraService.isActive() &&
      (cameraSettings.layout === "full" ||
        cameraSettings.layout === "full-wide");

    // Apply offset ONLY in follow-heading mode
    // - When debug overlay is not showing (normal behavior)
    // - OR when camera is in fullscreen overlay mode (always offset for camera overlay)
    if (
      this.mode === "follow-heading" &&
      (!this.isDebugOverlayShowing || isCameraOverlay)
    ) {
      const size = this.map.getSize();
      if (size) {
        const resolution = this.view.getResolution();
        if (resolution) {
          let offsetFraction: number;

          if (isCameraOverlay) {
            // When map is overlaying camera, offset to bottom 25% of the small map view
            offsetFraction = 0.25;
          } else {
            // Normal follow-heading mode offset
            offsetFraction = size[1] < 500 ? 0.0 : 0.2;
          }

          // We want GPS at bottom third, so offset map center upward
          const offsetPixels = size[1] * offsetFraction;
          const offsetDistance = offsetPixels * resolution;
          // Use provided rotation or get current rotation
          const currentRotation =
            rotation !== undefined ? rotation : this.view.getRotation();

          const offsetX = offsetDistance * Math.sin(currentRotation);
          const offsetY = offsetDistance * Math.cos(currentRotation);

          return [baseCoords[0] - offsetX, baseCoords[1] + offsetY];
        }
      }
    }
    return baseCoords;
  }

  private getGPSCenter(): [number, number] | null {
    // Try geometry first (most up to date)
    const geometry = this.positionFeature.getGeometry();
    if (geometry) {
      const coords = (geometry as Point).getCoordinates();
      return [coords[0], coords[1]];
    }

    // Fallback to last known position if no geometry yet
    if (this.lastKnownPosition) {
      const coords = fromLonLat([
        this.lastKnownPosition.lng,
        this.lastKnownPosition.lat,
      ]);
      return [coords[0], coords[1]];
    }

    return null;
  }

  // ============================================================================
  // Smooth Rotation Animation
  // ============================================================================

  private startRotationAnimation(): void {
    const animate = () => {
      // Calculate the shortest angular distance
      let angleDiff = this.targetMapRotation - this.currentMapRotation;

      // Normalize angle difference to [-PI, PI]
      while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
      while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

      // If we're close enough, snap to target and stop
      if (Math.abs(angleDiff) < 0.001) {
        this.currentMapRotation = this.targetMapRotation;
        this.view.setRotation(this.currentMapRotation);
        this.lastMapRotation = this.currentMapRotation;
        this.rotationAnimationFrame = null;
        return;
      }

      // Smooth interpolation - adjust the factor for responsiveness
      // Higher values = faster rotation, lower = smoother
      // Using 0.35 for fast response to 30fps compass updates
      const smoothingFactor = 0.35;
      this.currentMapRotation += angleDiff * smoothingFactor;

      // Normalize to [-PI, PI]
      while (this.currentMapRotation > Math.PI)
        this.currentMapRotation -= 2 * Math.PI;
      while (this.currentMapRotation < -Math.PI)
        this.currentMapRotation += 2 * Math.PI;

      // Calculate rotation delta
      const rotationDelta = this.currentMapRotation - this.lastMapRotation;

      // In follow-heading mode, rotate around the GPS position as anchor
      if (this.mode === "follow-heading") {
        const geometry = this.positionFeature.getGeometry();
        if (geometry) {
          const gpsCoords = (geometry as Point).getCoordinates();

          // Use adjustRotation with the GPS position as anchor
          // This rotates the map around the GPS position, not the center
          this.view.adjustRotation(rotationDelta, gpsCoords);
        } else {
          // No GPS position, just apply rotation normally
          this.view.setRotation(this.currentMapRotation);
        }
      } else {
        // Not follow-heading mode, just apply rotation normally
        this.view.setRotation(this.currentMapRotation);
      }

      this.lastMapRotation = this.currentMapRotation;

      debugRotation(
        "Smooth rotation: target=%s°, current=%s°, diff=%s°",
        ((this.targetMapRotation * 180) / Math.PI).toFixed(1),
        ((this.currentMapRotation * 180) / Math.PI).toFixed(1),
        ((angleDiff * 180) / Math.PI).toFixed(1),
      );

      // Continue animation
      this.rotationAnimationFrame = requestAnimationFrame(animate);
    };

    // Initialize current rotation if needed
    if (this.currentMapRotation === 0 && this.lastMapRotation === 0) {
      this.currentMapRotation = this.view.getRotation();
    }

    this.rotationAnimationFrame = requestAnimationFrame(animate);
  }

  private stopRotationAnimation(): void {
    if (this.rotationAnimationFrame) {
      cancelAnimationFrame(this.rotationAnimationFrame);
      this.rotationAnimationFrame = null;
    }
  }

  // ============================================================================
  // Styles
  // ============================================================================

  private getPositionStyle(): Style | Style[] {
    const styles: Style[] = [];

    // Main blue dot
    styles.push(
      new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: "#4285f4" }),
          stroke: new Stroke({ color: "#ffffff", width: 2 }),
        }),
      }),
    );

    // Direction triangle - only show when heading is reliable
    const headingUnreliable =
      this.positionFeature?.get("headingUnreliable") ?? false;
    if (this.positionMarkerIcon && !headingUnreliable) {
      styles.push(
        new Style({
          image: this.positionMarkerIcon,
        }),
      );
    }

    // Accuracy circle (optional, shown as lighter blue circle)
    const accuracy = this.positionFeature?.get("accuracy");
    if (accuracy && accuracy > 10) {
      styles.unshift(
        new Style({
          image: new CircleStyle({
            radius: Math.min(accuracy, 50), // Cap at 50px for display
            fill: new Fill({ color: "rgba(66, 133, 244, 0.1)" }),
            stroke: new Stroke({ color: "rgba(66, 133, 244, 0.3)", width: 1 }),
          }),
        }),
      );
    }

    return styles;
  }

  /**
   * Calculate maximum line width based on zoom level
   * Gradually reduces width as you zoom out to prevent thick lines at world scale
   * Uses linear interpolation: zoom 8 = 2px, zoom 14 = full width
   */
  private getMaxLineWidthForZoom(configuredWidth: number): number {
    // If view not initialized yet, return full width
    if (!this.view) {
      return configuredWidth;
    }

    const zoom = this.view.getZoom() || 14;

    // Clamp to minimum at world scale
    if (zoom <= 8) return Math.min(configuredWidth, 2);

    // Full width when zoomed in
    if (zoom >= 14) return configuredWidth;

    // Linear interpolation: zoom 8→2px, zoom 14→configuredWidth
    const t = (zoom - 8) / (14 - 8);
    return 2 + t * (configuredWidth - 2);
  }

  private getTrackStyle(
    settings: TrackDisplaySettings,
  ): Style | Style[] | StyleFunction {
    const lineColor = settings.lineColor || "#3e6ae1";
    const configuredWidth = settings.lineWidth || 4;
    const lineWidth = this.getMaxLineWidthForZoom(configuredWidth);
    const lineOpacity = settings.lineOpacity ?? 0.8;
    const showOutline = settings.showOutline ?? true;
    const outlineColorSetting = settings.outlineColor || "auto";

    // Convert hex color to rgba with opacity
    const colorWithOpacity = this.hexToRgba(lineColor, lineOpacity);

    // If no outline, just return simple line
    if (!showOutline) {
      return new Style({
        stroke: new Stroke({
          color: colorWithOpacity,
          width: lineWidth,
          lineCap: "butt",
          lineJoin: "round",
        }),
      });
    }

    // Determine outline color
    const outlineColor =
      outlineColorSetting === "auto"
        ? this.getOutlineColor(lineColor)
        : outlineColorSetting;

    // If opacity is 1.0, use simple two-stroke technique (faster)
    if (lineOpacity >= 1.0) {
      return [
        new Style({
          stroke: new Stroke({
            color: outlineColor,
            width: lineWidth + 2, // 1px on each side
            lineCap: "butt",
            lineJoin: "round",
          }),
        }),
        new Style({
          stroke: new Stroke({
            color: colorWithOpacity,
            width: lineWidth,
            lineCap: "butt",
            lineJoin: "round",
          }),
        }),
      ];
    }

    // For semi-transparent with outline: use edge outline technique
    // This is applied per-feature via a style function
    return (feature: FeatureLike) => {
      const geometry = (feature as Feature).getGeometry();
      if (geometry && geometry instanceof LineString) {
        const lineCoords = geometry.getCoordinates();
        const { leftEdge, rightEdge } = this.lineToPolygon(
          lineCoords,
          lineWidth,
        );

        if (leftEdge.length > 0 && rightEdge.length > 0) {
          return [
            // Left edge outline
            new Style({
              geometry: new LineString(leftEdge),
              stroke: new Stroke({
                color: outlineColor,
                width: 1,
                lineCap: "butt",
                lineJoin: "round",
              }),
            }),
            // Right edge outline
            new Style({
              geometry: new LineString(rightEdge),
              stroke: new Stroke({
                color: outlineColor,
                width: 1,
                lineCap: "butt",
                lineJoin: "round",
              }),
            }),
            // Center line with color/opacity
            new Style({
              stroke: new Stroke({
                color: colorWithOpacity,
                width: lineWidth,
                lineCap: "butt",
                lineJoin: "round",
              }),
            }),
          ];
        }
      }

      // Fallback to simple line if geometry is not LineString
      return new Style({
        stroke: new Stroke({
          color: colorWithOpacity,
          width: lineWidth,
          lineCap: "butt",
          lineJoin: "round",
        }),
      });
    };
  }

  /**
   * Convert a LineString to a Polygon by buffering it with a given width
   * This creates a polygon that represents the line with thickness
   * Also returns the left and right edge lines for outline rendering
   */
  private lineToPolygon(
    lineCoords: number[][],
    widthInPixels: number,
  ): { polygon: number[][]; leftEdge: number[][]; rightEdge: number[][] } {
    if (lineCoords.length < 2)
      return { polygon: [], leftEdge: [], rightEdge: [] };

    const resolution = this.view.getResolution() || 1;
    const widthInMapUnits = widthInPixels * resolution;
    const halfWidth = widthInMapUnits / 2;

    const leftSide: number[][] = [];
    const rightSide: number[][] = [];

    for (let i = 0; i < lineCoords.length; i++) {
      const current = lineCoords[i];
      let dx: number, dy: number;

      if (i === 0) {
        // First point: use direction to next point
        const next = lineCoords[i + 1];
        dx = next[0] - current[0];
        dy = next[1] - current[1];
      } else if (i === lineCoords.length - 1) {
        // Last point: use direction from previous point
        const prev = lineCoords[i - 1];
        dx = current[0] - prev[0];
        dy = current[1] - prev[1];
      } else {
        // Middle points: use average of both directions
        const prev = lineCoords[i - 1];
        const next = lineCoords[i + 1];
        const dx1 = current[0] - prev[0];
        const dy1 = current[1] - prev[1];
        const dx2 = next[0] - current[0];
        const dy2 = next[1] - current[1];
        dx = dx1 + dx2;
        dy = dy1 + dy2;
      }

      // Normalize and get perpendicular
      const length = Math.sqrt(dx * dx + dy * dy);
      if (length === 0) continue;

      const perpX = (-dy / length) * halfWidth;
      const perpY = (dx / length) * halfWidth;

      // Add offset points to left and right sides
      leftSide.push([current[0] + perpX, current[1] + perpY]);
      rightSide.push([current[0] - perpX, current[1] - perpY]);
    }

    // Combine: left side forward, right side backward, close the polygon
    const polygonCoords = [
      ...leftSide,
      ...rightSide.reverse(),
      leftSide[0], // Close the polygon
    ];

    return {
      polygon: polygonCoords,
      leftEdge: leftSide,
      rightEdge: rightSide,
    };
  }

  /**
   * Convert hex color to rgba with opacity
   */
  private hexToRgba(hexColor: string, opacity: number): string {
    if (hexColor.startsWith("#") && hexColor.length === 7) {
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);

      // Check for NaN values
      if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      } else {
        console.warn("Invalid hex color format, using fallback:", hexColor);
        return `rgba(34, 197, 94, ${opacity})`; // Fallback green
      }
    } else {
      // If not a hex color, use as-is (might be rgb, rgba, named color, etc)
      return hexColor;
    }
  }

  /**
   * Determine outline color based on brightness of the line color
   * Returns black for very bright colors, white otherwise
   * Considers opacity - transparent colors appear less bright
   */
  private getOutlineColor(color: string): string {
    let r: number,
      g: number,
      b: number,
      a: number = 1;

    // Handle hex colors
    if (color.startsWith("#") && color.length === 7) {
      r = parseInt(color.slice(1, 3), 16);
      g = parseInt(color.slice(3, 5), 16);
      b = parseInt(color.slice(5, 7), 16);
    }
    // Handle rgb/rgba colors
    else if (color.startsWith("rgb")) {
      const match = color.match(/rgba?\(([^)]+)\)/);
      if (match) {
        const parts = match[1].split(",").map((p) => parseFloat(p.trim()));
        r = parts[0] || 0;
        g = parts[1] || 0;
        b = parts[2] || 0;
        a = parts[3] !== undefined ? parts[3] : 1; // Extract alpha if present
      } else {
        return "#ffffff"; // Default to white if can't parse
      }
    } else {
      return "#ffffff"; // Default to white for unknown formats
    }

    if (isNaN(r) || isNaN(g) || isNaN(b)) {
      return "#ffffff";
    }

    // Calculate relative luminance (perceived brightness)
    // Human eyes are most sensitive to green, least to blue
    let luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    // Reduce perceived brightness based on opacity
    // Transparent colors appear less bright against typical backgrounds
    luminance = luminance * a;

    // Use black outline for bright colors (luminance > 180)
    // Prefer white unless color is bright
    return luminance > 180 ? "#000000" : "#ffffff";
  }

  private getRouteStyle(): Style {
    // Use route-specific settings with configured opacity
    const routeColor = this.routeSettings.lineColor || "#9333ea";
    const opacity = this.routeSettings.lineOpacity ?? 0.7;

    // Convert hex to rgba with configured opacity
    const routeColorWithAlpha = this.hexToRgba(routeColor, opacity);

    debug("Route style:", {
      routeColor,
      opacity,
      routeColorWithAlpha,
      width: this.routeSettings.lineWidth || 3,
      settings: this.routeSettings,
    });

    return new Style({
      stroke: new Stroke({
        color: routeColorWithAlpha,
        width: this.routeSettings.lineWidth || 3,
        // Solid line for route preview
      }),
    });
  }

  private getPlaceStyle(feature: Feature): Style {
    const place = feature.get("place") as Place;
    const type = feature.getGeometry()?.getType();

    if (type === "Point") {
      return new Style({
        image: new CircleStyle({
          radius: 6,
          fill: new Fill({ color: place.markerColor || "#ef4444" }),
          stroke: new Stroke({ color: "#ffffff", width: 2 }),
        }),
        text: new Text({
          text: place.name,
          offsetY: -20,
          font: "12px sans-serif",
          fill: new Fill({ color: "#ffffff" }),
          stroke: new Stroke({ color: "#000000", width: 3 }),
        }),
      });
    } else {
      return new Style({
        stroke: new Stroke({
          color: place.markerColor || "#ef4444",
          width: 3,
        }),
        fill: new Fill({
          color: place.markerColor ? place.markerColor + "33" : "#ef444433",
        }),
      });
    }
  }

  private getFriendStyle(): StyleFunction {
    return (feature: FeatureLike) => {
      const friend = feature.get("friend");

      // Use the same fallback logic as FriendListItem.tsx
      // Priority: mapIcon/mapColor (custom) -> defaultMapIcon/defaultMapColor (user's defaults) -> fallback
      const mapIcon = friend?.mapIcon || friend?.defaultMapIcon || "";
      const mapColor = friend?.mapColor || friend?.defaultMapColor || "#6366f1";

      // If no icon, use initials (first character of username)
      const displayIcon =
        mapIcon ||
        (friend?.username ? friend.username.charAt(0).toUpperCase() : "?");

      // Create icon with emoji text or initials
      return new Style({
        image: new CircleStyle({
          radius: 16,
          fill: new Fill({ color: mapColor }),
          stroke: new Stroke({ color: "#ffffff", width: 2 }),
        }),
        text: new Text({
          text: displayIcon,
          font: mapIcon ? "20px sans-serif" : "16px sans-serif", // Smaller font for initials
          offsetY: 0,
          fill: new Fill({ color: "#ffffff" }),
        }),
      });
    };
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  getCenter(): { lat: number; lon: number } | null {
    const center = this.view.getCenter();
    if (!center) return null;

    const lonLat = toLonLat(center);
    const result = {
      lat: lonLat[1],
      lon: lonLat[0],
    };

    // Reduced debug logging - center calculation appears correct
    // Uncomment for debugging: debug("Map center: lat=%.6f, lon=%.6f", result.lat, result.lon);

    return result;
  }

  onMoveEnd(callback: () => void): () => void {
    // Listen for map move end events
    const handleMoveEnd = () => {
      callback();
    };

    this.map.on("moveend", handleMoveEnd);

    // Return unsubscribe function
    return () => {
      this.map.un("moveend", handleMoveEnd);
    };
  }

  // Cache Grid Display
  // ============================================================================

  async updateCacheGridOverlay() {
    debug("updateCacheGridOverlay called");

    if (!this.cacheGridLayer) {
      debug.error("No cache grid layer!");
      return;
    }

    const source = this.cacheGridLayer.getSource();
    if (!source) {
      debug.error("No cache grid source!");
      return;
    }

    // Clear existing features
    source.clear();

    // Only show if enabled
    const showGrid = localStorage.getItem("showCacheGrid") === "true";
    debug("Show cache grid setting: %s", showGrid);
    this.cacheGridLayer.setVisible(showGrid);

    if (!showGrid) {
      debug("Cache grid disabled, not rendering");
      return;
    }

    try {
      // Note: Cache grid visualization disabled after overpassService.ts removal
      // This feature used the old overpassService getCachedGridBounds() method
      // which doesn't exist in overpassWorkerService
      debug("Cache grid visualization disabled - overpassService removed");
      return;

      /*
      const gridBounds = [];
      debug("Updating cache grid overlay with %d squares", gridBounds.length);

      if (gridBounds.length === 0) {
        debug.warn("No cached grid squares found!");
        return;
      }
      */

      /*
      // Create polygon features for each cached grid square
      for (const grid of gridBounds) {
        const { bounds } = grid;

        debug(
          "Creating polygon for grid %.1f,%.1f",
          grid.gridLat,
          grid.gridLon,
        );

        // Convert bounds to map projection
        const polygon = new Polygon([
          [
            fromLonLat([bounds.west, bounds.north]),
            fromLonLat([bounds.east, bounds.north]),
            fromLonLat([bounds.east, bounds.south]),
            fromLonLat([bounds.west, bounds.south]),
            fromLonLat([bounds.west, bounds.north]),
          ],
        ]);

        const feature = new Feature({
          geometry: polygon,
          gridLat: grid.gridLat,
          gridLon: grid.gridLon,
        });

        source.addFeature(feature);
      }

      const features = source.getFeatures();
      debug("Added %d cache grid features to map", features.length);

      // Check layer visibility
      debug("Cache grid layer visible: %s", this.cacheGridLayer.getVisible());
      */
    } catch (err) {
      debug.error("Failed to update cache grid overlay:", err);
    }
  }

  setupCacheGridListener() {
    // Listen for storage events to toggle cache grid
    window.addEventListener("storage", (e) => {
      if (e.key === "showCacheGrid") {
        this.updateCacheGridOverlay();
      }
    });

    // Listen for cache updates
    window.addEventListener("cacheUpdated", () => {
      if (localStorage.getItem("showCacheGrid") === "true") {
        this.updateCacheGridOverlay();
      }
    });

    // Initial update
    this.updateCacheGridOverlay();
  }

  // Cleanup
  // ============================================================================

  destroy() {
    this.map.setTarget(undefined);
    debug.info("Map service destroyed");
  }
}
